
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { buildTommyUrl, SUPPORT_PHONE_TEL } from '@/constants/support';
import { motion } from 'framer-motion';
import { X, ArrowLeft, Plane, Calendar, Clock, MapPin, User as UserIcon, CreditCard, ExternalLink, Image as ImageIcon, Users, Edit, Trash2, MessageSquare, Loader2, AlertCircle, Hotel, Star, Award, Save, PlaneTakeoff, XCircle, CheckCircle, ArrowRight, ChevronDown, Luggage, Armchair, Info, MessageCircle, ArrowUpCircle, Sparkles, Gift, Ticket, Copy, Check, Activity as ActivityIcon, Phone, ShieldCheck, Link2, WifiOff } from 'lucide-react';
import { isNativeApp } from '@/native/bootstrap';
import { useOnline } from '@/native/offline';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { format, parseISO, formatDistanceToNow, startOfDay } from 'date-fns';
import { FlightBooking, Passenger, User, InternalComment } from '@/api/entities';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import FlightOptionsMenu from './FlightOptionsMenu';
import AdminEditModal from '../admin/AdminEditModal';
import PassengerEditModal from '../passenger/PassengerEditModal';
import WheelchairAssistanceCard from './WheelchairAssistanceCard';
import { needsWheelchairQuestions } from '../../../supabase/functions/_shared/wheelchair-assistance.js';
import FlightOfferCard from '../search/FlightOfferCard';
import SegmentSeatChips from './SegmentSeatChips';
import { resolveCarrierLogo } from '@/utils/airlineBranding';
import { fmtAmount, fmtCurrency } from '@/utils/money';

// Lazy-loaded so the ~400KB seatmaps.com widget is only fetched when a
// customer actually opens a seat map.
const BookedSeatMapViewer = React.lazy(() => import('../seat/BookedSeatMapViewer'));
// Lazy-loaded: pulls in the Duffel SeatMap renderer + Stripe Elements, only
// needed when a customer starts a post-booking seat purchase.
const PurchaseSeatsFlow = React.lazy(() => import('../seat/PurchaseSeatsFlow'));
const PurchaseBaggageFlow = React.lazy(() => import('../baggage/PurchaseBaggageFlow'));
const SEAT_MAPS_ENABLED = Boolean(
  import.meta.env.VITE_SEATMAPS_API_URL &&
  import.meta.env.VITE_SEATMAPS_APP_ID &&
  import.meta.env.VITE_SEATMAPS_API_KEY
);
import { getCityName, getAirportCode } from '@/components/airportUtils';
import { initiateSlicePayFlow } from '@/api/functions';
import { createPageUrl, parseMonetaryValue, calculateReviewPagePricing, getServiceFeeRate } from '@/utils';
import { readCartDiscount } from '@/utils/discountCodes';
import { resolveHotelRating } from '@/utils/hotelRating';
import { seatPurchaseEconomics, seatPurchaseMarginContribution } from '@/utils/seatPurchaseEconomics';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import PaymentScheduleDisplay from '../booking/PaymentScheduleDisplay';
import ServiceInquiryCard from '@/components/support/ServiceInquiryCard';
import DownloadTripDocumentButton from '../booking/DownloadTripDocumentButton';
import SectionBand from '@/components/ui/SectionBand';
import { UserPreferences } from '@/api/entities';
import { supabase } from '@/lib/supabase';
import FlightLegLive from '@/components/flight/FlightLegLive';
import FlightAlertTextsSwitch from '@/components/flight/FlightAlertTextsSwitch';
import { useMilesBalanceRealtime } from '@/hooks/useMilesBalanceRealtime';
import { deferaZeroUrl, deferaCapitalUrl, deferaCapitalNmiUrl, getApiUrl } from '@/lib/apiUrls';
import { getProgramLabel } from '@/constants/loyaltyPrograms';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { isChangeFlightEligible, tripHasAirlineConfirmation, getChangeFlightRouting, getTripAirline } from '@/utils/changeFlightEligibility';
import { REINSTATEMENT_FEE_LABEL } from '@/constants/paymentPolicy';
import { retainedExtensionFeesCents } from '@/utils/paymentExtensionChoices';
import { getFlightOptionsState, isTripCompleted } from '@/utils/flightOptionsEligibility';
import { getMarketingFareName } from '@/utils/marketingFareName';
import { getPurchasedBaggageSummary } from '@/utils/baggageAllowance';
import { referralSourceLabel } from '@/utils/referralSource';
import { useHydratedTrip } from './useHydratedTrip';
import PropTypes from 'prop-types';
import { getSeatPreferenceLabel } from '@/utils/seatPreference';
import { getSeatPurchaseEligibility } from '@/utils/seatPurchaseEligibility';
import { checkoutSeatTotalFromFlightData, checkoutSeatCountFromFlightData } from '@/utils/seatTotalFromFlightData';
import { useInstallmentsCopy } from '@/hooks/useInstallmentsCopy';
import { useInstallmentsCheckoutProvider } from '@/hooks/useInstallmentsCheckoutProvider';
import { useIsMounted } from '@/hooks/useIsMounted';
import ActionModalShell from '@/components/ui/ActionModalShell';
import { MODAL_GHOST_BTN_CLASS, MODAL_PRIMARY_BTN_CLASS } from '@/components/booking/paymentScheduleModalStyles';
import { DEFERA_CAPITAL_HOLD_FEE_RATE, SLICE_PAY_HOLD_FEE_RATE } from '@/utils/paymentScheduleCalculator';
import { computeFlightHotelBreakdown } from '@/utils/flightHotelBreakdown';
import { cabinForChangeSearch } from '@/utils/cabinClass';
import { isStayBooked } from '../../../supabase/functions/_shared/stay-cancel-rules.js';
import TripOpsPanel from '@/components/admin/TripOpsPanel';
import PriceReceiptCard from '@/components/admin/PriceReceiptCard';
import VipBadge from '@/components/admin/VipBadge';
import VipUpgradeNudge from '@/components/admin/VipUpgradeNudge';
import { canEditTripOps, canViewTripInternals, isAdmin, isConcierge } from '@/utils/roles';
import { supportChatPath } from '@/utils/chatTopic';
import { splitPaidAcrossPackage } from '@/utils/packagePaidSplit';
import { publicOrigin } from '@/lib/publicOrigin';
import { INSURANCE_STATUS, PREMIUM_REFUND_METHOD, cancelInsuranceSplit, customerPolicyView, insuranceAddedAfterBooking, readTripInsurance, tripInsurancePremiumCents, tripInsuranceTotalCents, withoutInsurancePremium } from '../../../supabase/functions/_shared/trip-insurance.js';
import TripInsuranceCard from './TripInsuranceCard';
import AddTripInsuranceCard from './AddTripInsuranceCard';
import LinkedTripsBanner from './LinkedTripsBanner';
import { CustomerTravelingTogether } from './TravelingTogetherCard';
import { normalizeCompanionReference } from '@/utils/companionBookingReference';
import { FAYE_INSURANCE_ENABLED } from '@/utils/fayeFlag';
import { releaseTimingSentence } from '../../../supabase/functions/_shared/confirmation-release-window.js';
import { MILES_LIABILITY_CENTS_PER_MILE } from '../../../supabase/functions/_shared/miles-redemption.js';
import { MILES_TILE_MINIMUMS } from '@/constants/milesTiles';
import { writePassengerParams } from '@/utils/passengerTypes';
import MyVipBadge from '@/components/vip/MyVipBadge';
import TripRewardsCard from '@/components/flight/TripRewardsCard';
import { tripRewardsOf } from '@/utils/tripRewards';
import useTripTravelRequirements from '@/hooks/useTripTravelRequirements';
import { displayFirstName, missingTravelDocument, passportExpiryIssue, passportExpiryMessage, travelerVisaFlags, visaFlagMessage } from '@/utils/travelRequirements';

/**
 * Tiny copy-to-clipboard chip used inline next to copyable values (booking ref,
 * confirmation #, passenger name/DOB/phone/email, etc.). Renders a 16×16 icon
 * button that flashes a green check for ~1.5s after a successful copy. Field
 * keys must be unique within the modal so concurrent flashes don't collide.
 *
 * Lives at module scope (not inside FlightDetailsModal) so it doesn't get
 * re-created on every parent render.
 */
function CopyChip({ fieldKey, value, copiedField, onCopy, ariaLabel }) {
  if (!value) return null;
  const isCopied = copiedField === fieldKey;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onCopy(fieldKey, value);
      }}
      className={cn(
        'inline-flex items-center justify-center w-4 h-4 rounded transition-colors flex-shrink-0',
        isCopied
          ? 'text-emerald-300'
          : 'text-white/30 hover:text-white/70'
      )}
      aria-label={ariaLabel || `Copy ${fieldKey}`}
      title={isCopied ? 'Copied' : 'Copy'}
    >
      {isCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

/**
 * Resolve the customer's actual Stripe-charged total when self-healing a corrupted
 * booking row. Only called when `grand_total_with_hold > total_amount + 1` (the
 * corruption signal — see the call site for details). Two observed patterns:
 *
 *   1. AdminEditModal recompute: `total_amount` stays at the Stripe-canonical value,
 *      `grand_total` and `grand_total_with_hold` are sequentially inflated above it.
 *      Signal: `gtwh > grand_total > total_amount`. → Use `total_amount` (the only
 *      column the modal didn't touch).
 *
 *   2. Carts-push / mixed: `total_amount` got rewritten (to the base or to some other
 *      unrelated value); `grand_total = grand_total_with_hold = Stripe canonical`.
 *      Signal: `grand_total ≈ grand_total_with_hold` and both exceed `total_amount`.
 *      → Use `grand_total_with_hold` (matched pair beats the lone outlier).
 *
 * Heuristic: when `grand_total ≈ grand_total_with_hold` (matched pair signature of
 * pattern 2), use `grand_total_with_hold`. Otherwise (pattern 1) take the smallest of
 * the three candidates above the `ticketCost × 1.05` floor — which excludes the
 * "base-as-total" outlier in pattern 2 if it sneaks past the matched-pair check.
 */
function pickStripeCanonicalTotal(flightWithPrice, mainFlight, flightData, ticketCost, fallbackGrandTotalWithHold) {
  // FlightBooking entity exposes `bookings.total_amount` under both `total_amount` and
  // the legacy `totalPrice` alias. Read both so a missing alias on a future entity tweak
  // can never silently drop the booking-column value and let the inflated raw Duffel
  // `flight_data.total_amount` win (PY2RE8 regression class).
  const totalAmount = parseFloat(
    flightWithPrice?.total_amount ?? flightWithPrice?.totalPrice ??
    mainFlight?.total_amount ?? mainFlight?.totalPrice ??
    flightData?.total_amount ?? 0
  ) || 0;
  const grandTotal = parseFloat(flightWithPrice?.grand_total ?? mainFlight?.grand_total ?? flightData?.grand_total ?? 0) || 0;
  const grandTotalWithHold = parseFloat(flightWithPrice?.grand_total_with_hold ?? mainFlight?.grand_total_with_hold ?? flightData?.grand_total_with_hold ?? fallbackGrandTotalWithHold) || fallbackGrandTotalWithHold;
  const matchedPair = grandTotal > 0 && grandTotalWithHold > 0 && Math.abs(grandTotal - grandTotalWithHold) <= 1;
  if (matchedPair && grandTotalWithHold > ticketCost + 1) return grandTotalWithHold;
  const floor = ticketCost * 1.05;
  const candidates = [totalAmount, grandTotal, grandTotalWithHold].filter((v) => v >= floor);
  if (candidates.length > 0) return Math.min(...candidates);
  return grandTotalWithHold || fallbackGrandTotalWithHold;
}

function formatSliceDuration(isoDuration) {
  if (isoDuration == null) return '';
  if (typeof isoDuration === 'number') {
    const h = Math.floor(isoDuration / 60);
    const m = isoDuration % 60;
    return `${h}h ${m}m`;
  }
  const matches = String(isoDuration).match(/PT(\d+H)?(\d+M)?/);
  if (!matches) return '';
  const hours = matches[1] ? parseInt(matches[1].slice(0, -1)) : 0;
  const minutes = matches[2] ? parseInt(matches[2].slice(0, -1)) : 0;
  return `${hours}h ${minutes}m`;
}

function formatCabinDisplay(rawValue) {
  const value = String(rawValue || '').trim();
  if (!value) return 'Economy';
  if (value === 'premium_economy') return 'Premium Economy';
  return value
    .replaceAll('_', ' ')
    .split(' ')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ''))
    .join(' ');
}

const groupFlightsIntoLegs = (flights) => {
  if (!flights || flights.length === 0) return [];

  const legs = [];
  let currentLeg = [];

  flights.forEach((flight, index) => {
    if (index === 0) {
      currentLeg.push(flight);
    } else {
      const prevFlight = flights[index - 1];
      const isContinuation = prevFlight.arrival_city === flight.departure_city;
      let timeDiffHours = 25;

      if (prevFlight.arrival_date && flight.departure_date && isContinuation) {
        const prevArrivalTime = prevFlight.arrival_time || '23:59';
        const flightDepartureTime = flight.departure_time || '00:00';
        const arrivalDateTime = new Date(`${prevFlight.arrival_date}T${prevArrivalTime}`);
        const departureDateTime = new Date(`${flight.departure_date}T${flightDepartureTime}`);

        if (!isNaN(arrivalDateTime.getTime()) && !isNaN(departureDateTime.getTime())) {
          timeDiffHours = (departureDateTime.getTime() - arrivalDateTime.getTime()) / (1000 * 60 * 60);
        }
      }

      if (isContinuation && timeDiffHours <= 24 && timeDiffHours >= 0) {
        const hours = Math.floor(timeDiffHours);
        const minutes = Math.round((timeDiffHours - hours) * 60);
        let durationString = '';
        if (hours > 0) durationString += `${hours}h`;
        if (minutes > 0) {
          if (durationString.length > 0) durationString += ' ';
          durationString += `${minutes}m`;
        }
        if (durationString === '') durationString = '<1m';

        const flightWithDuration = { ...flight, connection_duration: durationString.trim() };
        currentLeg.push(flightWithDuration);
      } else {
        legs.push(currentLeg);
        currentLeg = [flight];
      }
    }
  });

  if (currentLeg.length > 0) {
    legs.push(currentLeg);
  }

  return legs;
};

function FlightDetailsModalInner({ trip, isOpen, onClose, onUpdate, currentUser, userMap = new Map(), deferaZeroPaymentSuccessBookingId, onDeferaZeroPaymentSuccessHandled, backLabel = null }) {
  // iOS app without signal: the trip itself shows from the copy saved on the
  // phone, but the payment schedule is live data (src/native/offline.js).
  const online = useOnline();
  const installmentsCheckoutProvider = useInstallmentsCheckoutProvider();
  const ic = useInstallmentsCopy();
  const navigate = useNavigate();
  /** Async loaders that can outlive the page check this after each await.
   *  See useIsMounted. */
  const isMounted = useIsMounted();
  const [passengers, setPassengers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showImageOverlay, setShowImageOverlay] = useState(false);
  const [showAdminEdit, setShowAdminEdit] = useState(false);
  const [showPassengerEdit, setShowPassengerEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  // "Finished paying" CTA → confirmation-release inquiry. `tripPaidNotice`
  // is the single-button acknowledgement overlay ({ title, message }).
  const [isSubmittingTripPaid, setIsSubmittingTripPaid] = useState(false);
  const [tripPaidNotice, setTripPaidNotice] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isGeneratingPayment, setIsGeneratingPayment] = useState(false);
  const [isEditingSeats, setIsEditingSeats] = useState(false);
  const [editedSeatNumbers, setEditedSeatNumbers] = useState({});
  const [showArchivedItinerary, setShowArchivedItinerary] = useState(false);
  const [archivedComparisonMode, setArchivedComparisonMode] = useState('newest'); // 'newest' | 'oldest' when multiple changes
  const [isSavingSeats, setIsSavingSeats] = useState(false);
  const [tripUser, setTripUser] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRequestChangeModal, setShowRequestChangeModal] = useState(false);
  const [showAirlineChangeModal, setShowAirlineChangeModal] = useState(false);
  const [isCancellingDeferaZero, setIsCancellingDeferaZero] = useState(false);
  // Defera Zero 75% cash refund: server-computed preview, then confirm.
  const [zeroRefundPreview, setZeroRefundPreview] = useState(null);
  const [isLoadingZeroRefund, setIsLoadingZeroRefund] = useState(false);
  const [isExecutingZeroRefund, setIsExecutingZeroRefund] = useState(false);

  // Unbooked (no airline confirmation) cancel-for-credit: paid-so-far amount
  // shown in the new overlay, fetched from the per-processor schedule endpoint.
  const [cancelCreditPaidCents, setCancelCreditPaidCents] = useState(null);
  /** The Faye premium inside what was paid, when its policy was never issued:
   *  it goes back to the card on cancel instead of into Defera Credit. */
  const [cancelPremiumRefundCents, setCancelPremiumRefundCents] = useState(0);
  /** The part of that refund inside the paid figure, so it comes off the credit
   *  (none of it when insurance was charged upfront, outside the plan). */
  const [cancelPremiumInPaidCents, setCancelPremiumInPaidCents] = useState(0);
  // Ops-only hotel lifecycle (migration 153): whether WE have the room booked.
  // Local mirror so the card reflects a change without a full trip refetch.
  const [stayStatusOverride, setStayStatusOverride] = useState(null);
  /** The Faye policy row after an ops move, until the trip reloads. */
  const [insurancePolicyOverride, setInsurancePolicyOverride] = useState(null);
  const [stayConfirmationInput, setStayConfirmationInput] = useState('');
  const [isSavingStayStatus, setIsSavingStayStatus] = useState(false);
  const [isLoadingCancelCredit, setIsLoadingCancelCredit] = useState(false);
  // Defaulted + unbooked trip → Defera Credit opt-in (progress-bar button).
  // Two-step confirm: first click arms the button, second click converts.
  const [isConvertingDefaultCredit, setIsConvertingDefaultCredit] = useState(false);
  const [defaultCreditArmed, setDefaultCreditArmed] = useState(false);
  // Admin-only: reinstate a defaulted trip (regenerate schedule + re-hook
  // processor via admin-reinstate-defaulted-trip). Same two-step confirm.
  const [isReinstating, setIsReinstating] = useState(false);
  const [reinstateArmed, setReinstateArmed] = useState(false);
  const [showPurchaseSeatsModal, setShowPurchaseSeatsModal] = useState(false);
  const [showSeatPurchaseFlow, setShowSeatPurchaseFlow] = useState(false); // live seat-map purchase (one-time)
  const [showBaggagePurchaseFlow, setShowBaggagePurchaseFlow] = useState(false);
  const [showChangeSeatModal, setShowChangeSeatModal] = useState(false);
  const [showCabinUpgradeModal, setShowCabinUpgradeModal] = useState(false);
  // Admin-only live price check (SerpAPI) for Cost Breakdown comparison
  const [livePriceCheck, setLivePriceCheck] = useState(null);
  const [livePriceCheckError, setLivePriceCheckError] = useState(null);
  const [isCheckingLivePrice, setIsCheckingLivePrice] = useState(false);
  const [livePriceHistory, setLivePriceHistory] = useState([]);
  const [livePriceHistoryError, setLivePriceHistoryError] = useState(null);
  const [isLoadingLivePriceHistory, setIsLoadingLivePriceHistory] = useState(false);
  const [livePriceHistoryExpanded, setLivePriceHistoryExpanded] = useState(false);
  // Track which copy-enabled field (booking ref, confirmation #, passenger
  // contact details, credit reference / pin, etc.) was just copied to the
  // clipboard, so the corresponding button can flash a Check state for ~1.5s
  // without forcing a full re-render of its surrounding section. Field keys
  // are namespaced per call site (e.g. `pax-{id}-email`, `credit-{id}-pin`)
  // to avoid collisions.
  const [copiedField, setCopiedField] = useState(null);
  const [openSeatMapKey, setOpenSeatMapKey] = useState(null); // `${legIndex}-${segIndex}` of the expanded seat map
  const handleCopyField = useCallback(async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedField(key);
      toast.success('Copied to clipboard');
      setTimeout(() => {
        setCopiedField((current) => (current === key ? null : current));
      }, 1500);
    } catch {
      toast.error('Could not copy');
    }
  }, []);
  const changeFlightSearchUrl = useMemo(() => {
    const main = trip?.[0];
    const flightData = main?.flightData || main?.flight_data || {};
    const slices = flightData.slices || [];
    const firstSlice = slices[0];
    const retSlice = slices[1];
    const seg0 = firstSlice?.segments?.[0];
    const lastSeg = firstSlice?.segments?.[firstSlice?.segments?.length - 1];
    const origin = seg0?.origin?.iata_code || main?.departure_city?.slice(0, 3)?.toUpperCase() || 'XXX';
    const dest = lastSeg?.destination?.iata_code || main?.arrival_city?.slice(0, 3)?.toUpperCase() || 'XXX';
    // Use raw date from departing_at (slice 0,10) to avoid UTC conversion shifting dates (e.g. June 13 10pm local → June 14 UTC)
    const depDate = seg0?.departing_at ? (String(seg0.departing_at).slice(0, 10) || main?.departure_date?.slice(0, 10) || '') : main?.departure_date?.slice(0, 10) || '';
    let retDate = retSlice?.segments?.[0]?.departing_at ? String(retSlice.segments[0].departing_at).slice(0, 10) : '';
    if (!retDate && trip?.[1]) {
      const retFlight = trip[1];
      const retFd = retFlight?.flightData || retFlight?.flight_data || {};
      const retSeg = retFd?.slices?.[0]?.segments?.[0];
      retDate = retSeg?.departing_at ? String(retSeg.departing_at).slice(0, 10) : '';
    }
    // Passenger counts: prefer _searchParams (from original search), else derive from trip's actual passengers
    // Who to search for: the original search's passengers, else the trip's
    // travelers, else its passenger count (all adults).
    const searchParamsPassengers = flightData?._searchParams?.passengers;
    const tripPassengers = main?.passengers;
    const party = searchParamsPassengers?.length
      ? searchParamsPassengers
      : tripPassengers?.length
        ? tripPassengers.map((p) => ({ type: p.type || 'adult' }))
        : Array.from({ length: Math.max(1, main?.passenger_count ?? main?.passengerCount ?? 1) }, () => ({ type: 'adult' }));
    // The PURCHASED cabin, as a search code. `main.cabin_class` is the fare
    // brand ("First", "Delta Main Classic"), and passing it raw silently
    // searched economy for every premium fare — 250ABB bought United First and
    // was offered only Basic and Main. See src/utils/cabinClass.js.
    const cabin = cabinForChangeSearch(main);
    const params = new URLSearchParams({
      origin,
      destination: dest,
      departureDate: depDate || new Date().toISOString().slice(0, 10),
      cabin,
    });
    writePassengerParams(params, party);
    if (retDate) params.set('returnDate', retDate);
    params.set('searchMethod', 'change_flight');
    return createPageUrl('search') + `?${params.toString()}`;
  }, [trip]);
  const changeFlightRouting = useMemo(() => getChangeFlightRouting(trip), [trip]);
  const handleChangeFlightClick = useCallback(() => {
    // Completed (ticketed) trips: the in-app change-search can't touch an
    // issued ticket — point the customer at the airline instead. Slice Pay
    // plans: no in-app schedule to validate new dates against — route to a
    // support message. Only everything else opens the change search.
    if (changeFlightRouting === 'airline_direct') {
      setShowAirlineChangeModal(true);
      return;
    }
    if (changeFlightRouting === 'inquiry') {
      setShowRequestChangeModal(true);
      return;
    }
    onClose?.();
    navigate(changeFlightSearchUrl, { state: { changeFlightTrip: trip } });
  }, [onClose, changeFlightSearchUrl, trip, navigate, changeFlightRouting]);
  const changeFlightEligibility = useMemo(() => isChangeFlightEligible(trip), [trip]);
  const tripOwnerId = trip?.[0]?.user_id || trip?.[0]?.userId;
  /** The signed-in viewer is the customer who booked this trip. */
  const viewerOwnsTrip = !!tripOwnerId && tripOwnerId === currentUser?.id;
  /** Staff who may READ this trip's internals: admins, and the concierge
   *  agent since 2026-10-08 (full view access for Meta Muse). Every control
   *  inside those panels stays on isAdmin. */
  const staffView = canViewTripInternals(currentUser);
  // Live seat-map purchase: offered to the booking owner, or to an admin
  // acting on the customer's behalf (the server always charges the CUSTOMER's
  // Stripe profile, never the admin's). Only on eligible airlines and only
  // while the booking has no seat assignments — strictly once per booking.
  const seatPurchaseEligibility = useMemo(() => getSeatPurchaseEligibility(trip?.[0]), [trip]);
  const isSeatPurchaseActAs = tripOwnerId !== currentUser?.id;
  const canUseSeatPurchaseFlow = seatPurchaseEligibility.eligible
    && (!isSeatPurchaseActAs || currentUser?.role === 'admin');
  const hasSeatAssignments = useMemo(() => {
    const seatAssignments =
      trip?.[0]?.flightData?.seat_assignments || trip?.[0]?.flight_data?.seat_assignments || {};
    return (
      Object.keys(seatAssignments).length > 0
      && Object.values(seatAssignments).some(
        (seats) => seats && typeof seats === 'object' && Object.keys(seats).length > 0,
      )
    );
  }, [trip]);
  const { balance: milesBalance, isLoading: isMilesLoading } = useMilesBalanceRealtime(
    isOpen && trip?.length ? tripOwnerId : null,
    { isOwnUser: tripOwnerId === currentUser?.id }
  );

  const bookingId = trip && trip.length > 0 ? trip[0].id : null;

  /** The "Link a trip" form in the linked-trips banner, opened from the
   *  banner itself or the header's link button (admins). Closed per trip. */
  const [linkTripFormOpen, setLinkTripFormOpen] = useState(false);
  useEffect(() => { setLinkTripFormOpen(false); }, [bookingId, isOpen]);
  /** "This trip" on the customer's Traveling together card. */
  const travelingTogetherReference = (() => {
    const row = (trip && trip[0]) || {};
    return row.booking_reference || row.bookingReference || row.trip_reference || row.tripReference || null;
  })();

  // Reset live price check + history whenever the modal opens for a different booking
  useEffect(() => {
    setLivePriceCheck(null);
    setLivePriceCheckError(null);
    setIsCheckingLivePrice(false);
    setLivePriceHistory([]);
    setLivePriceHistoryError(null);
    setIsLoadingLivePriceHistory(false);
    setLivePriceHistoryExpanded(false);
    // The "finished paying" notice is per-booking state too. The modal
    // component never unmounts (parents render it unconditionally), so
    // without this a notice resolved after a mid-flight close would pop
    // over whichever trip gets opened next.
    setTripPaidNotice(null);
    setIsSubmittingTripPaid(false);
  }, [bookingId, isOpen]);

  const loadLivePriceHistory = useCallback(async () => {
    if (!bookingId) return;
    setIsLoadingLivePriceHistory(true);
    setLivePriceHistoryError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!isMounted()) return;
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }
      const res = await fetch(`/api/admin/booking-price-history?booking_id=${encodeURIComponent(bookingId)}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      const data = await res.json();
      if (!isMounted()) return;
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to load price history');
      }
      const history = Array.isArray(data.history) ? data.history : [];
      setLivePriceHistory(history);
      // Show the latest stored check as the headline result so admins don't re-fetch.
      // A manual quote for other flights is a line in the history, never the
      // trip's current fare.
      const headline = history.find((r) => !(r.source === 'manual' && !r.matched));
      if (headline && !livePriceCheck) {
        const latest = headline;
        setLivePriceCheck({
          ...latest,
          booking_id: bookingId,
          check_id: latest.id,
          checked_at: latest.checked_at
        });
      }
    } catch (err) {
      if (isMounted()) setLivePriceHistoryError(err.message || 'Failed to load price history');
    } finally {
      if (isMounted()) setIsLoadingLivePriceHistory(false);
    }
    // livePriceCheck intentionally excluded from deps — we only seed the headline once
    // per modal open; subsequent refresh clicks update it via handleCheckLivePrice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, isMounted]);

  // Auto-load history when the modal opens for staff so the timeline shows up immediately.
  useEffect(() => {
    if (!isOpen || !bookingId) return;
    if (!staffView) return;
    loadLivePriceHistory();
  }, [isOpen, bookingId, staffView, loadLivePriceHistory]);

  const handleCheckLivePrice = useCallback(async () => {
    if (!bookingId || isCheckingLivePrice) return;
    setIsCheckingLivePrice(true);
    setLivePriceCheckError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }
      const res = await fetch('/api/admin/check-booking-price', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ booking_id: bookingId })
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to fetch current price');
      }
      setLivePriceCheck(data);
      // Prepend to history so the timeline updates without a second round-trip.
      setLivePriceHistory((prev) => [
        {
          id: data.check_id ?? `local-${Date.now()}`,
          booking_id: bookingId,
          checked_at: data.checked_at,
          current_price_cents: data.current_price_cents,
          current_price_display: data.current_price_display,
          booked_base_cents: data.booked_base_cents,
          booked_base_display: data.booked_base_display,
          delta_cents: data.delta_cents,
          delta_display: data.delta_display,
          matched: data.matched,
          in_past: data.in_past ?? false,
          price_dropped: data.price_dropped,
          price_increased: data.price_increased,
          price_same: data.price_same,
          passenger_count: data.passenger_count,
          adults: data.adults,
          children: data.children,
          infants: data.infants,
          cabin_class: data.cabin_class,
          currency: data.currency
        },
        ...prev
      ]);
    } catch (err) {
      setLivePriceCheckError(err.message || 'Failed to fetch current price');
    } finally {
      setIsCheckingLivePrice(false);
    }
  }, [bookingId, isCheckingLivePrice]);

  const refetchPassengers = useCallback(async () => {
    if (!bookingId) return;
    
    // First, check if passengers are already included in the trip data (from backend API)
    const tripPassengers = trip?.[0]?.passengers;
    console.log('🔍 [refetchPassengers] Checking for trip passengers:', {
      hasTripData: !!trip?.[0],
      tripId: trip?.[0]?.id,
      passengersField: trip?.[0]?.passengers,
      passengersLength: tripPassengers?.length,
      isArray: Array.isArray(tripPassengers)
    });
    if (tripPassengers && Array.isArray(tripPassengers) && tripPassengers.length > 0) {
      console.log('📦 Using passengers from trip data:', tripPassengers.length, 'passengers');
      // Normalize passenger data from raw database format
      const normalizedPassengers = tripPassengers.map(p => ({
        id: p.id,
        bookingId: p.booking_id,
        type: p.type,
        title: p.title,
        firstName: p.given_name || p.firstName,
        lastName: p.family_name || p.lastName,
        givenName: p.given_name || p.givenName,
        familyName: p.family_name || p.familyName,
        gender: p.gender,
        bornOn: p.born_on || p.bornOn || p.date_of_birth || p.dateOfBirth,
        nationality: p.nationality,
        email: p.email,
        phoneNumber: p.phone_number || p.phoneNumber,
        passportNumber: p.passport_number || p.passportNumber,
        passportExpiryDate: p.passport_expiry_date || p.passportExpiryDate,
        passportCountry: p.passport_country || p.passportCountry,
        frequentFlyerProgram: p.frequent_flyer_program || p.frequentFlyerProgram,
        frequentFlyerNumber: p.frequent_flyer_number || p.frequentFlyerNumber,
        knownTravelerNumber: p.known_traveler_number || p.knownTravelerNumber,
        specialRequests: p.special_requests || p.specialRequests,
        seatPreference: p.seat_preference || p.seatPreference,
        middleName: p.middle_name || p.middleName,
        wheelchairAssistance: p.wheelchair_assistance || p.wheelchairAssistance || null
      }));
      normalizedPassengers.forEach((p, i) => {
        console.log(`  Passenger ${i + 1}:`, p.firstName || p.givenName, p.lastName || p.familyName, '| ID:', p.id);
      });
      setPassengers(normalizedPassengers);
      return;
    }
    
    // Fallback: fetch passengers separately if not included in trip data
    try {
      console.log('🔍 Fetching passengers for booking:', bookingId);
      const passengerData = await Passenger.findByBookingId(bookingId);
      console.log('📦 Fetched passenger data:', passengerData.length, 'passengers');
      passengerData.forEach((p, i) => {
        console.log(`  Passenger ${i + 1}:`, p.firstName || p.givenName, p.lastName || p.familyName, '| ID:', p.id);
      });
      setPassengers(passengerData);
    } catch (error) {
      console.error("Failed to load passenger data:", error);
      setPassengers([]);
    }
  }, [bookingId, trip]);

  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !bookingId) {
        return;
      }

      setIsLoading(true);
      try {
        console.log('🔄 FlightDetailsModal fetching passengers for booking:', bookingId);
        await refetchPassengers();

        if (canViewTripInternals(currentUser)) {
          // Through the server: the table's RLS admits admins only.
          InternalComment.listForTrip(bookingId)
            .then(commentData => setComments(commentData))
            .catch(error => {
              console.error("Failed to load comments (non-critical):", error);
              setComments([]);
            });
          
          // Who booked it: one account, by trip, readable by any staff viewer.
          const userId = trip?.[0]?.userId || trip?.[0]?.user_id;
          if (userId) {
            setIsLoadingUser(true);
            try {
              const user = await User.findTripOwner(bookingId);
              setTripUser(user);
            } catch (error) {
              console.error("Failed to load user information (non-critical):", error);
              setTripUser(null);
            } finally {
              setIsLoadingUser(false);
            }
          }

        }
      } catch (error) {
        console.error("Error in FlightDetailsModal data fetch:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isOpen, bookingId, currentUser?.role, refetchPassengers, trip]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Fake "Processing" progress step: for the first 30 seconds after a booking
  // is created, the progress bar shows a "Processing" stage (not a real trip
  // status) before advancing to "Paying Off" — gives new customers visible
  // reassurance that their trip was processed and secured.
  const PROCESSING_WINDOW_MS = 30 * 1000;
  const [isProcessingWindow, setIsProcessingWindow] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    const createdRaw = trip?.[0]?.created_at || trip?.[0]?.createdAt;
    const createdMs = createdRaw ? new Date(createdRaw).getTime() : NaN;
    const msLeft = Number.isFinite(createdMs) ? (createdMs + PROCESSING_WINDOW_MS) - Date.now() : 0;
    if (msLeft <= 0) {
      setIsProcessingWindow(false);
      return;
    }
    setIsProcessingWindow(true);
    const timer = setTimeout(() => setIsProcessingWindow(false), msLeft);
    return () => clearTimeout(timer);
  }, [isOpen, trip]);

  /** Booked less than 24 hours ago: a cancel sends the deposit back to the card
   *  (app-wide policy; supabase/functions/_shared/deposit-24h-return.ts and the
   *  NMI cancel). Read when the cancel overlay opens. */
  const bookedWithin24h = useMemo(() => {
    const firstBooking = (Array.isArray(trip) && trip[0]) || {};
    const createdRaw = firstBooking.created_at || firstBooking.createdAt;
    const createdMs = createdRaw ? new Date(createdRaw).getTime() : NaN;
    const age = Date.now() - createdMs;
    return Number.isFinite(createdMs) && age >= 0 && age <= 24 * 60 * 60 * 1000;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip, showCancelModal]);

  // Defera Zero: can cancel before first payment (no API - use payment schedule like Reserve Cancel Plan).
  // First payment = booking_date + 7 days (weekly) or 14 days (biweekly). If today < first payment, allow cancel.
  const canInstantCancelDeferaZero = useMemo(() => {
    if (!showCancelModal || !trip?.[0]) return false;
    const mf = trip[0];
    const fd = mf?.flightData || mf?.flight_data || {};
    const paymentMethod = fd._payment_method || fd.payment_method || mf.payment_method || 'installments';
    if (paymentMethod !== 'defera_zero') return false;
    const ps = fd.payment_schedule || {};
    const bookingDate = ps.booking_date || mf.created_at || mf.createdAt;
    const paymentPlan = ps.payment_plan || fd._payment_frequency || 'weekly';
    if (!bookingDate) return false;
    const booking = new Date(bookingDate);
    if (isNaN(booking.getTime())) return false;
    const intervalDays = paymentPlan === 'biweekly' ? 14 : 7;
    const firstPayment = new Date(booking);
    firstPayment.setDate(firstPayment.getDate() + intervalDays);
    const today = startOfDay(new Date());
    const firstPaymentStart = startOfDay(firstPayment);
    return today < firstPaymentStart;
  }, [showCancelModal, trip]);

  // Unbooked installment trip (Defera Zero / Capital, no airline confirmation
  // yet) → the customer can cancel ANYTIME and get 100% of what they've paid
  // back as Defera Credit. This takes priority over the legacy cancel screens.
  const isUnbookedInstallmentTrip = useMemo(() => {
    const mf = trip?.[0];
    if (!mf) return false;
    const fd = mf?.flightData || mf?.flight_data || {};
    const pm = fd._payment_method || fd.payment_method || mf.payment_method || 'installments';
    if (pm !== 'defera_zero' && pm !== 'defera_capital') return false;
    const status = String(mf.trip_status || mf.status || '').toLowerCase();
    if (status === 'cancelled' || status === 'completed' || status === 'defaulted') return false;
    /**
     * A hotel we have already booked needs a human to unwind it with the
     * property, so it takes the trip out of the self-serve 100%-credit
     * overlay — the same answer the cancel endpoints now give (they refuse
     * with STAY_BOOKED_NEEDS_TEAM). The inverse is the standing rule: nothing
     * booked on our end means 100% back as Defera Credit, whatever the room's
     * own terms say, which is why this asks about OUR reservation rather than
     * about refundability.
     */
    const stayOnTrip = mf.stay_data || (Array.isArray(mf.stays) ? mf.stays[0] : mf.stays) || null;
    if (isStayBooked(stayOnTrip)) return false;
    // Same for a Faye policy already issued: Defera has paid Faye, so cancelling
    // goes to the team (who can cancel it inside Faye's free look).
    if (trip.some((row) => readTripInsurance(row)?.status === INSURANCE_STATUS.ISSUED)) return false;
    return !tripHasAirlineConfirmation(trip);
  }, [trip]);

  const flightOptionsState = useMemo(() => getFlightOptionsState(trip), [trip]);
  const tripIsCompleted = useMemo(() => isTripCompleted(trip), [trip]);
  // A trip paid with Defera Miles alone owes nothing: its cancel modal is the
  // paid-in-full one (the team handles it, and a cancellation returns the
  // Miles), never the plan's "stop payments, balance to collections".
  const tripPaidWithMilesAlone = useMemo(() => {
    const fd = (Array.isArray(trip) ? trip[0] : trip)?.flight_data || {};
    return [fd._payment_method, fd.payment_method, fd.booking_option].includes('miles');
  }, [trip]);

  const cancelMode = isUnbookedInstallmentTrip
    ? 'unbooked_credit'
    : (canInstantCancelDeferaZero
      ? 'instant_zero'
      : (tripIsCompleted || tripPaidWithMilesAlone ? 'completed' : 'policy'));

  // When the unbooked cancel-for-credit overlay opens, load how much the
  // customer has paid so far (the amount we'll refund as Defera Credit) from
  // the per-processor schedule endpoint. The backend recomputes authoritatively
  // on submit; this is purely to show the figure up front.
  useEffect(() => {
    if (!showCancelModal || cancelMode !== 'unbooked_credit' || !bookingId) {
      setCancelCreditPaidCents(null);
      setCancelPremiumRefundCents(0);
      setCancelPremiumInPaidCents(0);
      return;
    }
    let aborted = false;
    (async () => {
      setIsLoadingCancelCredit(true);
      setCancelCreditPaidCents(null);
      setCancelPremiumRefundCents(0);
      setCancelPremiumInPaidCents(0);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const mf = trip?.[0] || {};
        const fd = mf.flightData || mf.flight_data || {};
        const pm = fd._payment_method || fd.payment_method;
        const isNmi = pm === 'defera_capital' && fd._payment_processor === 'nmi';
        const scheduleUrl = pm === 'defera_capital'
          ? (isNmi ? deferaCapitalNmiUrl('schedule') : deferaCapitalUrl('schedule'))
          : deferaZeroUrl('schedule');
        const res = await fetch(`${scheduleUrl}?booking_id=${bookingId}`, {
          headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` }),
        });
        const data = await res.json().catch(() => ({}));
        if (aborted || !res.ok) return;
        // What we display in the cancel-for-credit modal is the TOTAL the
        // customer will receive back, not just the cash leg. There are two
        // legs and both end up in their Defera Credit balance:
        //   1. paid_amount_cents — what the trip has been paid. On NMI that is
        //      net cash; on the Stripe rails it also counts Defera Credit an
        //      invoice already consumed (netted cycles, a credit-paid deposit).
        //   2. restorable_defera_credit_cents — the committed credit NOT already
        //      inside (1). The schedule endpoints keep the two legs disjoint, so
        //      the sum is cash + committed credit: exactly what the cancel
        //      endpoint returns (cash via `grantCancellationCredit`, credit via
        //      `restore_defera_credit_on_cancel`).
        // Showing only (1) under-states the refund whenever credit was applied
        // at checkout (Flavio's case: $0 net cash deposit + $48.55 restorable),
        // which is why a previous version of this modal could read "$0.00" for
        // an unbooked cancel that would in fact pay back ~$50. Sum on the
        // client so the BE response shape stays additive — the cancel handler
        // still recomputes both legs authoritatively on submit.
        let paid = Number(data?.paid_amount_cents);
        if (!Number.isFinite(paid)) {
          const total = Number(data?.total_amount_cents);
          const remaining = Number(data?.remaining_cents);
          paid = (Number.isFinite(total) && Number.isFinite(remaining)) ? Math.max(0, total - remaining) : 0;
        }
        const restorable = Number(data?.restorable_defera_credit_cents);
        /** Less any Extension Fee (moving a payment): never refunded. The server
         *  takes it from what was paid by card (never from restored credit), so
         *  the preview does too. */
        const extensionFeeKept = Math.min(retainedExtensionFeesCents(fd), Math.max(0, paid));
        const total = Math.max(0, paid + (Number.isFinite(restorable) ? Math.max(0, restorable) : 0) - extensionFeeKept);
        setCancelCreditPaidCents(Math.max(0, Math.round(total)));
        // A Faye premium whose policy was never issued is refunded to the card,
        // not credited. The server's figure wins once it sends one; until then
        // it's the settlement's split (cancelInsuranceSplit): the premium's
        // share of the payments that carried it, or, when it was charged
        // upfront after booking, all of it and none of it inside `paid`.
        const policy = (trip || []).map(readTripInsurance).find(Boolean);
        const serverPremium = Number(data?.insurance_premium_refund_cents);
        let split = { refundCents: 0, insidePaidCents: 0 };
        if (Number.isFinite(serverPremium)) {
          const cents = Math.max(0, Math.round(serverPremium));
          split = { refundCents: cents, insidePaidCents: cents };
        } else {
          split = cancelInsuranceSplit({ policy, booking: { ...mf, flight_data: fd }, paidCents: Math.max(0, paid) });
        }
        const inside = Math.min(split.insidePaidCents, Math.max(0, Math.round(total)));
        setCancelPremiumInPaidCents(inside);
        setCancelPremiumRefundCents(split.refundCents - split.insidePaidCents + inside);
      } catch {
        /* leave null — overlay falls back to generic copy */
      } finally {
        if (!aborted) setIsLoadingCancelCredit(false);
      }
    })();
    return () => { aborted = true; };
  }, [showCancelModal, cancelMode, bookingId, trip]);

  /** Record ops' judgement about the room. Admin-only endpoint; the button
   *  block that calls it is gated on isAdmin. */
  const setStayStatus = async (nextStatus) => {
    const bookingId = trip?.[0]?.id;
    if (!bookingId || isSavingStayStatus) return;
    setIsSavingStayStatus(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/admin/stay-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({
          booking_id: bookingId,
          status: nextStatus,
          ...(nextStatus === 'reserved' && stayConfirmationInput.trim()
            ? { supplier_confirmation: stayConfirmationInput.trim() }
            : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || 'Could not update the stay');
      setStayStatusOverride(data.stay?.status || nextStatus);
      setStayConfirmationInput('');
    } catch (e) {
      alert(e.message || 'Could not update the stay');
    } finally {
      setIsSavingStayStatus(false);
    }
  };
  /**
   * Flight + Hotel packages are financed as ONE plan, so a customer looking at
   * a single "back as Defera Credit" figure can't tell whether the hotel is
   * part of it. It is. Split what they've paid across the package the same way
   * they were charged for it — proportionally — so the hotel is visible.
   *
   * Reads both spellings of every field: this gets a FlightBooking entity
   * (camelCase, no snake_case mirrors) in the app and a raw row in tests.
   */
  /** ", less the $35.00 Extension Fee": moving a payment is never refunded. */
  const cancelExtensionFeeNote = useMemo(() => {
    const main = trip?.[0] || {};
    const fee = retainedExtensionFeesCents(main.flightData || main.flight_data || {});
    return fee > 0 ? `, less the $${fmtCurrency(fee)} Extension Fee` : '';
  }, [trip]);
  const cancelPackageSplit = useMemo(() => {
    const main = trip?.[0] || {};
    const fd = main.flightData || main.flight_data || {};
    const stay = main.stay_data || main.stayData || main.stay_snapshot
      || (Array.isArray(main.stays) ? main.stays[0] : null) || fd.stay_data || null;
    const toCents = (v) => Math.round((Number(v) || 0) * 100);
    return splitPaidAcrossPackage({
      paidCents: Math.max(0, (Number(cancelCreditPaidCents) || 0) - cancelPremiumInPaidCents),
      flightCents: toCents(fd.total_amount ?? fd.base_amount),
      stayCents: toCents(stay?.total_amount ?? stay?.totalAmount),
      feeCents: toCents(main.service_fee ?? main.serviceFee) + toCents(main.hold_fee ?? main.holdFee),
    });
  }, [cancelCreditPaidCents, cancelPremiumInPaidCents, trip]);

  /** The hotel's name, for the breakdown line. */
  const cancelStayName = useMemo(() => {
    const main = trip?.[0] || {};
    const fd = main.flightData || main.flight_data || {};
    const stay = main.stay_data || main.stayData || main.stay_snapshot
      || (Array.isArray(main.stays) ? main.stays[0] : null) || fd.stay_data || null;
    const name = stay?.name || stay?.hotel_name;
    return typeof name === 'string' && name.trim() ? name.trim() : 'Hotel';
  }, [trip]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    // SECURITY: Only admins can submit internal comments
    const roles = currentUser?.app_metadata?.roles || [];
    const isAdmin = roles.includes('admin');
    if (!newComment.trim() || !isAdmin) return;

    setIsSubmittingComment(true);
    try {
      await InternalComment.create({
        bookingId: trip[0].id,
        comment: newComment.trim(),
      });
      setNewComment('');
      const commentData = await InternalComment.findByBookingId(trip[0].id);
      setComments(commentData);
    } catch (error) {
      console.error('Failed to submit comment:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      alert('Failed to submit comment: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleDelete = async () => {
    if (!trip || trip.length === 0) return;
    
    // Double-check confirmation text
    if (deleteConfirmationText.trim() !== 'Yes please delete') {
      toast.error('Please type "Yes please delete" to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      console.log('🗑️ Starting trip deletion process...', { tripCount: trip.length, tripIds: trip.map(f => f.id) });
      
      // Delete passengers first
      for (const flight of trip) {
        console.log('Deleting passengers for booking:', flight.id);
        try {
          const flightPassengers = await Passenger.findByBookingId(flight.id);
          console.log(`Found ${flightPassengers.length} passengers to delete`);
          
          for (const passenger of flightPassengers) {
            try {
              await Passenger.delete(passenger.id);
              console.log('✅ Deleted passenger:', passenger.id);
            } catch (passengerError) {
              console.error('❌ Failed to delete passenger:', passenger.id, passengerError);
              throw new Error(`Failed to delete passenger: ${passengerError.message}`);
            }
          }
        } catch (passengerFetchError) {
          console.error('❌ Failed to fetch passengers:', passengerFetchError);
          // Continue with booking deletion even if passenger fetch fails
        }
      }

      // Delete bookings using static method
      for (const flight of trip) {
        console.log('Deleting booking:', flight.id);
        try {
          await FlightBooking.delete(flight.id);
          console.log('✅ Deleted booking:', flight.id);
        } catch (bookingError) {
          console.error('❌ Failed to delete booking:', flight.id, bookingError);
          throw new Error(`Failed to delete booking: ${bookingError.message}`);
        }
      }

      // Delete related comments (admin only)
      if (currentUser?.role === 'admin') {
        console.log('Deleting related comments for booking:', trip[0].id);
        try {
          const relatedComments = await InternalComment.findByBookingId(trip[0].id);
          console.log(`Found ${relatedComments.length} comments to delete`);
          
          for (const comment of relatedComments) {
            try {
              await InternalComment.delete(comment.id);
              console.log('✅ Deleted comment:', comment.id);
            } catch (commentError) {
              console.error('❌ Failed to delete comment:', comment.id, commentError);
              // Don't throw - comments deletion failure shouldn't block trip deletion
            }
          }
        } catch (commentFetchError) {
          console.error('❌ Failed to fetch comments:', commentFetchError);
          // Continue even if comment fetch fails
        }
      }

      console.log('✅ Trip deletion completed successfully');
      toast.success('Trip deleted successfully');
      onClose();
      if (onUpdate) {
        onUpdate(); // Refresh the trips list
      }
    } catch (error) {
      console.error('❌ Error deleting itinerary:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        tripIds: trip.map(f => f.id)
      });
      toast.error(`Failed to delete trip: ${error.message || 'Unknown error. Please check console for details.'}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteConfirmationText('');
    }
  };

  const handleSaveSeatAssignments = async () => {
    setIsSavingSeats(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No session found');
      }

      // Build FULL seat_assignments: merge existing + edited so we never lose other segments
      const existing = trip?.[0]?.flightData?.seat_assignments || trip?.[0]?.flight_data?.seat_assignments || {};
      const seatAssignments = {};
      (trip?.[0]?.flightData?.slices || trip?.[0]?.flight_data?.slices || []).forEach((slice, sliceIndex) => {
        (slice.segments || []).forEach((segment, segIndex) => {
          const segmentId = segment.id || `segment-${sliceIndex}-${segIndex}`;
          passengers.forEach((passenger, passengerIndex) => {
            const passengerId = passenger.id || `passenger-${passengerIndex}`;
            const editedVal = editedSeatNumbers[passengerId]?.[segmentId];
            const existingVal = existing[passengerId]?.[segmentId] || '';
            const value = editedVal !== undefined ? editedVal : existingVal;
            if (value?.trim()) {
              if (!seatAssignments[passengerId]) seatAssignments[passengerId] = {};
              seatAssignments[passengerId][segmentId] = value.trim();
            }
          });
        });
      });

      // Update via backend admin endpoint - send full seat_assignments
      const response = await fetch(`/api/admin/update-booking`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify({
          bookingId: bookingId,
          seat_assignments: seatAssignments
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update seat assignments');
      }

      toast.success('Seat assignments saved successfully');
      setIsEditingSeats(false);
      setEditedSeatNumbers({});
      
      // Refresh the trip data
      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error('Error saving seat assignments:', error);
      toast.error(`Failed to save seat assignments: ${error.message}`);
    } finally {
      setIsSavingSeats(false);
    }
  };

  const handleAdminSave = async () => {
    setShowAdminEdit(false);
    // Reload fresh data from database to show updated confirmation number
    if (onUpdate) {
      await onUpdate();
    }
  };

  const handlePassengerSave = async (updatedPassengers) => {
    try {
      console.log('💾 Saving passengers:', updatedPassengers.length, 'passengers');

      // Delete passengers that were removed in the edit modal
      const updatedIds = new Set(updatedPassengers.filter(p => p.id).map(p => p.id));
      const removedPassengers = (passengers || []).filter(p => p.id && !updatedIds.has(p.id));
      for (const removed of removedPassengers) {
        console.log(`🗑️ Deleting removed passenger (ID: ${removed.id})`);
        await new Passenger({ id: removed.id }).delete();
        console.log(`✅ Successfully deleted passenger (ID: ${removed.id})`);
      }

      for (let i = 0; i < updatedPassengers.length; i++) {
        const passenger = updatedPassengers[i];
        console.log(`💾 Processing passenger ${i + 1}:`, {
          id: passenger.id,
          firstName: passenger.firstName,
          lastName: passenger.lastName,
          hasUserId: !!passenger.userId || !!passenger.user_id
        });
        
        if (passenger.id) {
          // Update existing passenger - preserve userId
          // Normalize field names to ensure lastName/firstName override familyName/givenName
          const normalizedData = {
            id: passenger.id,
            userId: passenger.userId || passenger.user_id || passengers.find(p => p.id === passenger.id)?.userId,
            bookingId: passenger.bookingId,
            type: passenger.type,
            title: passenger.title,
            givenName: passenger.firstName || passenger.givenName,
            familyName: passenger.lastName || passenger.familyName,
            gender: passenger.gender,
            bornOn: passenger.bornOn,
            email: passenger.email,
            phoneNumber: passenger.phoneNumber,
            passportNumber: passenger.passportNumber,
            passportExpiryDate: passenger.passportExpiryDate,
            passportCountry: passenger.passportCountry,
            nationality: passenger.nationality,
            frequentFlyerProgram: passenger.frequentFlyerProgram,
            frequentFlyerNumber: passenger.frequentFlyerNumber,
            knownTravelerNumber: passenger.knownTravelerNumber,
            specialRequests: passenger.specialRequests,
            seatPreference: passenger.seatPreference || passenger.seat_preference,
            middleName: passenger.middleName || passenger.middle_name
          };
          
          const passengerEntity = new Passenger(normalizedData);
          console.log(`✏️ Updating passenger ${i + 1} (ID: ${passenger.id})`);
          await passengerEntity.save();
          console.log(`✅ Successfully updated passenger ${i + 1}`);
        } else if (passenger.firstName || passenger.lastName) {
          // Create new passenger (only if they have some data)
          const newPassengerData = {
            ...passenger,
            bookingId: bookingId, // Associate with the current booking
          };
          const passengerEntity = new Passenger(newPassengerData);
          console.log(`➕ Creating new passenger ${i + 1}`);
          await passengerEntity.save();
          console.log(`✅ Successfully created passenger ${i + 1}`);
        }
      }

      console.log('✅ All passengers saved successfully, refetching...');
      await refetchPassengers();
      setShowPassengerEdit(false);
      
      // Refresh the parent trip data to show updated passengers
      if (onUpdate) {
        console.log('🔄 Calling onUpdate to refresh parent trip data...');
        await onUpdate();
      }
      
      toast.success('Passenger information saved successfully');
    } catch (error) {
      console.error('❌ Error updating passengers:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      // Show error to user with more details
      toast.error(`Failed to save passenger information: ${error.message}`);
    }
  };

  /** A traveler's wheelchair answers were saved (WheelchairAssistanceCard):
   *  show them at once, then refresh the parent like a passenger edit does. */
  const handleWheelchairSaved = (passengerId, record) => {
    setPassengers((prev) => prev.map((p) => (p.id === passengerId ? { ...p, wheelchairAssistance: record } : p)));
    toast.success('Wheelchair assistance saved');
    onUpdate?.();
  };

  const handleGeneratePayment = async () => {
    // Check if payment URL already exists in the booking
    const flightWithPrice =
      trip.find(
        (f) =>
          (f.totalPrice && f.totalPrice > 0) ||
          (f.total_amount && f.total_amount > 0) ||
          (f.total_price && f.total_price > 0) ||
          parseFloat(f.grand_total_with_hold ?? f.grandTotalWithHold ?? 0) > 0 ||
          parseFloat(f.grand_total ?? f.grandTotal ?? 0) > 0,
      ) || trip[0];
    const existingPaymentUrl = flightWithPrice?.payment_url || flightWithPrice?.paymentUrl;
    
    if (existingPaymentUrl) {
      // Use the existing payment URL instead of generating a new one
      window.open(existingPaymentUrl, '_blank');
      return;
    }

    // If no existing payment URL, generate a new one (fallback)
    setIsGeneratingPayment(true);
    try {
      const totalPrice = flightWithPrice ? (flightWithPrice.totalPrice || flightWithPrice.total_amount || flightWithPrice.total_price || 0) : 0;
      const serviceFeeRate = getServiceFeeRate(totalPrice, false);
      const grandTotal = flightWithPrice?.grand_total || totalPrice + (totalPrice * serviceFeeRate);
      
      const baseUrl = publicOrigin();
      const success_url = `${baseUrl}${createPageUrl('Dashboard')}?payment=success`;
      const cancel_url = `${baseUrl}${createPageUrl('Dashboard')}`;

      const { data } = await initiateSlicePayFlow({
        flight: {
          ...flightWithPrice,
          total_amount: grandTotal,
          total_currency: 'USD'
        },
        passengers: passengers.map(p => ({
          first_name: p.given_name || p.first_name,
          last_name: p.family_name || p.last_name,
          email: p.email
        })),
        success_url,
        cancel_url
      });

      if (data && data.embedded_flow_url) {
        window.open(data.embedded_flow_url, '_blank');
      } else {
        throw new Error('Could not retrieve payment link');
      }
    } catch (error) {
      console.error('Payment generation error:', error);
      alert('Failed to generate payment link. Please try again.');
    } finally {
      setIsGeneratingPayment(false);
    }
  };

  // Visa and passport checks for the staff view of each traveler (international
  // trips only). Called before the early return below: it's a hook.
  const firstBooking = isOpen && Array.isArray(trip) ? trip[0] : null;
  const travelRequirement = useTripTravelRequirements(
    firstBooking ? (firstBooking.flightData || firstBooking.flight_data || null) : null,
  );

  if (!isOpen || !trip || !Array.isArray(trip) || trip.length === 0) return null;

  const mainFlight = trip[0];
  const primaryFlight = trip.find(f => f.is_primary_flight) || mainFlight;
  const tripStatus = primaryFlight.trip_status || mainFlight.status || 'pending';
  // Staff can switch off the review asks on a single trip (flight_data._hide_review_card),
  // e.g. after a rough rebooking where asking for a review would land badly.
  const hideReviewCard = Boolean((mainFlight.flightData || mainFlight.flight_data || {})._hide_review_card);

  // Faye travel insurance (migration 156). The premium sits inside the stored
  // totals like a package's hotel, so miles, fees and the base fare below are
  // worked out from rows with it taken out, and it comes back as its own line.
  const insurancePremiumCents = tripInsurancePremiumCents(trip);
  const insurancePremium = insurancePremiumCents / 100;
  const rawPolicyRow = insurancePolicyOverride
    || trip.map((row) => (Array.isArray(row?.trip_insurance) ? row.trip_insurance[0] : row?.trip_insurance)).find(Boolean)
    || null;
  /** The policy as ops sees it (true status), for the ops strip. */
  const tripInsurance = rawPolicyRow ? readTripInsurance({ trip_insurance: rawPolicyRow }) : null;
  /** The policy as the customer sees it: no purchase shown before paid in full. */
  const customerInsurance = rawPolicyRow ? readTripInsurance({ trip_insurance: customerPolicyView(rawPolicyRow, mainFlight) }) : null;
  /** A plan added after booking and paid in full at once: outside the plan's
   *  totals (insurancePremium reads 0), so the cost lines show it on its own. */
  const insuranceUpfrontPremium = (tripInsuranceTotalCents(trip) - insurancePremiumCents) / 100;
  const viewerIsAdmin = (currentUser?.app_metadata?.roles || []).includes('admin') || currentUser?.role === 'admin';
  
  // Extract miles earned - check multiple sources with full fallback chain
  // Priority: flight_data.miles_earned (JSONB) > miles_earned column > milesEarned prop > calculate from grand_total
  const flightData = mainFlight.flightData || mainFlight.flight_data || {};
  let milesEarned = Number(flightData.miles_earned ?? 0);
  
  // Fallback to dedicated miles_earned column if JSONB field is empty
  if (milesEarned === 0) {
    milesEarned = Number(mainFlight.miles_earned ?? mainFlight.milesEarned ?? 0);
  }
  
  // Final fallback: calculate from grand_total if miles still 0 (2 miles per $1)
  if (milesEarned === 0 && (mainFlight.grand_total > 0 || mainFlight.grandTotal > 0)) {
    const travelRow = withoutInsurancePremium(mainFlight);
    const totalAmount = Number(travelRow.grand_total || travelRow.grandTotal || 0);
    milesEarned = Math.floor(totalAmount * 2);
  }

  const flightWithPrice =
    trip.find(
      (f) =>
        (f.totalPrice && f.totalPrice > 0) ||
        (f.total_amount && f.total_amount > 0) ||
        (f.total_price && f.total_price > 0) ||
        parseFloat(f.grand_total_with_hold ?? f.grandTotalWithHold ?? 0) > 0 ||
        parseFloat(f.grand_total ?? f.grandTotal ?? 0) > 0,
    ) || trip[0];
  
  // Extract stay data if available (check stay_data, stays array from API join, and flight_data.stay_data)
  const rawStay = mainFlight?.stay_data || primaryFlight?.stay_data || 
                  (Array.isArray(mainFlight?.stays) ? mainFlight?.stays?.[0] : mainFlight?.stays) ||
                  (Array.isArray(primaryFlight?.stays) ? primaryFlight?.stays?.[0] : primaryFlight?.stays) ||
                  (Array.isArray(trip?.[0]?.stays) ? trip?.[0]?.stays?.[0] : trip?.[0]?.stays) ||
                  mainFlight?.flight_data?.stay_data || mainFlight?.flightData?.stay_data ||
                  null;
  
  // Normalize stay data: handle both snake_case (from DB/stays table) and camelCase (from StayBooking/cart)
  const stayData = rawStay ? {
    name: rawStay.name,
    address: (() => {
      const a = rawStay.address;
      if (!a) return null;
      if (typeof a === 'string') return a;
      if (typeof a === 'object') return [a.street, a.address_line_1, a.address_line_2, a.city, a.region, a.postal_code, a.country].filter(Boolean).join(', ') || JSON.stringify(a);
      return null;
    })(),
    // Resolved with its scale: Duffel scores out of 10, Google out of 5.
    rating: resolveHotelRating(rawStay),
    reviews: rawStay.reviews,
    hotelClass: rawStay.hotelClass || rawStay.hotel_class,
    roomType: rawStay.roomType || rawStay.room_type || rawStay.metadata?.room_name,
    bedType: rawStay.bedType || rawStay.bed_type || (() => {
      const beds = rawStay.metadata?.beds;
      if (!beds) return null;
      if (Array.isArray(beds) && beds.length > 0 && typeof beds[0] === 'object') {
        return beds.map(b => `${b.count || 1} ${b.type || 'Bed'}`).join(', ');
      }
      return Array.isArray(beds) ? beds.join(', ') : beds;
    })(),
    check_in: rawStay.check_in || rawStay.checkInDate || rawStay.check_in_date,
    check_out: rawStay.check_out || rawStay.checkOutDate || rawStay.check_out_date,
    nights: rawStay.nights,
    nightly_rate: rawStay.nightly_rate ?? rawStay.nightlyRate,
    totalAmount: rawStay.totalAmount ?? rawStay.total_amount,
    total_amount: rawStay.total_amount ?? rawStay.totalAmount,
    amenities: Array.isArray(rawStay.amenities) ? rawStay.amenities : (rawStay.amenities ? [].concat(rawStay.amenities) : []),
    // Ops-only lifecycle (migration 153). Never rendered for customers — the
    // block that shows it is gated on isAdmin.
    id: rawStay.id,
    status: rawStay.status || 'pending',
    supplierConfirmation: rawStay.supplier_confirmation || rawStay.supplierConfirmation || null,
    isRefundable: rawStay.is_refundable ?? rawStay.isRefundable ?? null,
  } : null;
  
  // Travel-only copies of the rows the pricing below reads: every fee, base
  // fare and margin figure is worked out as if the premium weren't there.
  const priceRow = withoutInsurancePremium(flightWithPrice);
  const priceMain = withoutInsurancePremium(mainFlight);

  // Calculate total price including hotel (if Flights + Stays package)
  const flightPrice = parseMonetaryValue(priceRow?.totalPrice || priceRow?.total_amount || priceRow?.total_price);
  
  // Calculate hotel price with fallback: totalAmount OR (nightly_rate * nights)
  // Uses parseMonetaryValue to handle formatted currency strings ($1,299.00)
  let hotelPrice = 0;
  if (stayData) {
    hotelPrice = parseMonetaryValue(stayData.totalAmount ?? stayData.total_amount);
    // Fallback: calculate from nightly rate if totalAmount is missing
    if (hotelPrice === 0 && (stayData.nightly_rate ?? stayData.nightlyRate) && stayData.nights) {
      hotelPrice = parseMonetaryValue(stayData.nightly_rate ?? stayData.nightlyRate) * parseInt(stayData.nights, 10);
    }
  }
  
  const totalPrice = flightPrice + hotelPrice;

  // Resolve payment method and pricing from flight_data (matches Carts/EditCart)
  const pricingSnapshot = flightData.pricing_snapshot;
  const paymentMethod = flightData._payment_method || flightData.payment_method || priceMain.payment_method || flightData.booking_option || pricingSnapshot?.payment_method || 'installments';
  const isMilesOnly = paymentMethod === 'miles';
  const isMilesInstallments = paymentMethod === 'miles_installments';
  const isReserve = paymentMethod === 'reserve';
  const isDeferaZero = paymentMethod === 'defera_zero';
  // Only bookings created as Defera Capital (persisted defera_capital). Do not use the global
  // installments checkout setting: Slice Pay trips stay payment_method=installments and would
  // otherwise hit defera-capital/schedule and 404 ("trip not found").
  const isDeferaCapital = paymentMethod === 'defera_capital';
  const pricingEmbedded = !!flightData._change_flight_pricing_embedded;
  const snapshotCashPortion = parseFloat(pricingSnapshot?.cash_portion ?? 0);
  const snapshotMilesUsed = parseFloat(pricingSnapshot?.miles_used ?? 0);
  const milesToDeplete = flightData._miles_to_deplete ?? flightData.miles_to_deplete ?? 0;
  const creditsTotal = parseFloat(flightData.credits_total || 0);
  // Discount code (migration 141). Read off the booking's own stamp, so the
  // trip shows the customer the same "−$20.00 (JAY20)" line they agreed to at
  // checkout instead of a total that silently doesn't add up.
  const bookingDiscount = readCartDiscount(flightData);
  const discountTotal = bookingDiscount ? bookingDiscount.cents / 100 : 0;
  const hasMilesSnapshot = isMilesInstallments && pricingSnapshot && (snapshotMilesUsed > 0 || snapshotCashPortion >= 0);

  // Use pricing_snapshot for miles_installments; fallback to calculateReviewPagePricing when we have miles but no snapshot
  let serviceFee, grandTotal, subtotalBeforeHold, holdFee, grandTotalWithHold, baseFareDisplay, displayCashPortion, displayMilesUsed;
  if (hasMilesSnapshot) {
    baseFareDisplay = parseFloat(pricingSnapshot.original_base_fare || totalPrice);
    displayCashPortion = snapshotCashPortion;
    displayMilesUsed = snapshotMilesUsed;
    serviceFee = parseFloat(pricingSnapshot.service_fee || 0);
    holdFee = parseFloat(pricingSnapshot.hold_fee || 0);
    grandTotalWithHold = parseFloat(pricingSnapshot.grand_total || 0);
    subtotalBeforeHold = snapshotCashPortion + serviceFee;
    grandTotal = grandTotalWithHold - holdFee;
  } else if (isMilesInstallments && milesToDeplete > 0) {
    // Fallback: compute from base + miles when snapshot missing (e.g. old push)
    const baseAfterCredits = Math.max(0, totalPrice - creditsTotal);
    const pricing = calculateReviewPagePricing(
      baseAfterCredits,
      0,
      'miles_installments',
      milesToDeplete,
      0,
      hotelPrice > 0,
      installmentsCheckoutProvider
    );
    baseFareDisplay = totalPrice;
    displayCashPortion = pricing.cashPortion;
    displayMilesUsed = pricing.milesUsed;
    serviceFee = pricing.serviceFee;
    holdFee = pricing.holdFee;
    subtotalBeforeHold = pricing.subtotalBeforeHold;
    grandTotalWithHold = pricing.totalExcludingSeats;
    grandTotal = grandTotalWithHold - holdFee;
  } else if (isMilesOnly) {
    // A trip paid entirely with Defera Miles (2026-10-05 on) carries its price
    // on pricing_snapshot (server/miles-only-booking.js): the total its Miles
    // paid, fees included. Older Miles-only trips covered the fare alone.
    const paidWithMiles = pricingSnapshot?.paid_with_miles ? pricingSnapshot : null;
    const fromCents = (v) => (Number(v) || 0) / 100;
    baseFareDisplay = totalPrice;
    displayCashPortion = 0;
    displayMilesUsed = paidWithMiles ? Number(paidWithMiles.miles) || 0 : 0;
    serviceFee = paidWithMiles ? fromCents(paidWithMiles.service_fee_cents) : 0;
    holdFee = paidWithMiles ? fromCents(paidWithMiles.hold_fee_cents) : 0;
    subtotalBeforeHold = paidWithMiles ? fromCents(paidWithMiles.subtotal_before_hold_cents) : totalPrice;
    grandTotal = subtotalBeforeHold;
    grandTotalWithHold = paidWithMiles ? fromCents(paidWithMiles.trip_total_cents) : totalPrice;
  } else if (isReserve) {
    // Reserve: total_amount/grand_total is base + 11% service fee. Do NOT add service fee again.
    grandTotal = hotelPrice > 0 ? totalPrice : parseFloat(priceRow?.grand_total ?? priceMain?.grand_total ?? flightData?.grand_total ?? totalPrice);
    serviceFee = hotelPrice > 0 ? 0 : parseFloat(priceRow?.service_fee ?? priceMain?.service_fee ?? flightData?.service_fee ?? 0);
    if (serviceFee <= 0 && grandTotal > 0) {
      baseFareDisplay = grandTotal / 1.11;
      serviceFee = grandTotal - baseFareDisplay;
    } else {
      baseFareDisplay = grandTotal - serviceFee;
    }
    displayCashPortion = null;
    displayMilesUsed = null;
    holdFee = 0;
    subtotalBeforeHold = grandTotal;
    grandTotalWithHold = grandTotal;
  } else if (isDeferaZero) {
    /**
     * Defera Zero: base + tiered service fee + 15% hold fee (refundable / $0-down path).
     *
     * Self-heal: `flight_data.base_amount` is the canonical Duffel ticket price (set at
     * booking, never modified). If it's set and the booking row's totals have drifted from
     * it, we anchor display on (a) `flight_data.base_amount` as the base and (b) the
     * Stripe-canonical post-fee total (= the smallest `total_amount`/`grand_total`/
     * `grand_total_with_hold` that's >= base; that's the value written at creation and
     * not mutated by AdminEditModal's pre-fix recompute). Service fee uses the tiered
     * rate; hold fee falls out as the residual so the line items always sum to what the
     * customer actually owes Stripe — regardless of which corruption pattern bit them
     * (admin-modal recompute, Carts-push base-as-total, etc.).
     *
     * XFZXIZ (July 2026): the Home.jsx / Search.jsx client-side coercions failed to
     * catch a Defera Zero + Flight+Hotel checkout, so the booking landed as DZ with a
     * real stored hold_fee=$498.70 that Stripe collected as part of the plan total. The
     * flight+hotel arm below trusts the DB unconditionally: no phantom-hold heuristic
     * (which was tuned for change-flight leftovers like 13Q84U — a flight-only shape)
     * and no double-count of `totalPrice = flightPrice + hotelPrice` (where flightPrice
     * itself was already the whole-package total). The flight-only DZ code path is
     * untouched, so 13Q84U / PY2RE8 / FJO7C0 self-heals keep working exactly as before.
     */
    if (hotelPrice > 0) {
      const fh = computeFlightHotelBreakdown({
        booking: priceRow || priceMain || flightData,
        hotelPrice,
        totalPriceFallback: totalPrice,
      });
      holdFee = fh.holdFee;
      serviceFee = fh.serviceFee;
      grandTotalWithHold = fh.grandTotalWithHold;
      grandTotal = fh.grandTotal;
      baseFareDisplay = fh.baseFareDisplay;
      subtotalBeforeHold = fh.subtotalBeforeHold;
      displayCashPortion = null;
      displayMilesUsed = null;
    } else {
    const flightDataBase = parseFloat(flightData?.base_amount ?? 0) || 0;
    grandTotalWithHold = parseFloat(priceRow?.grand_total_with_hold ?? priceMain?.grand_total_with_hold ?? flightData?.grand_total_with_hold ?? priceRow?.grand_total ?? priceMain?.grand_total ?? flightData?.grand_total ?? totalPrice);
    /**
     * Defera Zero invariant: `grand_total === grand_total_with_hold` (DZ has no hold fee — see
     * supabase/functions/change-flight/index.ts line ~478). When the row reflects that invariant,
     * ignore any stored `hold_fee` (often stale from a different payment method or a pre-DZ
     * version of the booking) so the breakdown can't display a phantom hold fee that drags
     * `baseFareDisplay` below the real base. 13Q84U was the canary: stored hold_fee=$48.29 left
     * over from the original Choice Preferred booking, gtwh=205.35=grand_total → must be 0.
     */
    const storedGrandTotal = parseFloat(priceRow?.grand_total ?? priceMain?.grand_total ?? flightData?.grand_total ?? 0) || 0;
    const dzMatchedTotals = storedGrandTotal > 0 && grandTotalWithHold > 0 && Math.abs(storedGrandTotal - grandTotalWithHold) <= 1;
    holdFee = dzMatchedTotals ? 0 : parseFloat(priceRow?.hold_fee ?? priceMain?.hold_fee ?? flightData?.hold_fee ?? 0);
    grandTotal = grandTotalWithHold - holdFee;
    serviceFee = parseFloat(priceRow?.service_fee ?? priceMain?.service_fee ?? flightData?.service_fee ?? 0);
    if (serviceFee <= 0 && grandTotal > 0) {
      baseFareDisplay = grandTotal / 1.11;
      serviceFee = grandTotal - baseFareDisplay;
    } else {
      baseFareDisplay = grandTotal - serviceFee;
    }
    /**
     * Only self-heal when the row is actually corrupted. The admin-modal corruption pattern
     * leaves `total_amount` at the Stripe-canonical value but inflates `grand_total_with_hold`
     * above it; the Carts-push pattern produces `grand_total_with_hold > total_amount` too
     * (because `total_amount` got rewritten to the base). For healthy rows all three columns
     * are equal — including trips with airline taxes/extras where `flight_data.base_amount`
     * (raw fare) is meaningfully less than the ticket cost (FJO7C0: base $792, ticket $1272,
     * stored fees match $1272). Triggering on `gtwh > base+1` would false-positive every such
     * trip and force base=$792 into the residual model, dumping the $480 of taxes into a
     * fake "85% Hold Fee" line.
     */
    // Same alias guard as the Capital branch — see PY2RE8 note in `pickStripeCanonicalTotal`.
    const storedTotalAmount = parseFloat(
      priceRow?.total_amount ?? priceRow?.totalPrice ??
      priceMain?.total_amount ?? priceMain?.totalPrice ??
      flightData?.total_amount ?? 0
    ) || 0;
    /**
     * Skip the self-heal when the stored columns already reconcile to the customer's
     * charged total (`total_amount + service_fee ≈ grand_total_with_hold`, with hold_fee=0
     * for DZ). 13Q84U's `flight_data.total_amount` was stuck at "230" (the SerpAPI search
     * display price) while the row itself was healthy ($185 base + $20.35 fee = $205.35);
     * without this gate the self-heal grabbed the stale 230, computed
     * `residual = canonicalTotal − 230 ≤ 0`, and zeroed out Service Fee on the Cost
     * Breakdown card. See ChangeFlightReview.jsx fix that prevents this divergence on
     * future change-flight requests.
     */
    const dzColumnsReconcile = storedTotalAmount > 0 && serviceFee > 0 && holdFee === 0
      && Math.abs(storedTotalAmount + serviceFee - grandTotalWithHold) <= 1;
    if (flightDataBase > 0 && hotelPrice <= 0 && grandTotalWithHold > storedTotalAmount + 1 && !dzColumnsReconcile) {
      const ticketCost = parseFloat(flightData?.total_amount ?? 0) > 0 ? parseFloat(flightData.total_amount) : flightDataBase;
      const canonicalTotal = pickStripeCanonicalTotal(priceRow, priceMain, flightData, ticketCost, grandTotalWithHold);
      const dzServiceFeeRate = getServiceFeeRate(ticketCost, false);
      const naturalServiceFee = ticketCost * dzServiceFeeRate;
      const residual = Math.max(0, canonicalTotal - ticketCost);
      baseFareDisplay = ticketCost;
      serviceFee = Math.min(naturalServiceFee, residual);
      holdFee = Math.max(0, residual - serviceFee);
      grandTotal = baseFareDisplay + serviceFee;
      grandTotalWithHold = canonicalTotal;
    }
    displayCashPortion = null;
    displayMilesUsed = null;
    subtotalBeforeHold = grandTotal;
    } // end flight-only DZ branch
  } else {
    // Slice Pay / Defera Capital / standard installments. The top-level booking
    // columns store the post-hold grand total under multiple aliases
    // (`total_amount`, `grand_total`, `grand_total_with_hold` are all the same
    // number — see entities.js + supabase/functions/stripe-webhook/defera-capital.ts).
    // That means `totalPrice` here is the GRAND total, not the base fare. We
    // back the base fare out of (grand_total_with_hold - service_fee - hold_fee)
    // so that:
    //   1. "Service Fee (X%)" divides by base fare, not grand total
    //      (R8BBF5 was rendering "Service Fee (6%)" because 62.72 / 973.73 = 6.44%
    //      instead of the real 8% on a $784 base).
    //   2. "Hold Fee (X%)" divides by the pre-hold subtotal, not the post-hold
    //      total (otherwise 15% Capital looks like "12%").
    //   3. The "Base Fare" line renders the actual base fare, not the grand total.
    //   4. Line 971's display-invariant reconciliation yields the same grand total
    //      that's stored in the database.
    const storedServiceFee = parseFloat(priceRow?.service_fee ?? priceMain?.service_fee ?? 0) || 0;
    const storedHoldFee = parseFloat(priceRow?.hold_fee ?? priceMain?.hold_fee ?? 0) || 0;
    const storedGrandWithHold = parseFloat(
      priceRow?.grand_total_with_hold ??
        priceMain?.grand_total_with_hold ??
        priceRow?.grand_total ??
        priceMain?.grand_total ??
        0
    ) || 0;
    const derivedBaseFare = storedGrandWithHold > 0
      ? Math.max(0, storedGrandWithHold - storedServiceFee - storedHoldFee)
      : 0;
    // Brand-new offers being viewed before booking won't have stored fees yet;
    // fall back to treating `totalPrice` as the base fare and computing fees.
    //
    // A stored `service_fee` of 0 is legitimate (early Defera Capital / promo
    // pricing: base fare + hold fee, no service fee). Gating on it alone sent
    // those rows down the not-yet-priced path, where `totalPrice` — already the
    // GRAND total — was treated as the base fare and had 8% service + 15% hold
    // re-added on top. NJM4JY: $589.95 stored ($513 base + $76.95 hold) rendered
    // as a fabricated $732.72 on both the admin Cost Breakdown and the customer's
    // Total Cost card, contradicting the authoritative Payment Schedule. Either
    // real fee is enough to prove the row is priced; only a row with both at zero
    // is genuinely uninitialized.
    const useStored = derivedBaseFare > 0 && (storedServiceFee > 0 || storedHoldFee > 0);

    /**
     * Flight + Hotel bookings on this rail: trust the DB unconditionally. Same
     * reasoning as the DZ branch above — checkout writes clean values
     * (Review.jsx L446-458, cartPricing.js L178-189), and the recompute path
     * that used to run here read `flightPrice = total_amount` (which is the
     * whole-package total on this rail per the note at L1313-1316), then
     * added `hotelPrice` again to form `totalPrice`, then applied the hold
     * rate to that double-counted number. XFZXIZ was the canary: displayed
     * $5,785.98 for a $3,823.34 trip. Flight-only bookings still run through
     * the full useStored / preferFlightDataBase self-heal logic below.
     */
    if (hotelPrice > 0) {
      const fh = computeFlightHotelBreakdown({
        booking: priceRow || priceMain || flightData,
        hotelPrice,
        totalPriceFallback: totalPrice,
      });
      holdFee = fh.holdFee;
      serviceFee = fh.serviceFee;
      grandTotalWithHold = fh.grandTotalWithHold;
      grandTotal = fh.grandTotal;
      baseFareDisplay = fh.baseFareDisplay;
      subtotalBeforeHold = fh.subtotalBeforeHold;
      displayCashPortion = null;
      displayMilesUsed = null;
    } else {
    /**
     * Self-heal for Defera Capital / Slice Pay — only when the row is actually corrupted.
     * Trigger: `grand_total_with_hold > total_amount + 1`. This catches the AdminEditModal
     * pattern (gtwh sequentially inflated above intact total_amount) and the Carts-push
     * pattern (total_amount overwritten to base, gtwh stays at the customer-paid total).
     *
     * NOT triggered for healthy trips with airline taxes/extras (e.g. FJO7C0: raw base
     * $792, ticket cost $1272, stored fees correctly computed off $1272). For those the
     * passthrough branch correctly displays `derivedBaseFare` (the ticket cost) as
     * "Base Fare", which is what customers should see — not the bare Duffel base fare.
     *
     * When self-healing, the displayed base = `flight_data.total_amount` (actual ticket
     * cost incl. taxes/extras) when set, falling back to `flight_data.base_amount`. The
     * canonical Stripe total comes from `pickStripeCanonicalTotal(...)`. Service fee
     * uses the tiered rate; hold fee is the residual so line items always sum to the
     * customer's actual Stripe-charged total.
     */
    const flightDataBase = parseFloat(flightData?.base_amount ?? 0) || 0;
    const flightDataTotal = parseFloat(flightData?.total_amount ?? 0) || 0;
    const ticketCost = flightDataTotal > 0 ? flightDataTotal : flightDataBase;
    // Read the booking's `total_amount` under both the snake_case column name and
    // FlightBooking entity's legacy `totalPrice` alias. If we only read `total_amount`
    // and the entity hasn't mirrored it, this falls through to 0 — which then trips
    // `preferFlightDataBase` and renders the raw Duffel airline price as Base Fare
    // (PY2RE8 was the canary: stored 1538.84, raw 1739, displayed 1739/0%/1739).
    const storedTotalAmount = parseFloat(
      priceRow?.total_amount ?? priceRow?.totalPrice ??
      priceMain?.total_amount ?? priceMain?.totalPrice ?? 0
    ) || 0;
    const preferFlightDataBase = ticketCost > 0
      && hotelPrice <= 0
      && storedGrandWithHold > storedTotalAmount + 1;
    const holdRate = isDeferaCapital ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE;

    if (preferFlightDataBase) {
      const canonicalTotal = pickStripeCanonicalTotal(priceRow, priceMain, flightData, ticketCost, storedGrandWithHold);
      const ratedServiceFeeRate = getServiceFeeRate(ticketCost, isDeferaCapital);
      const naturalServiceFee = ticketCost * ratedServiceFeeRate;
      const residual = Math.max(0, canonicalTotal - ticketCost);
      baseFareDisplay = ticketCost;
      displayCashPortion = null;
      displayMilesUsed = null;
      serviceFee = Math.min(naturalServiceFee, residual);
      subtotalBeforeHold = ticketCost + serviceFee;
      holdFee = Math.max(0, residual - serviceFee);
      grandTotal = subtotalBeforeHold;
      grandTotalWithHold = canonicalTotal;
    } else {
      const baseFare = useStored ? derivedBaseFare : totalPrice;
      const serviceFeeRate = getServiceFeeRate(baseFare, isDeferaCapital);
      baseFareDisplay = baseFare;
      displayCashPortion = null;
      displayMilesUsed = null;
      serviceFee = useStored ? storedServiceFee : baseFare * serviceFeeRate;
      subtotalBeforeHold = baseFare + serviceFee;
      holdFee = useStored && storedHoldFee > 0 ? storedHoldFee : subtotalBeforeHold * holdRate;
      grandTotal = subtotalBeforeHold;
      grandTotalWithHold = useStored && storedGrandWithHold > 0 ? storedGrandWithHold : subtotalBeforeHold + holdFee;
    }
    } // end flight-only Slice Pay / Defera Capital branch
  }

  /** Moving a payment ("Can't make this payment?") adds an Extension Fee to the
   *  stored totals, so a base fare backed out of them carries it. Show it as its
   *  own line instead of inside "Base Fare" (the components still sum to the
   *  same Grand Total). Only when the breakdown actually reconciles to the stored
   *  total, i.e. the fee is inside the base it backed out. */
  const extensionFeeLine = (() => {
    const fee = retainedExtensionFeesCents(flightData) / 100;
    if (!(fee > 0) || !(baseFareDisplay >= fee)) return 0;
    const parts = (Number(baseFareDisplay) || 0) + (Number(serviceFee) || 0) + (Number(holdFee) || 0);
    return Math.abs(parts - (Number(grandTotalWithHold) || 0)) < 1 ? fee : 0;
  })();
  if (extensionFeeLine > 0) {
    baseFareDisplay -= extensionFeeLine;
    subtotalBeforeHold -= extensionFeeLine;
    grandTotal -= extensionFeeLine;
  }

  // Calculate actual service fee percentage from stored value (base fare, not total)
  const feeBaseForPercent = (displayCashPortion ?? 0) > 0 ? displayCashPortion : baseFareDisplay;
  const actualServiceFeePercent = feeBaseForPercent > 0 ? Math.round((serviceFee / feeBaseForPercent) * 100) : 0;
  
  // Calculate actual hold fee percentage from stored values
  const actualHoldFeePercent = subtotalBeforeHold > 0 ? Math.round((holdFee / subtotalBeforeHold) * 100) : 0;

  /**
   * For Flight + Hotel bookings the Cost Breakdown renders a **Flight** row
   * separate from the **Hotel** row. `baseFareDisplay` for that case is the
   * combined pre-fee pre-hold subtotal (flight + hotel + seats), so we back
   * the hotel out of it here to get the flight-alone dollars. The floor at
   * 0 guards against stale data — if `baseFareDisplay` somehow came out
   * below `hotelPrice`, we'd prefer $0 to a negative rendering; the
   * Subtotal + Grand Total lines still reconcile because they read
   * directly from the stored booking columns.
   */
  const flightPortionForDisplay = hotelPrice > 0
    ? Math.max(0, baseFareDisplay - hotelPrice)
    : baseFareDisplay;

  /**
   * When credits are applied, the visible "Base Fare" row should show the GROSS
   * airline fare (`pricing_snapshot.original_base_fare`), so the breakdown reads
   * like:  $1,739 base − $500 credit + $99.12 service + $200.72 hold = $1,538.84.
   *
   * Without this, `baseFareDisplay` is already the credit-adjusted net (it's
   * back-derived from `total_amount − service_fee − hold_fee`), and rendering it
   * as "Base Fare" makes the visible math nonsense — PY2RE8 showed Base Fare
   * $1,239 with a separate −$500 line, which doesn't add up to anything sensible
   * for a customer or admin reading the row.
   *
   * Percentages and the line-item reconciliation (line ~1252) intentionally keep
   * using `baseFareDisplay` (post-credit), because Defera's fees are computed off
   * the credit-adjusted base by design (`calculateReviewPagePricing`'s
   * "credits-first" rule). So Service Fee (8%) stays at 8%.
   */
  const usedSnapshotBaseFare = creditsTotal > 0 && parseFloat(pricingSnapshot?.original_base_fare || 0) > baseFareDisplay;
  const baseFareDisplayGross = usedSnapshotBaseFare
    ? parseFloat(pricingSnapshot.original_base_fare)
    : baseFareDisplay;

  /**
   * Seats picked on the live map at checkout are financed: folded into the held
   * subtotal before both fees (Review.jsx L479-484), so they carry the service fee
   * AND the hold fee. They had no row of their own, and `baseFareDisplay` is
   * back-derived as `grandTotalWithHold − serviceFee − holdFee` — so the seat cost
   * surfaced silently inside "Base Fare". 13DYI1 read Base Fare $1,778.00 against
   * an airline fare of $1,760.00, its $18.00 window seat invisible while the
   * customer financed $22.36 for it ($18 × 1.08 service × 1.15 hold).
   *
   * `checkoutSeatTotalFromFlightData` deliberately ignores `flight_data.seat_purchase`
   * — post-booking seat buys are charged on their own Stripe PI with a flat 5% fee
   * and are NOT in the installment total, so adding them here would invent a charge.
   *
   * Netting rule: subtract seats from the fare row ONLY when that row was
   * back-derived from the grand total (and therefore contains them). When credits
   * are applied we show `pricing_snapshot.original_base_fare`, which is
   * `combinedBasePrice` — flight + hotel, seats already excluded (see
   * Review.flightDataForApi.js L52) — so subtracting there would double-count.
   */
  const checkoutSeatTotal = checkoutSeatTotalFromFlightData(flightData);
  const checkoutSeatCount = checkoutSeatCountFromFlightData(flightData);
  const seatTotalInFareRow = usedSnapshotBaseFare ? 0 : checkoutSeatTotal;
  /** Fare row with financed seats broken back out, floored so a corrupt seat price
   *  can never render a negative fare. */
  const baseFareExcludingSeats = Math.max(0, baseFareDisplayGross - seatTotalInFareRow);
  /** Same treatment for the Flight row on Flight + Hotel bookings, where
   *  `baseFareDisplay` is flight + hotel + seats. */
  const flightPortionExcludingSeats = Math.max(0, flightPortionForDisplay - seatTotalInFareRow);
  /** Render the row whenever seats were picked at checkout — including all-free
   *  picks, which still belong in the breakdown as a $0.00 line so the customer can
   *  see the seat was assigned at no charge rather than wonder if it was billed. */
  const showSeatRow = checkoutSeatCount > 0 && !pricingEmbedded;

  // Upfront charges collected when the customer changed to a costlier flight.
  // Surfaced as a "Flight change" cost-breakdown line visible to BOTH the
  // customer and admin so the price-difference charge is transparent. Driven by
  // flight_data._change_flight_upfront_charges (only populated when the trip's
  // installment plan stays intact and the delta is billed separately).
  const flightChangeUpfrontCharges = Array.isArray(flightData?._change_flight_upfront_charges)
    ? flightData._change_flight_upfront_charges
    : [];
  // A flight change on an insured trip can re-price the travel insurance; an
  // increase rides in the same upfront charge (`insurance_cents`), shown on
  // its own line and kept out of the fare math.
  const flightChangeInsuranceCents = flightChangeUpfrontCharges.reduce(
    (s, c) => s + (Number(c?.insurance_cents) || 0), 0
  );
  const flightChangeCostCents = flightChangeUpfrontCharges.reduce(
    (s, c) => s + (Number(c?.amount_cents) || 0), 0
  ) - flightChangeInsuranceCents;

  // Admin-only: actual cost override + net margin
  const actualCostOverride = parseFloat(flightData?._actual_cost ?? flightData?.actual_cost ?? 0) || 0;
  // Admin-only: cash refunded to the customer (set via Edit Flight Details →
  // Amount Refunded). Shown as its own Cost Breakdown line and subtracted
  // from Net Margin so the trip's realized profit reflects what Defera kept.
  const amountRefundedOverride = parseFloat(flightData?._amount_refunded ?? 0) || 0;
  /** Admin-only: revenue collected from Skip Payment fees ($20 per skip). These are
   *  independent one-off charges — NOT part of the trip's Grand Total and NOT counted
   *  toward the balance (ledger entries carry `amount: 0` with the fee in `fee_cents`),
   *  so they're surfaced as their own revenue line and folded into Net Margin. */
  const skipFeeEntries = Array.isArray(flightData?._one_time_payments_cents)
    ? flightData._one_time_payments_cents.filter((p) => p?.purpose === 'skip_payment_fee')
    : [];
  const skipFeeRevenue = skipFeeEntries.reduce((s, p) => s + (Number(p?.fee_cents) || 0), 0) / 100;
  const purchasedBaggageSummary = getPurchasedBaggageSummary(flightData);
  const purchasedBaggageTotal = purchasedBaggageSummary
    ? purchasedBaggageSummary.totalCents / 100
    : 0;

  const renderPurchasedBaggageCostLine = () => {
    if (!purchasedBaggageSummary || purchasedBaggageSummary.totalCents <= 0) return null;
    const bagCount = purchasedBaggageSummary.totalBags;
    const bagLabel = bagCount === 1 ? '1 bag' : `${bagCount} bags`;
    return (
      <>
        <div className="flex justify-between items-center py-1">
          <span className="text-white/60 flex items-center gap-2 min-w-0">
            <Luggage className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Purchased Baggage ({bagLabel})</span>
          </span>
          <span className="text-white font-normal whitespace-nowrap ml-3">
            ${purchasedBaggageTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <p className="text-white/60 text-[11px] font-normal pb-1">
          Paid separately at checkout — not part of your installment plan
        </p>
      </>
    );
  };
  /**
   * Customer-facing seat line for the "Total Cost" card. Mirrors the purchased-baggage
   * row above but carries the INVERSE annotation: baggage is bought separately and sits
   * outside the plan, whereas seats picked at checkout are financed inside it. Without
   * this row the customer sees no trace of the charge anywhere — 13DYI1 financed an
   * $18.00 seat with nothing on screen naming it.
   */
  const renderCheckoutSeatCostLine = () => {
    if (checkoutSeatCount <= 0 || checkoutSeatTotal <= 0) return null;
    const seatLabel = checkoutSeatCount === 1 ? '1 seat' : `${checkoutSeatCount} seats`;
    return (
      <>
        <div className="flex justify-between items-center py-1">
          <span className="text-white/60 flex items-center gap-2 min-w-0">
            <Armchair className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Seat Selection ({seatLabel})</span>
          </span>
          <span className="text-white font-normal whitespace-nowrap ml-3">
            ${checkoutSeatTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <p className="text-white/60 text-[11px] font-normal pb-1">
          Included in your installment plan
        </p>
      </>
    );
  };

  /** The Travel Insurance card: the Faye plan bought at checkout or added
   *  later. An upfront add leaves the premium out of the totals, so the policy
   *  alone is enough to show it. Its ops strip is admin-only, like the hotel's
   *  room status. Rendered under the Flights card for admins, after the hotel
   *  for everyone else. */
  const insuranceCard = (insurancePremiumCents > 0 || customerInsurance) ? (
    <TripInsuranceCard
      policy={customerInsurance}
      opsPolicy={tripInsurance}
      tripActive={!['cancelled', 'defaulted'].includes(String(tripStatus).toLowerCase())}
      premiumCents={insurancePremiumCents}
      addedAfterBooking={insuranceAddedAfterBooking(mainFlight)}
      bookingId={tripInsurance?.bookingId || mainFlight.id}
      isAdmin={viewerIsAdmin}
      showOps={staffView}
      onPolicyChange={setInsurancePolicyOverride}
    />
  ) : null;

  const money2 = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  /** Faye travel insurance on the Total Cost card. Financed like the seat line
   *  above, but outside Defera's fees: it is charged at Faye's exact price and
   *  spread over the payments after the deposit. A plan added after booking
   *  and paid upfront gets its own line instead, outside the Grand Total. */
  const renderInsuranceCostLine = () => {
    if (insurancePremium <= 0 && insuranceUpfrontPremium <= 0) return null;
    return (
      <>
        {insurancePremium > 0 && (
          <>
            <div className="flex justify-between items-center py-1">
              <span className="text-white/60 flex items-center gap-2 min-w-0">
                <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Travel Insurance ({tripInsurance?.label || 'Faye'})</span>
              </span>
              <span className="text-white font-normal whitespace-nowrap ml-3">
                ${money2(insurancePremium)}
              </span>
            </div>
            <p className="text-white/60 text-[11px] font-normal pb-1">
              Included in your installment plan, with no fees added
            </p>
          </>
        )}
        {insuranceUpfrontPremium > 0 && (
          <>
            <div className="flex justify-between items-center py-1">
              <span className="text-white/60 flex items-center gap-2 min-w-0">
                <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Travel Insurance ({tripInsurance?.label || 'Faye'})</span>
              </span>
              <span className="text-white font-normal whitespace-nowrap ml-3">
                ${money2(insuranceUpfrontPremium)}
              </span>
            </div>
            <p className="text-white/60 text-[11px] font-normal pb-1">
              Paid in full when you added it, separately from your plan
            </p>
          </>
        )}
      </>
    );
  };

  /** Post-booking seat purchase (own Stripe PI, outside the installment plan).
   *  Deliberately NOT part of the plan totals above — see
   *  src/utils/seatPurchaseEconomics.js — but it is real money on both sides. */
  const seatPurchaseMoney = seatPurchaseEconomics(flightData);

  // Net margin = total revenue (base + service fee + hold fee + skip fees) minus what
  // Defera actually paid for the ticket, minus any cash refunded to the customer.
  // Shown when either override is set so a refund-only trip still surfaces its margin.
  //
  // The seat term is revenue MINUS the airline's pass-through cost in one value,
  // so the two can never drift apart. Previously neither side was counted, which
  // only looked harmless because `_actual_cost` is set at ticketing — usually
  // months before a post-booking seat is bought (N2DUA2: ticketed April, seat
  // bought August). The moment an admin refreshed Actual Cost to include a seat,
  // margin would have dropped by the seat price with no offsetting revenue.
  const netMarginAmount = (actualCostOverride > 0 || amountRefundedOverride > 0)
    ? (grandTotalWithHold + skipFeeRevenue + seatPurchaseMarginContribution(flightData)
       - actualCostOverride - amountRefundedOverride)
    : null;

  // Pre-booking fare-change P&L (admin-only). When the customer or admin changes
  // the flight before Defera has booked it with the airline (no confirmation # on
  // file), the difference between what the customer is locked in to pay and the
  // new Base Fare lands on Defera's books the moment the trip is actually
  // booked. Surfaced separately from Net Margin (which keys off the post-booking
  // admin-set Actual Cost) so admins can see the realized fare delta on
  // unconfirmed trips immediately.
  //
  // Customer base = booking's preserved `total_amount` plus the base portion of
  // any upfront upgrade charges (`amount_cents / 1.11` — the 11% loading is the
  // service-fee share already counted elsewhere). For cash trips `total_amount`
  // tracks the new base, so the delta naturally comes out to ~$0. For
  // installment downgrades `total_amount` stays at the higher original, so the
  // delta surfaces as profit.
  const fareChangeHistory = Array.isArray(trip?.[0]?.change_flight_history)
    ? trip[0].change_flight_history
    : (Array.isArray(trip?.[0]?.changeFlightHistory) ? trip[0].changeFlightHistory : []);
  const fareChangeConfirmation = String(
    trip?.[0]?.confirmation_number ||
    trip?.[0]?.confirmationNumber ||
    flightData?.confirmation_number ||
    flightData?.airline_confirmation || ''
  ).trim();
  let fareChangeNetProfit = null;
  let fareChangeCustomerBase = null;
  let fareChangeAnticipatedCost = null;
  if (
    staffView
    && actualCostOverride <= 0
    && fareChangeConfirmation.length === 0
    && fareChangeHistory.length > 0
  ) {
    const latestEntry = fareChangeHistory[fareChangeHistory.length - 1];
    const newBaseCents = Number(latestEntry?.new_base_fare_cents) || 0;
    const anticipated = newBaseCents > 0
      ? newBaseCents / 100
      : (parseFloat(flightData?.base_amount ?? 0) || 0);
    if (anticipated > 0) {
      const preservedBase = parseFloat(
        priceRow?.total_amount
        ?? priceRow?.totalPrice
        ?? priceMain?.total_amount
        ?? priceMain?.totalPrice
        ?? 0
      ) || 0;
      const upfrontBaseDollars = flightChangeCostCents / 1.11 / 100;
      const customerBase = preservedBase + upfrontBaseDollars;
      if (customerBase > 0) {
        fareChangeCustomerBase = customerBase;
        fareChangeAnticipatedCost = anticipated;
        fareChangeNetProfit = customerBase - anticipated;
      }
    }
  }

  // Display invariant: the Grand Total must equal the sum of the visible line items. We've seen
  // cases where a booking's stored `grand_total_with_hold` was populated incorrectly (e.g. set to
  // the base fare during a manual Carts push), which causes the cost breakdown to display a
  // Grand Total that contradicts the rows above it. Reconcile here so the user-visible number
  // always matches what's shown. Miles-only bookings have no fees and skip this fallback.
  if (!isMilesOnly) {
    const cashBaseForTotal = (displayCashPortion != null && displayCashPortion > 0)
      ? displayCashPortion
      : baseFareDisplay;
    grandTotalWithHold = (cashBaseForTotal || 0) + (serviceFee || 0) + (holdFee || 0);
  }
  /** What the customer pays in all: the trip plus its Faye premium. Everything
   *  above stays travel-only on purpose, so Net Margin and the fee percentages
   *  never count money that belongs to Faye. */
  const planGrandTotal = grandTotalWithHold + insurancePremium;

  // Extract individual flight records from booking objects
  // A booking may have multiple slices (e.g., roundtrip has outbound + return slices)
  // CRITICAL: Do NOT spread {...booking} as it includes first-slice summary fields that 
  // cause all legs to inherit the same marketing_airline, flight numbers, etc.
  const extractedFlights = [];
  trip.forEach(booking => {
    const slices = booking.flightData?.slices || [];
    console.log('🔍 [FlightDetailsModal] Extracting slices from booking:', {
      bookingId: booking.id,
      slicesCount: slices.length,
      hasFlightData: !!booking.flightData
    });
    if (slices.length > 0) {
      // Create flight objects from each slice - only include essential booking props, NOT summary fields
      slices.forEach((slice, sliceIdx) => {
        if (slice.segments && slice.segments.length > 0) {
          const firstSegment = slice.segments[0];
          const lastSegment = slice.segments[slice.segments.length - 1];
          console.log(`🔍 [FlightDetailsModal] Slice ${sliceIdx} data:`, {
            origin: firstSegment?.origin?.iata_code,
            destination: lastSegment?.destination?.iata_code,
            departingAt: firstSegment?.departing_at,
            arrivingAt: lastSegment?.arriving_at,
            flightNumber: firstSegment?.marketing_carrier_flight_number,
            aircraft: firstSegment?.aircraft?.name
          });
          // Only include required booking properties - NOT first-slice summary fields
          extractedFlights.push({
            // Booking metadata (required for price display, comments, etc.)
            id: booking.id,
            bookingId: booking.id,
            userId: booking.userId,
            offerId: booking.offerId,
            status: booking.status,
            paymentStatus: booking.paymentStatus,
            bookingReference: booking.bookingReference,
            tripReference: booking.tripReference,
            // Price data
            totalPrice: booking.totalPrice,
            service_fee: booking.service_fee,
            grand_total: booking.grand_total,
            hold_fee: booking.hold_fee,
            grand_total_with_hold: booking.grand_total_with_hold,
            // Full flight data object (for accessing other slices, seat_assignments, etc.)
            flightData: booking.flightData,
            flight_data: booking.flight_data,
            // Authoritative cabin class from the bookings table row (used for cabin display)
            cabinClass: booking.cabinClass || booking.cabin_class,
            // Slice-specific fields derived from THIS slice's segments
            departure_city: firstSegment?.origin?.iata_code || firstSegment?.origin?.city_name,
            departure_time: firstSegment?.departing_at,
            departure_date: firstSegment?.departing_at?.split('T')[0] || firstSegment?.departing_at?.split(' ')[0],
            arrival_city: lastSegment?.destination?.iata_code || lastSegment?.destination?.city_name,
            arrival_time: lastSegment?.arriving_at,
            arrival_date: lastSegment?.arriving_at?.split('T')[0] || lastSegment?.arriving_at?.split(' ')[0],
            marketing_airline: firstSegment?.marketing_carrier?.name,
            // THIS slice's data for FlightOfferCard
            sliceData: slice,
            // Which slice of the booking this is (live flight status per leg)
            sliceIndex: sliceIdx,
          });
        }
      });
    } else {
      // Fallback: use booking's extracted fields (already populated by FlightBooking entity)
      console.log('🔍 [FlightDetailsModal] No slices, using booking fields:', {
        departure_city: booking.departure_city,
        arrival_city: booking.arrival_city,
        hasSliceData: !!booking.sliceData
      });
      extractedFlights.push(booking);
    }
  });
  console.log('🔍 [FlightDetailsModal] Total extracted flights:', extractedFlights.length);

  const sortedFlights = extractedFlights.sort((a, b) => {
    const dateTimeA = new Date(`${a.departure_date}T${a.departure_time || '00:00'}`);
    const dateTimeB = new Date(`${b.departure_date}T${b.departure_time || '00:00'}`);
    return dateTimeA - dateTimeB;
  });

  const flightLegs = groupFlightsIntoLegs(sortedFlights);
  
  // Determine trip type by counting slices across all bookings
  const totalSlices = trip.reduce((count, booking) => {
    return count + (booking.flightData?.slices?.length || 0);
  }, 0);
  const tripType = totalSlices > 1 ? 'Roundtrip' : 'One-way';

  const getCleanAirlineName = (flight) => {
    const rawName = flight.marketing_airline || flight.airline || 'Unknown';
    const majorAirlines = [
      'Delta', 'American', 'United', 'Southwest', 'JetBlue', 'Alaska',
      'Spirit', 'Frontier', 'Hawaiian', 'Allegiant', 'Sun Country',
      'Virgin Atlantic', 'Virgin America', 'British Airways', 'Lufthansa',
      'Air France', 'KLM', 'Emirates', 'Qatar Airways', 'Singapore Airlines'
    ];

    for (const airline of majorAirlines) {
      if (rawName.toLowerCase().includes(airline.toLowerCase())) {
        return airline;
      }
    }

    return rawName.split(' ')[0] || 'Unknown';
  };

  // Get airline logo from API data. Name-first via resolveCarrierLogo so an
  // admin airline rename re-brands the logo too (stored logo URLs keep
  // pointing at the pre-rename carrier).
  const getAirlineLogoFromAPI = (flight) => {
    const flightData = flight.flightData || flight.flight_data || {};
    const slices = flightData.slices || [];
    const firstSlice = slices[0];
    if (firstSlice && firstSlice.segments && firstSlice.segments.length > 0) {
      return resolveCarrierLogo(firstSlice.segments[0].marketing_carrier);
    }
    return null;
  };

  // Get route for header (Origin → Destination) - always use first slice for outbound destination
  const getRouteHeader = () => {
    const flightData = mainFlight.flightData || mainFlight.flight_data || {};
    
    // PRIORITY 1: Check for custom title stored inside flight_data (admin override)
    if (flightData.custom_title) {
      return flightData.custom_title;
    }
    
    // PRIORITY 2: Auto-generate from flight data
    const slices = flightData.slices || [];
    
    if (slices.length > 0) {
      const firstSlice = slices[0];
      
      const origin = firstSlice.segments?.[0]?.origin?.iata_code || mainFlight.departure_city;
      const destination = firstSlice.segments?.[firstSlice.segments.length - 1]?.destination?.iata_code || mainFlight.arrival_city;
      
      return `${getCityName(origin)} → ${getCityName(destination)}`;
    }
    
    // Fallback
    return `${getCityName(mainFlight.departure_city)} → ${getCityName(mainFlight.arrival_city)}`;
  };

  const marketingAirline = getCleanAirlineName(mainFlight);
  const airlineLogo = getAirlineLogoFromAPI(mainFlight);
  const routeHeader = getRouteHeader();

  const formatTime12Hour = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-brand-500/10 text-brand-400 border-brand-500/20',
      confirmed: 'bg-green-500/10 text-green-400 border-green-500/20',
      completed: 'bg-sky-400/10 text-sky-300 border-sky-400/20',
      cancelled: 'bg-red-500/10 text-red-400 border-red-500/20',
      defaulted: 'bg-orange-600/20 text-orange-400 border-orange-600/30',
      // NMI installment cron's terminal-decline state (migration 068/073):
      // recoverable via restart-plan, unlike defaulted. Amber, not orange —
      // it's a warning state, not a terminal one.
      delinquent: 'bg-amber-500/10 text-amber-300 border-amber-500/25'
    };
    return (
      <Badge className={`capitalize rounded-full text-xs font-normal px-3 py-0.5 ${styles[status] || styles.pending}`}>
        {status}
      </Badge>
    );
  };

  const renderTimestamp = (dateString) => {
    if (!dateString) return null;
    try {
      let date;
      
      // First, try to parse as UTC if it doesn't have timezone info
      if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
        // Add Z to indicate UTC if not present
        date = new Date(dateString + 'Z');
      } else {
        // Parse normally if timezone info is present
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        return 'a moment ago';
      }
      
      // If date is still in the future, force it to be interpreted as UTC
      const now = new Date();
      if (date > now) {
        // Try parsing as UTC explicitly
        const utcDate = new Date(dateString.replace('T', ' ').replace(/\.\d{3}/, '') + ' UTC');
        if (!isNaN(utcDate.getTime()) && utcDate <= now) {
          date = utcDate;
        } else {
          // If still in future, just use current time minus 1 minute
          date = new Date(now.getTime() - 60000);
        }
      }
      
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'a moment ago';
    }
  };

  const buildTripRouteLabel = () => {
    const mf = trip?.[0];
    if (!mf) return 'your flight';
    const flightData = mf.flightData || mf.flight_data || {};
    const slices = flightData.slices || [];
    if (slices.length > 0) {
      const firstSlice = slices[0];
      const origin = firstSlice.segments?.[0]?.origin?.iata_code || mf.departure_city;
      const destination = firstSlice.segments?.[firstSlice.segments.length - 1]?.destination?.iata_code || mf.arrival_city;
      return `${getCityName(origin)} → ${getCityName(destination)}`;
    }
    return 'your flight';
  };

  const openChangeFlightSms = () => {
    const userName = currentUser?.firstName && currentUser?.lastName
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : currentUser?.firstName || currentUser?.email || 'Customer';
    const route = buildTripRouteLabel();
    const bookingRef = trip[0]?.booking_reference || trip[0]?.bookingReference || trip[0]?.trip_reference || trip[0]?.tripReference || '';
    const bookingRefText = bookingRef ? `, booking reference: ${bookingRef}` : '';
    navigate(buildTommyUrl({
      message: `I'd like to make changes to my upcoming flight ${route}${bookingRefText}.`,
      bookingReference: bookingRef,
    }));
    setShowRequestChangeModal(false);
  };

  const handleCancelScenario = (scenario) => {
    const userName = currentUser?.firstName && currentUser?.lastName 
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : currentUser?.firstName || currentUser?.email || 'Customer';
    
    const route = buildTripRouteLabel() === 'your flight' ? 'my trip' : buildTripRouteLabel();
    
    const bookingRef = trip[0]?.booking_reference || trip[0]?.bookingReference || trip[0]?.trip_reference || trip[0]?.tripReference || '';
    const bookingRefText = bookingRef ? `, booking reference: ${bookingRef}` : '';
    
    let message = '';
    if (scenario === 'refund') {
      message = `I'd like to cancel my Defera Zero trip ${route}${bookingRefText} and receive the 75% refund.`;
    } else if (scenario === 'payoff') {
      message = `I'd like to cancel my trip ${route}${bookingRefText}. I understand I need to pay off my installment plan before receiving the flight credit.`;
    } else if (scenario === 'default') {
      message = `I'd like to cancel my trip ${route}${bookingRefText} and default on my installment plan. I understand this will jeopardize my standing with ${ic.defaultStandingJeopardy} and prohibit me from booking future trips.`;
    } else {
      message = `I'd like to cancel my trip ${route}${bookingRefText}.`;
    }
    
    navigate(buildTommyUrl({ message, bookingReference: bookingRef }));
    setShowCancelModal(false);
  };

  const handleInstantCancelDeferaZero = async () => {
    if (!bookingId) return;
    setIsCancellingDeferaZero(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Please sign in to continue');
        return;
      }
      const fdCancel = mainFlight?.flightData || mainFlight?.flight_data || {};
      const pmCancel = fdCancel._payment_method || fdCancel.payment_method;
      /** Cancel-trip endpoint is processor-aware: NMI bookings (deposit charged via NMI
       *  Customer Vault) must hit the NMI cancel endpoint so the void/refund is routed
       *  through `transact.php` instead of Stripe. The per-booking `_payment_processor`
       *  snapshot decides — NOT the live `defera_capital_processor` toggle — so existing
       *  Stripe bookings keep talking to Stripe even after the toggle flips. */
      const isNmiCancel = pmCancel === 'defera_capital' && fdCancel._payment_processor === 'nmi';
      const cancelTripUrl = pmCancel === 'defera_capital'
        ? (isNmiCancel ? deferaCapitalNmiUrl('cancel-trip') : deferaCapitalUrl('cancel-trip'))
        : deferaZeroUrl('cancel-trip');
      const res = await fetch(cancelTripUrl, {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({ booking_id: bookingId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.error || 'Failed to cancel trip');
        return;
      }
      toast.success('Trip cancelled. No charges will be made.');
      setShowCancelModal(false);
      onUpdate?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to cancel trip');
    } finally {
      setIsCancellingDeferaZero(false);
    }
  };

  // Unbooked (no airline confirmation): cancel + receive 100% of amount paid
  // back as Defera Credit. Routes to the same processor-aware cancel endpoint,
  // which detects the missing confirmation server-side and runs the credit path.

  /**
   * Defera Zero's 75% cash refund — preview, confirm, execute.
   *
   * The figures are NEVER computed here. The edge function returns the exact
   * plan (which charges, how much cash, how much comes back as credit) and the
   * confirm step replays it. Deriving amounts client-side would let the screen
   * and the payout disagree, and the customer is agreeing to the screen.
   */
  const openZeroCashRefund = async () => {
    if (!bookingId) return;
    setIsLoadingZeroRefund(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { toast.error('Please sign in to continue'); return; }
      const res = await fetch(getApiUrl('defera-zero/cancel-refund'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({ booking_id: bookingId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) { toast.error(data?.error || 'Could not load your refund details'); return; }
      // The server routes this one to the team (fare refund rules, a hotel we
      // reserved, a Faye policy, a card dispute): show its message and let
      // the customer send it, instead of figures that won't run automatically.
      if (data.needs_team_review) {
        setZeroRefundPreview(data);
        return;
      }
      if (!data.eligible) {
        toast.error(data.reason === 'inside_24h_cutoff'
          ? 'The 75% refund closes 24 hours before departure.'
          : 'This trip isn\u2019t eligible for the 75% refund.');
        return;
      }
      // A trip paid entirely with Defera Credit has no cash to give back, so
      // the cash option would return $0 and be identical to the credit one.
      // Explain that rather than opening a confirmation for nothing.
      // (Real case: PPAGDA — $247.08, all credit-funded.)
      if (!data.has_cash_to_refund) {
        toast.info('This trip was paid entirely with Defera Credit, so there\u2019s no card payment to refund \u2014 cancelling returns all of it as Defera Credit.');
        return;
      }
      setZeroRefundPreview(data);
    } catch {
      toast.error('Could not load your refund details');
    } finally {
      setIsLoadingZeroRefund(false);
    }
  };

  const executeZeroCashRefund = async () => {
    if (!bookingId || isExecutingZeroRefund) return;
    setIsExecutingZeroRefund(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { toast.error('Please sign in to continue'); return; }
      const res = await fetch(getApiUrl('defera-zero/cancel-refund'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({ booking_id: bookingId, confirm: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) { toast.error(data?.error || 'Refund could not be completed'); return; }
      // Sent to the team: nothing was cancelled or refunded yet.
      if (data.routed_to_team) {
        toast.success(data.message || 'Sent to our team. They\u2019ll email you within 24 hours.');
        setZeroRefundPreview(null);
        setShowCancelModal(false);
        onUpdate?.();
        return;
      }
      // A partial failure must not be reported as a clean success — the team
      // is emailed either way, but the customer needs the honest version.
      if (data.partial_failure) {
        toast.warning('Trip cancelled. Part of your refund needs a moment — our team has been notified and will finish it today.');
      } else {
        toast.success('Trip cancelled. Your refund is on its way back to your card.');
      }
      setZeroRefundPreview(null);
      setShowCancelModal(false);
      onUpdate?.();
      onClose?.();
    } catch {
      toast.error('Refund could not be completed');
    } finally {
      setIsExecutingZeroRefund(false);
    }
  };

  const handleCancelForCredit = async () => {
    if (!bookingId) return;
    setIsCancellingDeferaZero(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Please sign in to continue');
        return;
      }
      const fdCancel = mainFlight?.flightData || mainFlight?.flight_data || {};
      const pmCancel = fdCancel._payment_method || fdCancel.payment_method;
      const isNmiCancel = pmCancel === 'defera_capital' && fdCancel._payment_processor === 'nmi';
      const cancelTripUrl = pmCancel === 'defera_capital'
        ? (isNmiCancel ? deferaCapitalNmiUrl('cancel-trip') : deferaCapitalUrl('cancel-trip'))
        : deferaZeroUrl('cancel-trip');
      const res = await fetch(cancelTripUrl, {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({ booking_id: bookingId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (data && data.error)) {
        toast.error(data?.error || 'Failed to cancel trip');
        return;
      }
      // Total in the success toast = cash refund (`credit_cents`, the Stripe/
      // NMI cash leg) + restored checkout credit (`checkout_credit_restored_cents`,
      // returned by the new restore_defera_credit_on_cancel RPC). The modal
      // preview also adds these two — see the schedule-fetch effect above —
      // so the toast and the preview agree. Without summing, a Flavio-shape
      // trip (deposit fully credit-covered) would have toasted "$0.00 added"
      // even when ~$48 of credit was just restored to the balance.
      const cashCents = Number(data?.credit_cents) || 0;
      const restoredCents = Number(data?.checkout_credit_restored_cents) || 0;
      const totalCents = Math.max(0, cashCents) + Math.max(0, restoredCents);
      // The travel insurance paid so far goes back to the card, not into credit.
      const insuranceCents = PREMIUM_REFUND_METHOD === 'card' ? Math.max(0, Number(data?.insurance_refund_cents) || 0) : 0;
      // Booked under 24 hours ago: the deposit went back to the card (Stripe
      // rails send `deposit_returned_cents`, the NMI rail `deposit_return`).
      const depositCents = Math.max(0, Number(data?.deposit_returned_cents ?? data?.deposit_return?.amount_cents) || 0);
      const money = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const insuranceNote = insuranceCents > 0 ? ` The ${money(insuranceCents)} of travel insurance goes back to your card.` : '';
      const depositNote = depositCents > 0 ? ` Your ${money(depositCents)} deposit is going back to your card.` : '';
      if (totalCents > 0) {
        toast.success(`Trip cancelled.${depositNote} ${money(totalCents)} added to your Defera Credit.${insuranceNote}`);
      } else if (depositNote || insuranceNote) {
        toast.success(`Trip cancelled.${depositNote}${insuranceNote}`);
      } else {
        toast.success('Trip cancelled. No charges will be made.');
      }
      setShowCancelModal(false);
      onUpdate?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to cancel trip');
    } finally {
      setIsCancellingDeferaZero(false);
    }
  };

  // Defaulted + unbooked: convert 100% of cash paid into Defera Credit. The
  // edge function re-verifies every gate server-side (owner, status=defaulted,
  // no airline confirmation, collections not yet submitted) and shares the
  // cancel-for-credit idempotency key, so a booking can only ever credit once.
  const handleDefaultCreditOptIn = async () => {
    if (!bookingId) return;
    if (!defaultCreditArmed) {
      setDefaultCreditArmed(true);
      return;
    }
    setIsConvertingDefaultCredit(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Please sign in to continue');
        return;
      }
      const res = await fetch(getApiUrl('defera-credit/default-opt-in'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({ booking_id: bookingId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) {
        toast.error(data?.error || 'Failed to convert trip to Defera Credit');
        return;
      }
      // Same toast math as handleCancelForCredit: cash leg + restored
      // checkout-credit leg = the full amount landing on the balance.
      const cashCents = Number(data?.credit_cents) || 0;
      const restoredCents = Number(data?.checkout_credit_restored_cents) || 0;
      const totalCents = Math.max(0, cashCents) + Math.max(0, restoredCents);
      // As on a cancel: the travel insurance paid goes back to the card.
      const insuranceCents = PREMIUM_REFUND_METHOD === 'card' ? Math.max(0, Number(data?.insurance_refund_cents) || 0) : 0;
      const fmt = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const insuranceNote = insuranceCents > 0 ? ` The ${fmt(insuranceCents)} of travel insurance goes back to your card.` : '';
      if (totalCents > 0) {
        toast.success(`${fmt(totalCents)} added to your Defera Credit.${insuranceNote}`);
      } else if (insuranceNote) {
        toast.success(`Trip converted.${insuranceNote}`);
      } else {
        toast.success('Trip converted. No payments were on file to credit.');
      }
      onUpdate?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to convert trip to Defera Credit');
    } finally {
      setIsConvertingDefaultCredit(false);
      setDefaultCreditArmed(false);
    }
  };

  // Admin-only: reinstate a defaulted trip. The edge function first charges
  // the upfront Reinstatement Fee to the customer's card on file (aborting
  // with nothing changed if it declines — the customer accepted the fee in
  // chat before this was queued), then regenerates the payment schedule for
  // the remaining balance, re-hooks the processor (fresh Stripe sub / fresh
  // NMI installment rows), clears the default episode, and flips the booking
  // back to Confirmed. Beyond the fee, nothing else is charged on this click
  // — the balance spreads across the new cycles.
  const handleReinstateDefaultedTrip = async () => {
    if (!bookingId) return;
    if (!reinstateArmed) {
      setReinstateArmed(true);
      return;
    }
    setIsReinstating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Please sign in to continue');
        return;
      }
      const res = await fetch(getApiUrl('admin/reinstate-defaulted-trip'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
        body: JSON.stringify({ booking_id: bookingId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) {
        toast.error(data?.error || 'Failed to reinstate trip');
        return;
      }
      // The fee suffix distinguishes "charged now" from a converged retry
      // whose fee was already collected on the earlier run.
      const feeNote = data.reinstatement_fee_charged
        ? ` ${REINSTATEMENT_FEE_LABEL} reinstatement fee charged.`
        : (data.reinstatement_fee_cents ? ' Reinstatement fee was already collected.' : '');
      if (data.paid_in_full) {
        toast.success(`Trip reinstated — balance already paid in full.${feeNote}`);
      } else if (data.new_schedule) {
        const s = data.new_schedule;
        // first_payment_date is a UTC-midnight instant; parse only the calendar
        // date so admins west of UTC don't see the prior day (the cron/Stripe
        // bill on the UTC date).
        const firstChargeLabel = format(parseISO(String(s.first_payment_date).slice(0, 10)), 'MMM d, yyyy');
        toast.success(
          `Trip reinstated — ${s.num_payments} × $${fmtCurrency(s.per_payment_cents)} ${s.cadence}, first charge ${firstChargeLabel}.${feeNote}`,
        );
      } else {
        toast.success(`Trip reinstated.${feeNote}`);
      }
      (Array.isArray(data.warnings) ? data.warnings : []).forEach((w) => toast.warning(w, { duration: 10000 }));
      onUpdate?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to reinstate trip');
    } finally {
      setIsReinstating(false);
      setReinstateArmed(false);
    }
  };

  // Live seat-map purchase finished: seats are now registered on the booking,
  // so refresh the trip — eligibility flips off and the seats render in
  // Flight Details. From here on, "Purchase Seats" falls back to concierge.
  // NOTE: plain function, not useCallback — this sits below the component's
  // early `return null`, where adding a hook breaks React's hook order the
  // moment the modal opens.
  const handleSeatPurchaseComplete = () => {
    setShowSeatPurchaseFlow(false);
    toast.success('Seats confirmed — they now appear in your Flight Details');
    onUpdate?.();
  };

  const handleBaggagePurchaseComplete = () => {
    setShowBaggagePurchaseFlow(false);
    toast.success('Baggage purchase confirmed — our team will add it to your reservation');
    onUpdate?.();
  };

  const handlePurchaseSeats = () => {
    const userName = currentUser?.firstName && currentUser?.lastName 
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : currentUser?.firstName || currentUser?.email || 'Customer';
    
    const flightData = mainFlight?.flightData || mainFlight?.flight_data || {};
    const slices = flightData.slices || [];
    
    let route = '';
    if (slices.length > 0) {
      const firstSlice = slices[0];
      const origin = firstSlice.segments?.[0]?.origin?.iata_code || mainFlight.departure_city;
      const destination = firstSlice.segments?.[firstSlice.segments.length - 1]?.destination?.iata_code || mainFlight.arrival_city;
      route = `${getCityName(origin)} → ${getCityName(destination)}`;
    } else {
      route = 'my trip';
    }
    
    const bookingRef = trip[0]?.booking_reference || trip[0]?.bookingReference || trip[0]?.trip_reference || trip[0]?.tripReference || '';
    const bookingRefText = bookingRef ? `, booking reference: ${bookingRef}` : '';
    
    navigate(buildTommyUrl({
      message: `I'd like to choose my seats for my trip ${route}${bookingRefText}.`,
      bookingReference: bookingRef,
    }));
    setShowPurchaseSeatsModal(false);
  };

  const handlePurchaseBaggage = () => {
    const userName = currentUser?.firstName && currentUser?.lastName 
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : currentUser?.firstName || currentUser?.email || 'Customer';
    
    const flightData = mainFlight?.flightData || mainFlight?.flight_data || {};
    const slices = flightData.slices || [];
    
    let route = '';
    if (slices.length > 0) {
      const firstSlice = slices[0];
      const origin = firstSlice.segments?.[0]?.origin?.iata_code || mainFlight.departure_city;
      const destination = firstSlice.segments?.[firstSlice.segments.length - 1]?.destination?.iata_code || mainFlight.arrival_city;
      route = `${getCityName(origin)} → ${getCityName(destination)}`;
    } else {
      route = 'my trip';
    }
    
    const bookingRef = trip[0]?.booking_reference || trip[0]?.bookingReference || trip[0]?.trip_reference || trip[0]?.tripReference || '';
    const bookingRefText = bookingRef ? `, booking reference: ${bookingRef}` : '';
    
    const message = `I'd like to add baggage to my trip ${route}${bookingRefText}.`;
    
    navigate(buildTommyUrl({ message, bookingReference: bookingRef }));
  };

  const handleCabinUpgrade = () => {
    const userName = currentUser?.firstName && currentUser?.lastName 
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : currentUser?.firstName || currentUser?.email || 'Customer';
    
    const flightData = mainFlight?.flightData || mainFlight?.flight_data || {};
    const slices = flightData.slices || [];
    let route = '';
    if (slices.length > 0) {
      const firstSlice = slices[0];
      const origin = firstSlice.segments?.[0]?.origin?.iata_code || mainFlight.departure_city;
      const destination = firstSlice.segments?.[firstSlice.segments.length - 1]?.destination?.iata_code || mainFlight.arrival_city;
      route = `${getCityName(origin)} → ${getCityName(destination)}`;
    } else {
      route = 'my trip';
    }
    const bookingRef = trip[0]?.booking_reference || trip[0]?.bookingReference || trip[0]?.trip_reference || trip[0]?.tripReference || '';
    const bookingRefText = bookingRef ? `, booking reference: ${bookingRef}` : '';
    
    const message = `I'd like to upgrade my cabin class for my trip ${route}${bookingRefText}.`;
    
    navigate(buildTommyUrl({ message, bookingReference: bookingRef }));
    setShowCabinUpgradeModal(false);
  };

  // Open SMS compose for Defera Miles add-ons (seats, baggage, cabin) - used when customer has sufficient miles
  const openMilesAddOnSmsCompose = (addOnType) => {
    const userName = currentUser?.firstName && currentUser?.lastName 
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : currentUser?.firstName || currentUser?.email || 'Customer';
    
    const flightData = mainFlight?.flightData || mainFlight?.flight_data || {};
    const slices = flightData.slices || [];
    let route = '';
    if (slices.length > 0) {
      const firstSlice = slices[0];
      const origin = firstSlice.segments?.[0]?.origin?.iata_code || mainFlight.departure_city;
      const destination = firstSlice.segments?.[firstSlice.segments.length - 1]?.destination?.iata_code || mainFlight.arrival_city;
      route = `${getCityName(origin)} → ${getCityName(destination)}`;
    } else {
      route = 'my trip';
    }
    const bookingRef = trip[0]?.booking_reference || trip[0]?.bookingReference || trip[0]?.trip_reference || trip[0]?.tripReference || '';
    const bookingRefText = bookingRef ? `, booking reference: ${bookingRef}` : '';
    
    let message = '';
    if (addOnType === 'seats') {
      message = `I'd like to buy seats for my trip ${route}${bookingRefText} using my Defera Miles.`;
    } else if (addOnType === 'baggage') {
      message = `I'd like to add baggage for my trip ${route}${bookingRefText} using my Defera Miles.`;
    } else if (addOnType === 'cabin') {
      message = `I'd like to upgrade my cabin class for my trip ${route}${bookingRefText} using my Defera Miles.`;
    }
    
    navigate(buildTommyUrl({ message, bookingReference: bookingRef }));
  };

  /** "Finished paying for your trip?" — files the Confirmation Number
   *  Release inquiry (POST /api/concierge-inquiries/confirmation-release).
   *  The final-payment webhook usually filed it already, so the common
   *  outcome is the "already in progress" acknowledgement; the create path
   *  is the backstop for a missed webhook. Either way the customer gets a
   *  single-button overlay, never an error dead-end. */
  const handleFinishedPaying = async () => {
    if (isSubmittingTripPaid || !bookingId) return;
    setIsSubmittingTripPaid(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const res = await fetch('/api/concierge-inquiries/confirmation-release', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ bookingId }),
      });
      const data = await res.json().catch(() => ({}));

      // One customer-catered voice for everyone — an admin acting on the
      // customer's behalf sees exactly what the customer would see. No
      // internal mechanics (daily report, SLAs, support@) in the copy.
      if (data?.state === 'created') {
        // The new inquiry should appear on the trip's service card without
        // a reopen — ServiceInquiryCard listens for this.
        window.dispatchEvent(new CustomEvent('defera:inquiries-updated'));
        setTripPaidNotice({
          ok: true,
          title: 'Request submitted',
          // Accounting releases Monday through Friday: a Friday-afternoon or
          // weekend request lands Monday, Tuesday at the latest.
          message: `We have submitted a request to the team to release your confirmation number. ${releaseTimingSentence(new Date())} You can follow the status of this release in the Trips tab.`,
        });
      } else if (data?.state === 'already_pending') {
        setTripPaidNotice({
          ok: true,
          title: 'Already in progress',
          message: `Our system has already notified our team about your completion and is working on releasing your confirmation number for you. ${releaseTimingSentence(data.filed_at || new Date())}`,
        });
      } else if (data?.state === 'already_resolved') {
        setTripPaidNotice({
          ok: true,
          title: 'Already released',
          message: 'This request has already been completed — check the service card on this trip page for the note our team left you.',
        });
      } else {
        setTripPaidNotice({
          ok: false,
          title: 'One moment',
          message: data?.error || 'We couldn’t submit your request right now. Please try again in a few minutes.',
        });
      }
    } catch {
      setTripPaidNotice({
        ok: false,
        title: 'One moment',
        message: 'We couldn’t submit your request right now. Please try again in a few minutes.',
      });
    } finally {
      setIsSubmittingTripPaid(false);
    }
  };

  const handleChangeSeatSelection = () => {
    const userName = currentUser?.firstName && currentUser?.lastName 
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : currentUser?.firstName || currentUser?.email || 'Customer';
    
    const flightData = mainFlight?.flightData || mainFlight?.flight_data || {};
    const slices = flightData.slices || [];
    
    let route = '';
    if (slices.length > 0) {
      const firstSlice = slices[0];
      const origin = firstSlice.segments?.[0]?.origin?.iata_code || mainFlight.departure_city;
      const destination = firstSlice.segments?.[firstSlice.segments.length - 1]?.destination?.iata_code || mainFlight.arrival_city;
      route = `${getCityName(origin)} → ${getCityName(destination)}`;
    } else {
      route = 'my trip';
    }
    
    const bookingRef = trip[0]?.booking_reference || trip[0]?.bookingReference || trip[0]?.trip_reference || trip[0]?.tripReference || '';
    const bookingRefText = bookingRef ? `, booking reference: ${bookingRef}` : '';
    
    const message = `I'd like to change my seat selection for my trip ${route}${bookingRefText}.`;
    
    navigate(buildTommyUrl({ message, bookingReference: bookingRef }));
    setShowChangeSeatModal(false);
  };

  // Rendered into <body>, not where it was mounted. Everything here is
  // `position: fixed`, and a fixed element is positioned against the nearest
  // ancestor carrying a transform, filter, backdrop-filter or contain — not
  // the viewport. Trips mounts this at the page root so it never mattered;
  // Risk Review mounts it inside a row whose panel has `backdrop-blur-2xl`
  // and `overflow-hidden`, which made the modal size itself to the row and
  // then clipped it to a sliver (2026-09-18). Every other overlay in this app
  // already portals for the same reason.
  return createPortal(
    <>
      <motion.div
        className="fixed inset-0 z-50 flex items-start justify-center px-1 py-2 sm:p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="absolute inset-0 bg-gradient-to-b from-brand-950/90 via-[#050814]/90 to-black/90 backdrop-blur-md"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />

        <motion.div
          className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl px-2 py-4 sm:p-6 max-w-6xl w-full max-h-[calc(100vh-80px)] overflow-y-auto mt-[72px] sm:mt-20"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* A named way back, for callers that opened this from somewhere
              other than Trips. Without it the only desktop dismissal is
              clicking the backdrop, which is invisible — fine when you came
              from the trips list you are still looking at, not fine when you
              arrived from another page entirely (Risk Review, 2026-09-18). */}
          {backLabel && (
            <Button
              variant="ghost"
              onClick={onClose}
              className="mb-3 -ml-1 h-8 rounded-full px-3 text-white/60 hover:text-white hover:bg-white/10 text-xs font-normal"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
              {backLabel}
            </Button>
          )}

          {/* Mobile: Close button in top right corner */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-4 right-4 sm:hidden text-white/60 hover:text-white hover:bg-white/10 h-10 w-10 z-10"
          >
            <X className="w-5 h-5" />
          </Button>
          {/* Loading State */}
          {isLoading ? (
            <div className="space-y-6">
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-white/60 animate-spin" />
              </div>
              <div className="space-y-4">
                <div className="h-20 bg-white/5 rounded-2xl animate-pulse" />
                <div className="h-32 bg-white/5 rounded-2xl animate-pulse" />
                <div className="h-24 bg-white/5 rounded-2xl animate-pulse" />
              </div>
            </div>
          ) : (
            <>
              {/* Header + trip progress — single brand gradient wash */}
              <motion.div
                className="mb-3 -mx-2 -mt-4 sm:-mx-6 sm:-mt-6 rounded-t-3xl relative"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-t-3xl overflow-hidden bg-gradient-to-b from-brand-900 via-brand-800/60 to-transparent"
              />
              <div className="relative z-[1] flex flex-col sm:flex-row sm:items-start sm:justify-between px-4 pt-6 pb-4 sm:px-8 sm:pt-7 sm:pb-5 gap-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl sm:text-4xl font-semibold text-white tracking-tight mb-3">{routeHeader}</h2>
                  <div className="flex flex-col gap-3">
                    {/* First row: Trip type, status, and booking reference */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-sm text-white/60 font-normal">{tripType}</span>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(tripStatus)}
                        {/* The customer's own VIP badge; staff see theirs under Booked By. */}
                        {viewerOwnsTrip && <MyVipBadge />}
                        {(trip[0]?.booking_reference || trip[0]?.bookingReference || trip[0]?.trip_reference || trip[0]?.tripReference) && (
                          <div className="flex items-center gap-1.5 relative">
                          <span className="text-sm font-mono text-white/80 tracking-wide">
                            {trip[0]?.booking_reference || trip[0]?.bookingReference || trip[0]?.trip_reference || trip[0]?.tripReference}
                          </span>
                          <CopyChip
                            fieldKey="booking-reference"
                            value={trip[0]?.booking_reference || trip[0]?.bookingReference || trip[0]?.trip_reference || trip[0]?.tripReference}
                            copiedField={copiedField}
                            onCopy={handleCopyField}
                            ariaLabel="Copy booking reference"
                          />
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className="text-white/40 hover:text-white/60 transition-colors p-0.5 flex-shrink-0"
                                  aria-label="Booking reference information"
                                >
                                  <Info className="w-3.5 h-3.5" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="start"
                                side="bottom"
                                sideOffset={8}
                                collisionPadding={16}
                                className="z-[100] w-[min(calc(100vw-2rem),20rem)] bg-[#111] border-white/20 text-white/90 p-4 shadow-2xl rounded-xl"
                              >
                                <p className="text-xs leading-relaxed break-words">
                                  <strong className="font-medium text-white">Defera Booking Reference</strong>
                                  <br /><br />
                                  This reference number is issued by Defera for internal tracking purposes and is distinct from the airline&apos;s confirmation number. As this is a layaway service, official ticket documentation and airline confirmation details will be provided upon full payment of your installment plan.
                                </p>
                              </PopoverContent>
                            </Popover>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Second row: Confirmation number (mobile: buttons appear below this) */}
                    {(trip[0]?.confirmation_number || trip[0]?.confirmationNumber) && (staffView || tripStatus === 'completed') && (
                      <span className="text-sm font-mono text-green-400 tracking-wide flex items-center gap-1.5">
                        <span className="text-white/60">Confirmation:</span>
                        {trip[0]?.confirmation_number || trip[0]?.confirmationNumber}
                        <CopyChip
                          fieldKey="confirmation-number"
                          value={trip[0]?.confirmation_number || trip[0]?.confirmationNumber}
                          copiedField={copiedField}
                          onCopy={handleCopyField}
                          ariaLabel="Copy confirmation number"
                        />
                      </span>
                    )}
                    {/* Action buttons - on mobile they appear below confirmation, on desktop they're on the right */}
                    <div className="flex items-center gap-2 sm:hidden">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          // Open the concierge on /support with this trip
                          // already in focus, so the conversation starts on
                          // the trip they were just looking at.
                          const id = trip?.[0]?.id;
                          navigate(id ? `/support?bookingId=${encodeURIComponent(id)}` : '/support');
                        }}
                        className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-10 w-10"
                        title="Chat with Defera Support"
                        aria-label="Chat with Defera Support about this trip"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => { window.location.href = SUPPORT_PHONE_TEL; }}
                        className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-10 w-10"
                        title="Call Defera Support"
                        aria-label="Call Defera Support"
                      >
                        <Phone className="w-4 h-4" />
                      </Button>
                      <DownloadTripDocumentButton
                        trip={trip}
                        passengers={passengers}
                        currentUser={currentUser}
                        tripUser={tripUser}
                        route={routeHeader}
                      />
                      {currentUser?.role === 'admin' && (
                        <>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setShowAdminEdit(true)}
                            className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-10 w-10"
                            title="Edit Booking"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setIsEditingSeats(!isEditingSeats)}
                            className={`bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-10 w-10 ${isEditingSeats ? 'bg-primary/20 border-primary/30' : ''}`}
                            title="Edit Seat Assignments"
                          >
                            <Users className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setLinkTripFormOpen(true)}
                            className={`bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-10 w-10 ${linkTripFormOpen ? 'bg-amber-300/15 border-amber-300/30' : ''}`}
                            title="Link to another trip"
                            aria-label="Link this trip to another trip"
                          >
                            <Link2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              setShowDeleteConfirm(true);
                              setDeleteConfirmationText(''); // Reset confirmation text when opening modal
                            }}
                            className="bg-red-500/5 border-red-500/20 text-red-400 hover:bg-red-500/10 hover:border-red-500/30 h-10 w-10"
                            title="Delete Trip"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {/* Desktop: Buttons on the right */}
                <div className="hidden sm:flex items-center gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const id = trip?.[0]?.id;
                      navigate(id ? `/support?bookingId=${encodeURIComponent(id)}` : '/support');
                    }}
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-10 w-10"
                    title="Chat with Defera Support"
                    aria-label="Chat with Defera Support about this trip"
                  >
                    <MessageCircle className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => { window.location.href = SUPPORT_PHONE_TEL; }}
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-10 w-10"
                    title="Call Defera Support"
                    aria-label="Call Defera Support"
                  >
                    <Phone className="w-4 h-4" />
                  </Button>
                  <DownloadTripDocumentButton
                    trip={trip}
                    passengers={passengers}
                    currentUser={currentUser}
                    tripUser={tripUser}
                    route={routeHeader}
                  />
                  {currentUser?.role === 'admin' && (
                    <>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setShowAdminEdit(true)}
                        className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-10 w-10"
                        title="Edit Booking"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setIsEditingSeats(!isEditingSeats)}
                        className={`bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-10 w-10 ${isEditingSeats ? 'bg-primary/20 border-primary/30' : ''}`}
                        title="Edit Seat Assignments"
                      >
                        <Users className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setLinkTripFormOpen(true)}
                        className={`bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-10 w-10 ${linkTripFormOpen ? 'bg-amber-300/15 border-amber-300/30' : ''}`}
                        title="Link to another trip"
                        aria-label="Link this trip to another trip"
                      >
                        <Link2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          setShowDeleteConfirm(true);
                          setDeleteConfirmationText(''); // Reset confirmation text when opening modal
                        }}
                        className="bg-red-500/5 border-red-500/20 text-red-400 hover:bg-red-500/10 hover:border-red-500/30 h-10 w-10"
                        title="Delete Trip"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={onClose} 
                    className="text-white/60 hover:text-white hover:bg-white/10 h-10 w-10"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Trip progress: sleek flight-path style */}
              {(() => {
                const isTicketed = mainFlight?.flightData?.is_ticketed ?? mainFlight?.flight_data?.is_ticketed ?? false;
                const isCancelled = tripStatus === 'cancelled';
                const isDefaulted = tripStatus === 'defaulted';
                const isConfirmed = tripStatus === 'confirmed';
                // NMI terminal-decline state (migrations 068/073) — the plan
                // is mid-payoff with failing payments, and it's recoverable
                // (restart-plan flips it back to confirmed). The stepper must
                // hold at "Paying Off" with a warning, NOT regress to
                // "Processing" (F27QK3 incident, 2026-08-04).
                const isDelinquent = tripStatus === 'delinquent';
                const isBadEndState = isCancelled || isDefaulted;
                // Defaulted + never ticketed (no airline PNR) → the customer can
                // convert 100% of cash paid into Defera Credit. Gates mirror the
                // defera-credit-default-opt-in edge function: real airline
                // confirmation check, owner only (admins viewing someone else's
                // trip see the plain defaulted statement), and only the Capital/
                // Zero tracks — legacy slice_pay plans have no trusted payment
                // ledger to compute a credit from, so those stay with support.
                const tripOwnerId = trip?.[0]?.userId || trip?.[0]?.user_id || null;
                const isOwnTrip = !tripOwnerId || (currentUser?.id && tripOwnerId === currentUser.id);
                const fdForCredit = mainFlight?.flightData || mainFlight?.flight_data || {};
                const pmForCredit = fdForCredit._payment_method || fdForCredit.payment_method;
                // Close-in gate (founder policy 2026-08-11): a trip that
                // defaulted within 7 days of departure never converts
                // self-serve — support reviews it. The client can't see
                // defaulted_at, but every such trip necessarily departs
                // within 7 days of NOW (default time ≤ now), so gating on
                // departure covers the whole window; the edge function
                // enforces the precise rule server-side.
                const departingAtForCredit = fdForCredit?.slices?.[0]?.segments?.[0]?.departing_at;
                const departureMsForCredit = departingAtForCredit ? Date.parse(departingAtForCredit) : NaN;
                const departsBeyondCloseInWindow = Number.isFinite(departureMsForCredit)
                  && departureMsForCredit - Date.now() > 7 * 86400000;
                const canConvertDefaultToCredit = isDefaulted && isOwnTrip
                  && !tripHasAirlineConfirmation(trip)
                  && departsBeyondCloseInWindow
                  && (pmForCredit === 'defera_capital' || pmForCredit === 'defera_zero');
                // Admin viewing a defaulted trip → offer Reinstate (the edge
                // function re-verifies the admin role server-side).
                const viewerRoles = currentUser?.app_metadata?.roles || [];
                const isAdminViewer = viewerRoles.includes('admin') || currentUser?.role === 'admin';
                
                // "Processing" is a purely visual first stage. New bookings sit
                // there for the 30s window, then jump to "Paying Off" — which
                // checks off "Confirmed" along the way. The underlying trip
                // status stays 'confirmed' the whole time.
                const currentStage = (tripStatus === 'completed' || isBadEndState)
                  ? 4
                  : isDelinquent
                    ? 3 // still in payoff — just failing; the node renders the warning
                    : (isConfirmed || isTicketed)
                      ? (isProcessingWindow ? 1 : 3)
                      : 1;
                
                const stages = [
                  { label: 'Processing', value: 1 },
                  { label: 'Confirmed', value: 2 },
                  { label: 'Paying Off', value: 3 },
                  { 
                    label: isCancelled ? 'Cancelled' : isDefaulted ? 'Defaulted' : 'Completed', 
                    value: 4 
                  },
                ];
                
                return (
                  <motion.div
                    className="relative z-[1] px-2 sm:px-6 pb-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: 'easeOut', delay: 0.05 }}
                  >
                    <div className="relative pt-2 pb-4 sm:pt-3 sm:pb-5 px-2 sm:px-6">
                      {/* Bottom hairline — separates progress from card body below */}
                      <div className="absolute bottom-0 inset-x-4 sm:inset-x-12 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                      
                      {/* Subtle ambient glow in the center */}
                      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[var(--accent-color)]/5 via-transparent to-transparent pointer-events-none" />

                      {/* Connector Line Background (full width) */}
                      <div className="absolute top-9 sm:top-11 left-2 right-2 sm:left-6 sm:right-6 h-[2px] bg-white/10 rounded-full" />
                      
                      {/* Active Connector Line (progress based) */}
                      <div className="absolute top-9 sm:top-11 left-2 right-2 sm:left-6 sm:right-6 h-[2px] rounded-full overflow-hidden">
                        <motion.div 
                          className={cn(
                            "h-full bg-gradient-to-r",
                            isBadEndState 
                              ? "from-[var(--accent-color)]/50 via-[var(--accent-color)] to-red-500" 
                              : "from-[var(--accent-color)]/50 to-[var(--accent-color)]"
                          )}
                          initial={{ width: '0%' }}
                          animate={{ width: `${((currentStage - 1) / (stages.length - 1)) * 100}%` }}
                          transition={{ duration: 0.8, ease: 'easeInOut', delay: 0.2 }}
                        />
                      </div>

                      <div className="relative flex justify-between items-start w-full">
                        {stages.map((stage, index) => {
                          const isReached = currentStage >= stage.value;
                          const isCurrent = currentStage === stage.value;
                          const isPast = currentStage > stage.value;

                          // Determine if this specific node should be red (only the last one if bad state)
                          const isBadNode = isBadEndState && stage.value === 4;
                          // Delinquent: the Paying Off node itself carries the
                          // amber warning — recoverable, so never red/terminal.
                          const isWarnNode = isDelinquent && stage.value === 3;
                          
                          return (
                            <div key={stage.value} className="flex flex-col items-center flex-shrink-0 relative z-10 group cursor-default w-16 sm:w-28">
                              {/* Node Circle */}
                              <motion.div
                                className={cn(
                                  'flex items-center justify-center w-10 h-10 rounded-full border-[2px] backdrop-blur-md transition-all duration-500 z-20 relative',
                                  isCurrent
                                    ? (isBadNode
                                        ? 'bg-red-500 border-red-500 shadow-[0_0_20px_#ef4444] scale-110'
                                        : isWarnNode
                                          ? 'bg-amber-500 border-amber-500 shadow-[0_0_20px_#f59e0b] scale-110'
                                          : 'bg-[var(--accent-color)] border-[var(--accent-color)] shadow-[0_0_20px_var(--accent-color)] scale-110')
                                    : isPast
                                    ? 'bg-[var(--accent-color)] border-[var(--accent-color)]'
                                    : 'bg-[#0a0a0a] border-white/10 group-hover:border-white/20'
                                )}
                                initial={false}
                                animate={isCurrent ? { scale: 1.15 } : { scale: 1 }}
                              >
                                {isCurrent ? (
                                  isBadNode ? (
                                    <X className="w-5 h-5 text-white" strokeWidth={2.5} />
                                  ) : isWarnNode ? (
                                    <AlertCircle className="w-5 h-5 text-white" strokeWidth={2.5} />
                                  ) : stage.value === 1 ? (
                                    <Loader2 className="w-5 h-5 text-white animate-spin" strokeWidth={2.5} />
                                  ) : (
                                    <Plane className="w-5 h-5 text-white -rotate-45" strokeWidth={2.5} />
                                  )
                                ) : isPast ? (
                                  <CheckCircle className="w-5 h-5 text-white" strokeWidth={2.5} />
                                ) : (
                                  <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
                                )}
                              </motion.div>
                              
                              {/* Label - Static positioning to reserve space */}
                              <div className="flex flex-col items-center mt-3 sm:mt-4 space-y-0.5">
                                <span
                                  className={cn(
                                    'text-[9px] sm:text-[11px] uppercase tracking-[0.08em] sm:tracking-widest font-medium transition-colors duration-300 text-center leading-tight whitespace-nowrap',
                                    isReached ? (isBadNode ? 'text-red-500' : isWarnNode && isCurrent ? 'text-amber-400' : 'text-white') : 'text-white/30'
                                  )}
                                >
                                  {stage.label}
                                </span>
                                {isCurrent && (
                                  <motion.span
                                    initial={{ opacity: 0, y: -4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5 }}
                                    className={cn(
                                      "text-[9px] sm:text-[10px] font-medium whitespace-nowrap",
                                      isBadNode ? "text-red-500" : isWarnNode ? "text-amber-400" : "text-white/60"
                                    )}
                                  >
                                    {isWarnNode ? 'Payments failing' : 'Current Status'}
                                  </motion.span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Status Definition Text */}
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                        className="mt-5 sm:mt-6 mx-auto max-w-xl sm:max-w-4xl text-center"
                      >
                        <p className={cn(
                          "text-[10px] sm:text-xs font-normal leading-relaxed",
                          isBadEndState ? "text-red-400/90" : isDelinquent ? "text-amber-300/90" : "text-white/60"
                        )}>
                          {currentStage === 1 && (
                            (isConfirmed || isTicketed)
                              ? "We're processing your booking and securing your reservation. This only takes a moment — hang tight."
                              : "We've received your order and are verifying details before queuing for ticketing with the airline. This process may take a few days. Trips in this stage can be cancelled at any time for a refund of your deposit. In the rare event of a fare change during this process, we will reach out with next steps."
                          )}
                          {isDelinquent && (
                            // Recoverable state — present BOTH honest paths
                            // (founder call, 2026-08-04): catch up via the
                            // payment schedule below, or cancel from Flight
                            // Options (unbooked Capital/Zero plans cancel for
                            // 100% back as Defera Credit — the overlay states
                            // the exact figure).
                            "Recent installment payments on this trip didn't go through. Update your card or restart your plan from the payment schedule below to keep your trip on track — or cancel from the Flight Options menu in the flight details below. If the balance stays unresolved, the trip may be cancelled."
                          )}
                          {currentStage === 3 && !isBadEndState && !isDelinquent && (
                            "Your trip is confirmed and you're actively paying down your installment plan. The confirmation number will be released once your plan is paid in full."
                          )}
                          {currentStage === 4 && !isBadEndState && (
                            "Your plan is paid in full and the confirmation number has been released. You can now manage changes directly with the airline."
                          )}
                          {isCancelled && (
                            "This trip has been cancelled. If this is an error, please contact our concierge team immediately."
                          )}
                          {isDefaulted && !canConvertDefaultToCredit && (
                            "This trip was cancelled due to payment default. Outstanding balances may be referred to collections, and future service eligibility is suspended."
                          )}
                          {canConvertDefaultToCredit && (
                            // The client can't see the collections-case status
                            // (the edge function is the source of truth and
                            // refuses trips already with the agency), so the
                            // copy is conditional — never a flat promise — to
                            // avoid contradicting a "referred to collections"
                            // refusal the customer may hit on tap.
                            "This trip was cancelled due to payment default. Unless your balance has been referred to collections, you can convert what you've paid into Defera Credit toward a future trip."
                          )}
                        </p>
                        {isDefaulted && (
                          <div className="mt-4 flex justify-center gap-2 flex-wrap">
                            {isAdminViewer && (
                              <Button
                                size="sm"
                                onClick={handleReinstateDefaultedTrip}
                                disabled={isReinstating}
                                className="bg-white/10 border border-white/25 text-white hover:bg-white/20 hover:border-white/40"
                              >
                                {isReinstating ? (
                                  <>
                                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                    Reinstating…
                                  </>
                                ) : reinstateArmed ? (
                                  `Tap again — charges ${REINSTATEMENT_FEE_LABEL} fee`
                                ) : (
                                  'Reinstate Trip'
                                )}
                              </Button>
                            )}
                            {canConvertDefaultToCredit && (
                              <Button
                                size="sm"
                                onClick={handleDefaultCreditOptIn}
                                disabled={isConvertingDefaultCredit}
                                className="bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/25 hover:border-emerald-400/60 hover:text-emerald-200"
                              >
                                {isConvertingDefaultCredit ? (
                                  <>
                                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                    Converting…
                                  </>
                                ) : defaultCreditArmed ? (
                                  'Tap again to confirm'
                                ) : (
                                  'Convert to Defera Credit'
                                )}
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const bookingRef = trip?.[0]?.booking_reference || trip?.[0]?.bookingReference || trip?.[0]?.trip_reference || trip?.[0]?.tripReference || 'N/A';
                                const route = routeHeader || 'my trip';
                                const userName = tripUser?.firstName && tripUser?.lastName
                                  ? `${tripUser.firstName} ${tripUser.lastName}`
                                  : (currentUser?.firstName && currentUser?.lastName ? `${currentUser.firstName} ${currentUser.lastName}` : null)
                                  || passengers?.[0] ? `${passengers[0].given_name || passengers[0].givenName || ''} ${passengers[0].family_name || passengers[0].familyName || ''}`.trim() : 'Guest';
                                const message = `My trip [${bookingRef}] ${route} is in default and I need some help with it.`;
                                navigate(buildTommyUrl({ message, bookingReference: bookingRef }));
                              }}
                              className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 hover:text-red-300"
                            >
                              Contact Support
                            </Button>
                          </div>
                        )}
                      </motion.div>
                    </div>
                  </motion.div>
                );
              })()}
              </motion.div>

              {/* Linked trips (staff only): the other trips this one travels
                  with and the team's note, first thing under the header so a
                  request like "seat these two together" can't be missed.
                  Admins link and unlink here; the concierge reads it. */}
              {staffView && bookingId && (
                <LinkedTripsBanner
                  key={bookingId}
                  bookingId={bookingId}
                  canEdit={isAdmin(currentUser)}
                  formOpen={linkTripFormOpen}
                  onFormOpenChange={setLinkTripFormOpen}
                />
              )}

              {/* "Traveling together" (customer): the trip's owner sees that our
                  team linked it to their companions' bookings, for peace of
                  mind. Their own trips by reference; anyone else's only as
                  "another Defera booking" (the server never sends more). */}
              {viewerOwnsTrip && bookingId && (
                <CustomerTravelingTogether
                  key={`together-${bookingId}`}
                  bookingId={bookingId}
                  thisReference={travelingTogetherReference}
                />
              )}

              {/* Upgraded card — shown when admin has marked the trip as upgraded */}
              {(() => {
                const fd = mainFlight?.flightData || mainFlight?.flight_data || {};
                const isUpgraded = !!fd.is_upgraded;
                if (!isUpgraded) return null;
                const upgradedTo = (fd.upgraded_to || '').trim();
                const airlineLogo = getAirlineLogoFromAPI(mainFlight);
                const airlineName = (() => {
                  const firstSlice = fd?.slices?.[0];
                  const firstSegment = firstSlice?.segments?.[0];
                  return firstSegment?.marketing_carrier?.name || '';
                })();
                return (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, ease: 'easeOut', delay: 0.1 }}
                    className="relative mb-6 overflow-hidden rounded-2xl border border-brand-300/20"
                  >
                    {/* Mobile: static, lightweight gradient (no blurs, no backdrop-filter) */}
                    <div
                      className="absolute inset-0 sm:hidden"
                      style={{
                        background:
                          'radial-gradient(ellipse 120% 80% at 85% 0%, rgba(15, 25, 82,0.12), transparent 60%), radial-gradient(ellipse 100% 80% at 10% 100%, rgba(10,18,51,0.08), transparent 65%), linear-gradient(135deg, rgba(15, 25, 82,0.05) 0%, rgba(255,255,255,0.02) 50%, transparent 100%)',
                      }}
                    />

                    {/* Desktop: richer layered gradient with clipped glow blobs */}
                    <div className="hidden sm:block absolute inset-0 bg-gradient-to-br from-brand-400/[0.08] via-white/[0.03] to-transparent backdrop-blur-sm" />
                    <div className="hidden sm:block pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(15, 25, 82,0.14),_transparent_60%)]" />
                    <div className="hidden sm:block pointer-events-none absolute -right-16 -top-16 w-48 h-48 rounded-full bg-brand-300/10 blur-3xl" />
                    <div className="hidden sm:block pointer-events-none absolute -left-20 -bottom-20 w-56 h-56 rounded-full bg-brand-500/5 blur-3xl" />

                    <div className="relative flex items-start gap-4 sm:gap-5 p-4 sm:p-6">
                      <motion.div
                        initial={{ scale: 0.85, rotate: -8 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: 'spring', stiffness: 220, damping: 16, delay: 0.25 }}
                        className="relative flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white/[0.04] border border-white/10 ring-1 ring-brand-300/20 flex items-center justify-center shadow-[0_0_24px_-8px_rgba(15, 25, 82,0.35)] overflow-hidden p-2 sm:backdrop-blur-sm"
                      >
                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(15, 25, 82,0.12),_transparent_70%)]" />
                        {airlineLogo ? (
                          <img
                            src={airlineLogo}
                            alt={airlineName || 'Airline'}
                            className="relative w-full h-full object-contain"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <Sparkles className="relative w-5 h-5 sm:w-6 sm:h-6 text-brand-200" strokeWidth={2} />
                        )}
                      </motion.div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
                          <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] font-medium text-brand-200/80">
                            Complimentary Upgrade
                          </span>
                        </div>
                        <h3 className="text-white text-[15px] sm:text-lg font-[350] sm:font-light tracking-tight leading-snug">
                          You've been upgraded{upgradedTo ? (
                            <> to <span className="text-brand-200 font-normal">{upgradedTo}</span></>
                          ) : null}
                        </h3>
                        <p className="mt-1 text-white/60 text-[11px] sm:text-sm font-normal leading-snug sm:leading-relaxed">
                          As a thank you for booking with Defera, we've arranged this upgrade for you{airlineName ? ` on ${airlineName}` : ''}. It will be reflected on your boarding pass at check-in.
                        </p>
                        {!hideReviewCard && <a
                          href="https://www.trustpilot.com/review/defera.app"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-block text-[10px] text-white/20 underline underline-offset-2 decoration-white/10 hover:text-white/40 hover:decoration-white/20 transition-colors duration-200"
                        >
                          Leave a review
                        </a>}
                      </div>
                    </div>
                  </motion.div>
                );
              })()}

              {/* Complimentary rewards besides a cabin upgrade (Edit → Rewarded) */}
              {(() => {
                const rewards = tripRewardsOf(flightData);
                if (!rewards.length) return null;
                const airlineName = flightData?.slices?.[0]?.segments?.[0]?.marketing_carrier?.name || '';
                return <TripRewardsCard rewards={rewards} airlineName={airlineName} />;
              })()}

              {/* Trustpilot review CTA — shown only for completed trips */}
              {tripStatus === 'completed' && !hideReviewCard && (
                <motion.a
                  href="https://www.trustpilot.com/review/defera.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
                  className="group relative block mb-6 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#00B67A]/[0.08] via-white/[0.03] to-transparent backdrop-blur-sm transition-all duration-300 hover:border-[#00B67A]/40 hover:bg-[#00B67A]/[0.06] active:scale-[0.995]"
                >
                  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(0,182,122,0.12),_transparent_60%)]" />
                  <div className="pointer-events-none absolute -right-16 -top-16 w-48 h-48 rounded-full bg-[#00B67A]/10 blur-3xl transition-opacity duration-500 opacity-60 group-hover:opacity-100" />

                  <div className="relative flex items-center gap-3 sm:gap-4 p-4 sm:p-6">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-[#00B67A]/15 border border-[#00B67A]/25 flex items-center justify-center shadow-[0_0_24px_-6px_rgba(0,182,122,0.4)]">
                      <Star className="w-5 h-5 sm:w-7 sm:h-7 text-[#00B67A] fill-[#00B67A]" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-0.5 sm:mb-1">
                        <h3 className="text-white text-[14px] sm:text-[17px] font-normal tracking-tight leading-snug">
                          Enjoyed our customer service?
                        </h3>
                        <div className="hidden sm:flex items-center gap-0.5">
                          {[0, 1, 2, 3, 4].map((i) => (
                            <Star
                              key={i}
                              className="w-3 h-3 text-[#00B67A] fill-[#00B67A]"
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-white/60 text-[11px] sm:text-sm font-normal leading-relaxed line-clamp-2 sm:line-clamp-none">
                        Leave us a review on Trustpilot — it takes under a minute and means the world to our team.
                      </p>
                    </div>

                    <div className="flex-shrink-0 flex items-center gap-1.5 text-[#00B67A] text-xs sm:text-sm font-medium opacity-90 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-0.5">
                      <span className="hidden sm:inline">Review</span>
                      <ExternalLink className="w-4 h-4" />
                    </div>
                  </div>
                </motion.a>
              )}

              <div className="space-y-6">
            {/* Flight Change Alert - Show if changes exist (stored inside flight_data) */}
            {(() => {
              const flightData = mainFlight?.flightData || mainFlight?.flight_data;
              const flightChanges = flightData?.flight_changes;
              return flightChanges && flightChanges.slices && flightChanges.slices.length > 0 && (
              <div className="bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 backdrop-blur-sm border border-brand-800/30 rounded-3xl overflow-hidden">
                <SectionBand
                  icon={AlertCircle}
                  iconColor="text-brand-400"
                  accentRgb="10, 18, 51"
                  title="Flight Schedule Change"
                />
                <div className="p-6">
                <p className="text-white/85 text-sm mb-6">
                  The airline has updated your flight schedule. Below is a comparison of the original and updated itinerary.
                </p>
                
                <div className="space-y-6">
                  {flightChanges.slices.map((changedSlice, index) => {
                    // Get original flight from flightLegs
                    const originalLeg = flightLegs[index];
                    const originalFlight = originalLeg?.[0];
                    
                    // Parse changed flight data
                    const changedSegment = changedSlice.segments?.[0];
                    const changedLastSegment = changedSlice.segments?.[changedSlice.segments.length - 1];
                    
                    if (!originalFlight || !changedSegment) return null;
                    
                    const parseDateTime = (dateTimeStr) => {
                      if (!dateTimeStr) return { date: '', time: '' };
                      if (dateTimeStr.includes('T')) {
                        const [date, time] = dateTimeStr.split('T');
                        return { date, time: time?.substring(0, 5) || '' };
                      }
                      const parts = dateTimeStr.trim().split(' ');
                      if (parts.length >= 2) {
                        return { date: parts[0], time: parts[1]?.substring(0, 5) || '' };
                      }
                      return { date: parts[0] || '', time: '' };
                    };
                    
                    // Normalize dates for comparison (extract YYYY-MM-DD from any format)
                    const normalizeDate = (dateStr) => {
                      if (!dateStr) return '';
                      // Handle ISO format: "2026-03-03T15:50:00Z" or datetime: "2026-03-03 15:50"
                      if (dateStr.includes('T')) {
                        return dateStr.split('T')[0];
                      }
                      if (dateStr.includes(' ')) {
                        return dateStr.split(' ')[0];
                      }
                      // Plain date: "2026-03-03"
                      return dateStr.substring(0, 10);
                    };
                    
                    // Normalize times for comparison (extract HH:MM from any format)
                    const normalizeTime = (timeStr) => {
                      if (!timeStr) return '';
                      // Handle ISO format: "2026-03-03T15:50:00Z" or "2026-03-03T15:50"
                      if (timeStr.includes('T')) {
                        const timePart = timeStr.split('T')[1];
                        return timePart.substring(0, 5);
                      }
                      // Handle space-delimited: "2026-03-03 15:50"
                      if (timeStr.includes(' ')) {
                        const parts = timeStr.split(' ');
                        return parts[parts.length - 1].substring(0, 5);
                      }
                      // Plain time: "15:50"
                      return timeStr.substring(0, 5);
                    };
                    
                    // Format time to 12-hour format with AM/PM
                    const formatTime = (timeStr) => {
                      if (!timeStr) return '';
                      // Extract time from various formats using normalizeTime
                      const time24 = normalizeTime(timeStr);
                      const [hours, minutes] = time24.split(':').map(Number);
                      if (isNaN(hours) || isNaN(minutes)) return timeStr;
                      const period = hours >= 12 ? 'PM' : 'AM';
                      const hours12 = hours % 12 || 12;
                      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
                    };
                    
                    const changedDeparture = parseDateTime(changedSegment.departing_at);
                    const changedArrival = parseDateTime(changedLastSegment.arriving_at);
                    
                    // Check if this flight actually changed - compare key fields with normalization
                    const originalDepTime = normalizeTime(originalFlight.departure_time);
                    const originalArrTime = normalizeTime(originalFlight.arrival_time);
                    const originalDepDate = normalizeDate(originalFlight.departure_date);
                    const originalArrDate = normalizeDate(originalFlight.arrival_date);
                    
                    // Get the flight number from original data
                    const originalFlightNumber = originalFlight.flight_number || 
                                                 originalFlight.marketing_carrier_flight_number ||
                                                 originalFlight.sliceData?.segments?.[0]?.marketing_carrier_flight_number;
                    
                    const flightChanged = 
                      originalFlightNumber !== changedSegment.marketing_carrier_flight_number ||
                      originalDepTime !== changedDeparture.time ||
                      originalArrTime !== changedArrival.time ||
                      originalDepDate !== changedDeparture.date ||
                      originalArrDate !== changedArrival.date;
                    
                    // Skip if flight didn't change
                    if (!flightChanged) return null;
                    
                    return (
                      <div key={`change-${index}`} className="space-y-3">
                        <h4 className="text-white/60 text-sm font-normal">
                          {index === 0 ? 'Outbound Flight' : 'Return Flight'}
                        </h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Original Flight */}
                          <div className="glass rounded-xl p-4 border border-white/10">
                            <p className="text-white/50 text-xs mb-3 uppercase tracking-wide">Original</p>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-white/60 text-sm">Airline</span>
                                <span className="text-white font-normal">{originalFlight.marketing_airline}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-white/60 text-sm">Flight</span>
                                <span className="text-white font-normal">{originalFlightNumber || 'N/A'}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-white/60 text-sm">Departure</span>
                                <span className="text-white font-normal">
                                  {originalFlight.departure_city} {formatTime(originalFlight.departure_time)}
                                  <div className="text-xs text-white/50">
                                    {originalFlight.departure_date ? format(parseISO(originalFlight.departure_date), 'MMM d, yyyy') : ''}
                                  </div>
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-white/60 text-sm">Arrival</span>
                                <span className="text-white font-normal">
                                  {originalFlight.arrival_city} {formatTime(originalFlight.arrival_time)}
                                  <div className="text-xs text-white/50">
                                    {originalFlight.arrival_date ? format(parseISO(originalFlight.arrival_date), 'MMM d, yyyy') : ''}
                                  </div>
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Changed Flight */}
                          <div className="glass rounded-xl p-4 border border-brand-500/30 bg-brand-500/5">
                            <p className="text-brand-300 text-xs mb-3 uppercase tracking-wide">Updated</p>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-white/60 text-sm">Airline</span>
                                <span className="text-white font-normal">{changedSegment.marketing_carrier?.name}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-white/60 text-sm">Flight</span>
                                <span className="text-white font-normal">{changedSegment.marketing_carrier_flight_number}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-white/60 text-sm">Departure</span>
                                <span className="text-white font-normal">
                                  {changedSegment.origin?.iata_code} {formatTime(changedDeparture.time)}
                                  <div className="text-xs text-white/50">
                                    {changedDeparture.date ? format(parseISO(changedDeparture.date), 'MMM d, yyyy') : ''}
                                  </div>
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-white/60 text-sm">Arrival</span>
                                <span className="text-white font-normal">
                                  {changedLastSegment.destination?.iata_code} {formatTime(changedArrival.time)}
                                  <div className="text-xs text-white/50">
                                    {changedArrival.date ? format(parseISO(changedArrival.date), 'MMM d, yyyy') : ''}
                                  </div>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>
              </div>
            );
            })()}
            
            {/* Flight Details Section */}
            <div
              className="bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 backdrop-blur-sm border border-brand-800/30 rounded-3xl overflow-hidden"
            >
              <SectionBand
                icon={Plane}
                iconColor="text-brand-400"
                accentRgb="56,189,248"
                title="Flight Details"
                rightSlot={
                  <FlightOptionsMenu
                    disabled={flightOptionsState.disabled}
                    changeFlightEligible={changeFlightRouting !== 'self_service' || changeFlightEligibility.eligible}
                    onChangeFlight={handleChangeFlightClick}
                    onRequestChange={() => setShowRequestChangeModal(true)}
                    onPurchaseSeats={() => {
                      if (canUseSeatPurchaseFlow) {
                        setShowSeatPurchaseFlow(true);
                        return;
                      }
                      console.info('[Purchase Seats] Live seat-map flow unavailable — falling back to concierge', {
                        reason: seatPurchaseEligibility.reason
                          || (isSeatPurchaseActAs ? 'viewer_not_owner_or_admin' : 'unknown'),
                      });
                      setShowPurchaseSeatsModal(true);
                    }}
                    onPurchaseBaggage={() => setShowBaggagePurchaseFlow(true)}
                    showChangeSeatSelection={hasSeatAssignments}
                    onChangeSeatSelection={() => setShowChangeSeatModal(true)}
                    onCancelFlight={() => setShowCancelModal(true)}
                  />
                }
              />

              <div className="p-6 space-y-6">
                {/* Seat provenance: make it obvious (especially to admins) when
                    seats were purchased on the LIVE seat map after booking via
                    the dashboard "Purchase Seats" flow. (Seats picked during the
                    original booking session intentionally get no banner.) */}
                {(() => {
                  const fd = trip?.[0]?.flightData || trip?.[0]?.flight_data || {};
                  const seatPurchase = fd.seat_purchase;
                  if (!seatPurchase) return null;
                  const purchasedTotal = `$${fmtCurrency(seatPurchase.total_cents || 0)}`;
                  const purchasedDate = seatPurchase?.purchased_at
                    ? format(new Date(seatPurchase.purchased_at), 'MMM d, yyyy')
                    : null;
                  return (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-brand-400/20 bg-brand-500/[0.07] px-3.5 py-2.5 text-xs font-normal">
                      <span className="flex items-center gap-1.5 text-brand-200/90">
                        <Armchair className="w-3.5 h-3.5 flex-shrink-0" />
                        Seats purchased on the live seat map after booking
                      </span>
                      {seatPurchase && (
                        <span className="text-white/45">
                          {purchasedDate ? `${purchasedDate} · ` : ''}
                          {(seatPurchase.total_cents || 0) > 0 ? `${purchasedTotal} paid in full` : 'No charge (included seats)'}
                        </span>
                      )}
                      {seatPurchase?.initiated_by_admin && staffView && (
                        <span className="rounded-full border border-brand-400/30 bg-brand-500/10 px-2 py-0.5 text-[10px] text-brand-300">
                          Initiated by admin
                        </span>
                      )}
                    </div>
                  );
                })()}
                {purchasedBaggageSummary && purchasedBaggageSummary.totalCents > 0 && (() => {
                  const purchases = flightData.baggage_purchases || [];
                  const latestPurchase = purchases[purchases.length - 1];
                  const purchasedTotal = `$${fmtAmount(purchasedBaggageTotal)}`;
                  const purchasedDate = latestPurchase?.purchased_at
                    ? format(new Date(latestPurchase.purchased_at), 'MMM d, yyyy')
                    : null;
                  const bagCount = purchasedBaggageSummary.totalBags;
                  return (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-brand-400/20 bg-brand-500/[0.07] px-3.5 py-2.5 text-xs font-normal">
                      <span className="flex items-center gap-1.5 text-brand-200/90">
                        <Luggage className="w-3.5 h-3.5 flex-shrink-0" />
                        Checked baggage purchased after booking
                        {bagCount > 0 && (
                          <span className="text-white/55">
                            · {bagCount} bag{bagCount === 1 ? '' : 's'}
                          </span>
                        )}
                      </span>
                      <span className="text-white/45">
                        {purchasedDate ? `${purchasedDate} · ` : ''}
                        {purchasedTotal} paid in full
                      </span>
                      {latestPurchase?.initiated_by_admin && staffView && (
                        <span className="rounded-full border border-brand-400/30 bg-brand-500/10 px-2 py-0.5 text-[10px] text-brand-300">
                          Initiated by admin
                        </span>
                      )}
                    </div>
                  );
                })()}
                {flightLegs.map((leg, index) => {
                  const firstFlight = leg[0];
                  
                  // Get marketing cabin fare name (e.g., "Main Select", "Comfort+", etc.)
                  const getCabinClass = () => {
                    const flightData = firstFlight.flightData || firstFlight.flight_data;
                    // bookingCabin comes from the bookings.cabin_class column — authoritative source.
                    // searchCabin is what the user originally searched for — reliable fallback.
                    const bookingCabin = firstFlight?.cabinClass || firstFlight?.cabin_class;
                    const searchCabin = flightData?._searchParams?.cabin_class;
                    const marketingName = getMarketingFareName({
                      flightData,
                      flight_data: flightData,
                      cabinClass: bookingCabin || searchCabin,
                      cabin_class: bookingCabin || searchCabin,
                    });
                    const fallbackSegment = firstFlight.sliceData?.segments?.[0];
                    const displayCabin = marketingName ||
                      fallbackSegment?.passengers_cabin_class_marketing_name ||
                      fallbackSegment?.cabin_class_marketing_name ||
                      bookingCabin ||
                      searchCabin ||
                      fallbackSegment?.cabin_class ||
                      'Economy';
                    return formatCabinDisplay(displayCabin);
                  };
                  
                  const getFlightNumbers = () => {
                    if (firstFlight.sliceData) {
                      const numbers = firstFlight.sliceData.segments
                        ?.map(seg => seg.marketing_carrier_flight_number)
                        .filter(Boolean);
                      return numbers && numbers.length > 0 ? numbers.join(', ') : null;
                    }
                    return null;
                  };
                  
                  const cabinClass = getCabinClass();
                  const flightNumbers = getFlightNumbers();
                  
                  // Check if we have sliceData available
                  if (firstFlight.sliceData) {
                    const legFlightData = firstFlight.flightData || firstFlight.flight_data || {};
                    // Use the original slice data for FlightOfferCard, plus booking metadata
                    // so baggage icons reflect the real fare (SerpAPI booking_option or Duffel).
                    const offer = {
                      slices: [firstFlight.sliceData],
                      owner: firstFlight.sliceData.segments?.[0]?.marketing_carrier,
                      bookingOption: legFlightData.bookingOption || legFlightData.booking_option,
                      flightData: legFlightData,
                    };
                    
                    // Extract aircraft and flight numbers for THIS leg only
                    const aircraftTypes = new Set();
                    const legFlightNumbers = new Set();
                    firstFlight.sliceData.segments?.forEach(segment => {
                      if (segment.aircraft?.name) {
                        aircraftTypes.add(segment.aircraft.name);
                      }
                      if (segment.marketing_carrier_flight_number) {
                        legFlightNumbers.add(segment.marketing_carrier_flight_number);
                      }
                    });
                    
                    const aircraftText = Array.from(aircraftTypes).join(', ');
                    const flightNumbersText = legFlightNumbers.size > 0 ? ` • ${Array.from(legFlightNumbers).join(', ')}` : '';
                    
                    // Build per-segment seat map for FlightOfferCard
                    const buildSegmentSeatMap = () => {
                      const seatAssignments = firstFlight.flightData?.seat_assignments || {};
                      // Use passengers from component state (loaded separately)
                      const segmentSeatMap = {};
                      
                      // For each segment in this leg, build array of {passengerName, seat}
                      firstFlight.sliceData.segments?.forEach(segment => {
                        const segmentId = segment.id;
                        if (!segmentId) return;
                        
                        const seatData = [];
                        
                        passengers.forEach(passenger => {
                          const passengerId = passenger.id;
                          const passengerSeats = seatAssignments[passengerId] || {};
                          const seat = passengerSeats[segmentId];
                          
                          if (seat) {
                            seatData.push(seat);
                          }
                        });
                        
                        if (seatData.length > 0) {
                          segmentSeatMap[segmentId] = seatData;
                        }
                      });
                      
                      return segmentSeatMap;
                    };
                    
                    const segmentSeatAssignments = buildSegmentSeatMap();
                    
                    return (
                      <div key={`leg-${index}`} className="space-y-3">
                        {/* Flight Date Header */}
                        <div className="flex items-center gap-1 sm:gap-2 text-xs text-white/60 min-w-0 flex-shrink">
                          <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                          <span className="whitespace-nowrap">{index === 0 ? 'Outbound' : 'Return'}:</span>
                          <span className="text-white font-normal whitespace-nowrap truncate">
                            {firstFlight.departure_date ? format(parseISO(firstFlight.departure_date), 'MMM d, yyyy') : 'N/A'}
                          </span>
                        </div>
                        <FlightOfferCard 
                          offer={offer} 
                          hidePrice={true}
                          isReturnJourney={index > 0}
                          segmentSeatAssignments={segmentSeatAssignments}
                          gradientBottomColor="#0A1233"
                        />
                        
                        {/* Per-Segment Aircraft, Cabin Class, and Seat Assignments */}
                        {firstFlight.sliceData.segments?.map((segment, segIdx) => {
                          const flightData = firstFlight.flightData || firstFlight.flight_data;
                          const bookingCabin = firstFlight?.cabinClass || firstFlight?.cabin_class;
                          const searchCabin = flightData?._searchParams?.cabin_class;
                          const marketingName = getMarketingFareName({
                            flightData,
                            flight_data: flightData,
                            cabinClass: bookingCabin || searchCabin,
                            cabin_class: bookingCabin || searchCabin,
                          });
                          const displayClass = formatCabinDisplay(
                            segment.passengers_cabin_class_marketing_name ||
                            segment.cabin_class_marketing_name ||
                            marketingName ||
                            bookingCabin ||
                            searchCabin ||
                            segment.cabin_class
                          );

                          const seatData = segmentSeatAssignments[segment.id];
                          const seatMapKey = `${index}-${segIdx}`;
                          const isSeatMapOpen = openSeatMapKey === seatMapKey;
                          
                          return (
                            <div key={segIdx} className="space-y-2">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-4 text-xs text-white/60 font-normal min-w-0">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <Plane className="w-3.5 h-3.5 flex-shrink-0" />
                                  <span className="truncate">
                                    {segment.aircraft?.name || 'Aircraft'} • {segment.marketing_carrier_flight_number || 'Flight'}
                                  </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pl-5 sm:pl-0 min-w-0 sm:flex-shrink-0">
                                  <SegmentSeatChips seats={seatData} />
                                  {displayClass && (
                                    <span className="sm:whitespace-nowrap text-white/90 font-medium">
                                      {displayClass}
                                    </span>
                                  )}
                                  {SEAT_MAPS_ENABLED && (
                                    <button
                                      type="button"
                                      onClick={() => setOpenSeatMapKey(isSeatMapOpen ? null : seatMapKey)}
                                      className="flex items-center gap-1 text-white/50 hover:text-white/90 transition-colors"
                                    >
                                      <Armchair className="w-3.5 h-3.5" />
                                      <span className="underline underline-offset-2 decoration-white/30">Seat map</span>
                                      <ChevronDown className={cn('w-3 h-3 transition-transform', isSeatMapOpen && 'rotate-180')} />
                                    </button>
                                  )}
                                </div>
                              </div>
                              {isSeatMapOpen && SEAT_MAPS_ENABLED && (
                                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 sm:p-4">
                                  <React.Suspense
                                    fallback={
                                      <div className="flex items-center justify-center gap-2 py-10 text-xs text-white/50">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Loading seat map…</span>
                                      </div>
                                    }
                                  >
                                    <BookedSeatMapViewer
                                      segment={segment}
                                      cabinClass={bookingCabin || searchCabin || segment.cabin_class}
                                      departureDate={firstFlight.departure_date}
                                      passengers={passengers}
                                      seatAssignments={firstFlight.flightData?.seat_assignments || firstFlight.flight_data?.seat_assignments || {}}
                                    />
                                  </React.Suspense>
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Live flight status, the inbound plane, airport delays,
                            the flight number's track record (FlightLegLive). */}
                        <FlightLegLive bookingId={firstFlight.bookingId || firstFlight.id} sliceIndex={firstFlight.sliceIndex} />

                        {/* Flight alert texts, once, under the trip's last leg. */}
                        {index === flightLegs.length - 1 && (
                          <FlightAlertTextsSwitch suggestedPhone={passengers[0]?.phoneNumber || ''} />
                        )}
                      </div>
                    );
                  }
                  
                  // Fallback to original rendering if no sliceData
                  const lastFlight = leg[leg.length - 1];
                  const numberOfStops = leg.length - 1;
                  
                  // Build seat assignments for fallback path
                  const buildFallbackSeatAssignments = () => {
                    const seatAssignments = firstFlight.flightData?.seat_assignments || {};
                    const allSeats = [];
                    
                    // Collect all seat numbers from all passengers for this leg
                    passengers.forEach(passenger => {
                      const passengerId = passenger.id;
                      const passengerSeats = seatAssignments[passengerId] || {};
                      // Get all seats for this passenger (across all segments)
                      Object.values(passengerSeats).forEach(seat => {
                        if (seat && seat.trim()) {
                          allSeats.push(seat.trim());
                        }
                      });
                    });
                    
                    return [...new Set(allSeats)]; // Remove duplicates
                  };
                  
                  const fallbackSeats = buildFallbackSeatAssignments();

                  return (
                    <div key={`leg-${index}`} className="space-y-3">
                      {/* Flight Date Header */}
                      <div className="flex items-center gap-1 sm:gap-2 text-xs text-white/60 min-w-0 flex-shrink">
                        <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                        <span className="whitespace-nowrap">{index === 0 ? 'Outbound' : 'Return'}:</span>
                        <span className="text-white font-normal whitespace-nowrap truncate">
                          {firstFlight.departure_date ? format(parseISO(firstFlight.departure_date), 'MMM d, yyyy') : 'N/A'}
                        </span>
                      </div>
                      
                      <div className={`${index > 0 ? 'border-t border-white/10 pt-4' : ''}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-medium text-white">{getCityName(firstFlight.departure_city)} → {getCityName(lastFlight.arrival_city)}</p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                              {numberOfStops === 0 ? 'Nonstop' : `${numberOfStops} stop${numberOfStops > 1 ? 's' : ''}`}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="text-center">
                            <p className="text-base font-bold text-white">{formatTime12Hour(firstFlight.departure_time)}</p>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{getAirportCode(firstFlight.departure_city)}</p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{firstFlight.departure_date ? format(parseISO(firstFlight.departure_date), 'MMM d') : ''}</p>
                          </div>

                          <div className="flex-1 mx-4 flex flex-col items-center justify-center gap-1">
                            <div className="w-full h-px bg-white/20 relative">
                              <Plane className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                            </div>
                            {numberOfStops > 0 && (
                              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                {leg.slice(0, -1).map((flight, i) => {
                                  const connection = leg[i + 1];
                                  const duration = connection.connection_duration;
                                  return `${getAirportCode(flight.arrival_city)}${duration ? ` (${duration})` : ''}`;
                                }).join(' → ')}
                              </div>
                            )}
                          </div>

                          <div className="text-center">
                            <p className="text-base font-bold text-white">{formatTime12Hour(lastFlight.arrival_time)}</p>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{getAirportCode(lastFlight.arrival_city)}</p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{lastFlight.arrival_date ? format(parseISO(lastFlight.arrival_date), 'MMM d') : ''}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Aircraft and Flight Numbers - Per Leg (fallback path) */}
                      {(flightNumbers || cabinClass || fallbackSeats.length > 0) && (
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-4 text-xs text-white/60 font-normal min-w-0">
                          {flightNumbers && (
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Plane className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="truncate">{flightNumbers}</span>
                            </div>
                          )}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pl-5 sm:pl-0 min-w-0 sm:flex-shrink-0">
                            <SegmentSeatChips seats={fallbackSeats} />
                            {cabinClass && (
                              <span className="sm:whitespace-nowrap text-white/90 font-medium">
                                {cabinClass}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Original vs New itinerary (archived after Change Flight) */}
              {(() => {
                const archived = trip?.[0]?.archived_flight_data || trip?.[0]?.archivedFlightData;
                const current = trip?.[0]?.flightData || trip?.[0]?.flight_data || {};
                const history = trip?.[0]?.change_flight_history || trip?.[0]?.changeFlightHistory || [];
                const historyArr = Array.isArray(history) ? history : [];
                const hasMultiple = historyArr.length > 1;
                const selectedIdx = hasMultiple && archivedComparisonMode === 'oldest' ? 0 : Math.max(0, historyArr.length - 1);
                const selectedEntry = historyArr[selectedIdx] || null;
                const origFlightData = selectedEntry?.archived_flight_data || archived;
                const newFlightData = selectedIdx < historyArr.length - 1
                  ? (historyArr[selectedIdx + 1]?.archived_flight_data)
                  : current;
                const getPrevCents = () => {
                  if (selectedEntry?.previous_grand_total_cents != null && selectedEntry.previous_grand_total_cents > 0) return selectedEntry.previous_grand_total_cents;
                  const creditCents = selectedEntry?.flight_credit_cents ?? (trip?.[0]?.flightData?._change_flight_credit_cents ?? trip?.[0]?.flight_data?._change_flight_credit_cents ?? 0);
                  const newCentsVal = selectedEntry?.new_grand_total_cents ?? Math.round((trip?.[0]?.grand_total ?? trip?.[0]?.grandTotal ?? 0) * 100);
                  if (creditCents > 0) return newCentsVal + creditCents;
                  const ps = origFlightData?.pricing_snapshot;
                  if (ps) {
                    const gt = parseFloat(ps.grand_total || 0);
                    if (gt > 0) return Math.round(gt * 100);
                    const base = parseFloat(ps.original_base_fare || 0);
                    const svc = parseFloat(ps.service_fee || 0);
                    const hold = parseFloat(ps.hold_fee || 0);
                    if (base > 0 || svc > 0) return Math.round((base + svc + hold) * 100);
                  }
                  const archGt = parseFloat(origFlightData?.grand_total || origFlightData?.total_amount || 0);
                  if (archGt > 0) return Math.round(archGt * 100);
                  return 0;
                };
                const prevCents = getPrevCents();
                const newCents = selectedEntry?.new_grand_total_cents ?? Math.round((trip?.[0]?.grand_total ?? trip?.[0]?.grandTotal ?? 0) * 100);
                const diffCents = newCents - prevCents;
                // Base-fare deltas. History entries from change-flight write
                // both `previous_base_fare_cents` and `new_base_fare_cents`
                // directly; fall back to the archived/current flight_data for
                // legacy rows where those columns weren't recorded.
                const getPrevBaseCents = () => {
                  if (selectedEntry?.previous_base_fare_cents != null && selectedEntry.previous_base_fare_cents > 0) {
                    return selectedEntry.previous_base_fare_cents;
                  }
                  const ps = origFlightData?.pricing_snapshot;
                  if (ps) {
                    const base = parseFloat(ps.original_base_fare || 0);
                    if (base > 0) return Math.round(base * 100);
                  }
                  const archBase = parseFloat(origFlightData?.base_amount ?? origFlightData?.total_amount ?? 0);
                  if (archBase > 0) return Math.round(archBase * 100);
                  return 0;
                };
                const getNewBaseCents = () => {
                  if (selectedEntry?.new_base_fare_cents != null && selectedEntry.new_base_fare_cents > 0) {
                    return selectedEntry.new_base_fare_cents;
                  }
                  const fdBase = parseFloat(newFlightData?.base_amount ?? newFlightData?.total_amount ?? 0);
                  if (fdBase > 0) return Math.round(fdBase * 100);
                  const bookingTotal = parseFloat(trip?.[0]?.total_amount ?? 0);
                  if (bookingTotal > 0) return Math.round(bookingTotal * 100);
                  return 0;
                };
                const prevBaseCents = getPrevBaseCents();
                const newBaseCents = getNewBaseCents();
                const baseDiffCents = newBaseCents - prevBaseCents;
                const fmt = (c) => (c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                const hasNewSlices = (newFlightData?.slices?.length || 0) > 0;
                const renderSliceCard = (slice, idx, fd) => {
                  const segment = slice.segments?.[0];
                  const lastSegment = slice.segments?.[slice.segments?.length - 1];
                  if (!segment) return null;
                  const mc = segment.marketing_carrier;
                  // Name-first, same as getAirlineLogoFromAPI above: the stored
                  // logo URL stays on the OLD carrier after an admin airline rename.
                  const sliceLogo = resolveCarrierLogo(mc);
                  const stops = (slice.segments?.length || 1) - 1;
                  // Use the booking row's cabin (mainFlight.cabinClass) as highest priority;
                  // fd.cabin_class can be stale (set from original search, not actual fare).
                  const bookingCabin = mainFlight?.cabinClass || mainFlight?.cabin_class;
                  const marketingFare = getMarketingFareName({
                    flight_data: fd,
                    cabinClass: bookingCabin || fd?.cabin_class,
                    cabin_class: bookingCabin || fd?.cabin_class,
                  });
                  const cabinDisplay = formatCabinDisplay(
                    segment.passengers_cabin_class_marketing_name ||
                    segment.cabin_class_marketing_name ||
                    marketingFare ||
                    bookingCabin ||
                    segment.cabin_class
                  );
                  return (
                    <div key={idx} className="mb-5 last:mb-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white/60 text-xs font-normal uppercase">
                          {idx === 0 ? 'Departing' : 'Return'} • {segment.departing_at ? format(parseISO(segment.departing_at), 'EEE, MMM d') : '—'}
                        </span>
                        {stops > 0 && <span className="text-brand-400/80 text-[10px] uppercase">{stops} stop{stops > 1 ? 's' : ''}</span>}
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 flex justify-center overflow-hidden">
                            {sliceLogo ? (
                              <img src={sliceLogo} alt="" className="w-full h-full object-contain" />
                            ) : (
                              <PlaneTakeoff className="w-5 h-5 text-white/40 m-auto" />
                            )}
                          </div>
                          <div>
                            <div className="text-white text-sm font-medium">
                              {segment.departing_at ? format(parseISO(segment.departing_at), 'h:mm a') : ''} – {lastSegment?.arriving_at ? format(parseISO(lastSegment.arriving_at), 'h:mm a') : ''}
                            </div>
                            <div className="text-white/40 text-xs">
                              {mc?.name}{segment.marketing_carrier_flight_number ? ` ${segment.marketing_carrier_flight_number}` : ''}
                            </div>
                            {cabinDisplay && <div className="text-white/30 text-xs capitalize mt-0.5">{cabinDisplay}</div>}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-white/50 text-xs uppercase">
                            {segment.origin?.iata_code}–{lastSegment?.destination?.iata_code}
                          </div>
                          <div className="text-white/60 text-xs">{formatSliceDuration(slice.duration)}</div>
                        </div>
                      </div>
                    </div>
                  );
                };
                if (!archived?.slices?.length) return null;
                return (
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <button
                      type="button"
                      onClick={() => setShowArchivedItinerary((s) => !s)}
                      className="flex items-center gap-2 text-white/50 hover:text-white/70 text-sm font-normal mb-4"
                    >
                      <ArrowRight className={`w-4 h-4 transition-transform ${showArchivedItinerary ? 'rotate-90' : ''}`} />
                      Original vs new itinerary
                    </button>
                    {showArchivedItinerary && (
                      <div className="space-y-6">
                        {hasMultiple && (
                          <div className="flex items-center gap-2">
                            <span className="text-white/50 text-xs">View:</span>
                            <div className="flex rounded-lg border border-white/10 overflow-hidden">
                              <button
                                type="button"
                                onClick={() => setArchivedComparisonMode('oldest')}
                                className={`px-3 py-1.5 text-xs font-medium transition-colors ${archivedComparisonMode === 'oldest' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white/70 hover:bg-white/5'}`}
                              >
                                Oldest
                              </button>
                              <button
                                type="button"
                                onClick={() => setArchivedComparisonMode('newest')}
                                className={`px-3 py-1.5 text-xs font-medium transition-colors ${archivedComparisonMode === 'newest' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white/70 hover:bg-white/5'}`}
                              >
                                Newest
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                            <p className="text-white/40 text-xs uppercase tracking-wider mb-4">Original itinerary</p>
                            {(origFlightData?.slices || []).map((slice, idx) => renderSliceCard(slice, idx, origFlightData))}
                          </div>
                          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                            <p className="text-white/40 text-xs uppercase tracking-wider mb-4">New itinerary</p>
                            {hasNewSlices ? (newFlightData?.slices || []).map((slice, idx) => renderSliceCard(slice, idx, newFlightData)) : (
                              <p className="text-white/40 text-sm">Current booking</p>
                            )}
                          </div>
                        </div>
                        {staffView && (
                          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-4">
                            <p className="text-white/40 text-xs uppercase tracking-wider">Price comparison</p>
                            <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                              <div>
                                <span className="text-white/50 text-xs block">Original total</span>
                                <p className="text-white text-lg font-medium">${fmt(prevCents)}</p>
                              </div>
                              <div className="text-white/30">→</div>
                              <div>
                                <span className="text-white/50 text-xs block">New total</span>
                                <p className="text-white text-lg font-medium">${fmt(newCents)}</p>
                              </div>
                              <div className="ml-auto text-right">
                                {diffCents > 0 ? (
                                  <span className="text-brand-400 text-sm tabular-nums font-normal">+${fmt(diffCents)}</span>
                                ) : diffCents < 0 ? (
                                  <span className="text-teal-400 text-sm tabular-nums font-normal">−${fmt(Math.abs(diffCents))}</span>
                                ) : (
                                  <span className="text-white/60 text-sm">No change</span>
                                )}
                              </div>
                            </div>
                            {/* Base-fare comparison (admin-only). Surfaces the
                                raw ticket-cost movement separately from the
                                grand-total movement so admins can see how the
                                airline-side price changed before service & hold
                                fees are layered on. */}
                            {(prevBaseCents > 0 || newBaseCents > 0) && (
                              <div className="flex flex-wrap items-center gap-4 sm:gap-6 border-t border-white/10 pt-4">
                                <div>
                                  <span className="text-white/50 text-xs block">Original base fare</span>
                                  <p className="text-white/90 text-base font-normal">${fmt(prevBaseCents)}</p>
                                </div>
                                <div className="text-white/30">→</div>
                                <div>
                                  <span className="text-white/50 text-xs block">New base fare</span>
                                  <p className="text-white/90 text-base font-normal">${fmt(newBaseCents)}</p>
                                </div>
                                <div className="ml-auto text-right">
                                  {baseDiffCents > 0 ? (
                                    <span className="text-brand-400 text-sm tabular-nums font-normal">+${fmt(baseDiffCents)}</span>
                                  ) : baseDiffCents < 0 ? (
                                    <span className="text-teal-400 text-sm tabular-nums font-normal">−${fmt(Math.abs(baseDiffCents))}</span>
                                  ) : (
                                    <span className="text-white/60 text-sm">No change</span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Admins: the trip's travel insurance right under the Flights card,
                so an insured trip is obvious at a glance (founder, 2026-10-23).
                Customers keep it after the hotel. */}
            {viewerIsAdmin && insuranceCard}

            {/* Hotel Details - Show when stay data exists (matches Carts/EditCart UI) */}
            {stayData && stayData.name && (
              <motion.div
                className="bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 backdrop-blur-sm border border-brand-800/30 rounded-3xl overflow-hidden"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.25 }}
              >
                <SectionBand
                  icon={Hotel}
                  iconColor="text-brand-400"
                  accentRgb="45,212,191"
                  title="Hotel Details"
                />
                <div className="p-6 space-y-4">
                  {/* Ops only: is this room actually booked with the hotel?
                      Nothing here is customer-facing — `stays.status` is the
                      record of an admin judgement, and it is what takes a trip
                      out of the self-serve 100%-credit cancel path. */}
                  {(() => {
                    if (!staffView) return null;
                    const canSetStayStatus = isAdmin(currentUser);
                    const status = stayStatusOverride || stayData.status || 'pending';
                    const TONE = {
                      reserved: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25',
                      needs_cancellation: 'bg-rose-500/10 text-rose-300 border-rose-500/25',
                      cancelled: 'bg-white/[0.04] text-white/50 border-white/10',
                      not_needed: 'bg-white/[0.04] text-white/50 border-white/10',
                      pending: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
                    };
                    const LABEL = {
                      reserved: 'Booked with hotel',
                      needs_cancellation: 'CANCEL WITH HOTEL',
                      cancelled: 'Cancelled',
                      not_needed: 'Not needed',
                      pending: 'Not booked yet',
                    };
                    return (
                      <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 space-y-2.5">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-white/40 text-[10px] uppercase tracking-[0.14em] font-normal">Ops · room status</span>
                            <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${TONE[status] || TONE.pending}`}>
                              {LABEL[status] || status}
                            </span>
                          </div>
                          {stayData.supplierConfirmation && (
                            <span className="text-white/45 text-[11.5px] font-normal tabular-nums">#{stayData.supplierConfirmation}</span>
                          )}
                        </div>
                        {canSetStayStatus && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="text"
                            value={stayConfirmationInput}
                            onChange={(e) => setStayConfirmationInput(e.target.value)}
                            placeholder="Hotel confirmation #"
                            aria-label="Hotel confirmation number"
                            className="h-8 w-44 rounded-lg bg-black/30 border border-white/15 px-2.5 text-[12.5px] text-white placeholder:text-white/30 focus:outline-none focus:border-emerald-400/50"
                          />
                          <button
                            type="button"
                            onClick={() => setStayStatus('reserved')}
                            disabled={isSavingStayStatus}
                            className="h-8 px-3 rounded-full bg-white text-black text-[12.5px] font-medium hover:bg-white/90 transition-colors disabled:opacity-50"
                          >
                            Mark booked
                          </button>
                          <button
                            type="button"
                            onClick={() => setStayStatus('cancelled')}
                            disabled={isSavingStayStatus}
                            className="h-8 px-3 rounded-full bg-white/[0.06] border border-white/10 text-white/70 text-[12.5px] font-normal hover:bg-white/[0.1] transition-colors disabled:opacity-50"
                          >
                            Mark cancelled
                          </button>
                          <button
                            type="button"
                            onClick={() => setStayStatus('not_needed')}
                            disabled={isSavingStayStatus}
                            className="h-8 px-3 rounded-full bg-white/[0.06] border border-white/10 text-white/70 text-[12.5px] font-normal hover:bg-white/[0.1] transition-colors disabled:opacity-50"
                          >
                            Not needed
                          </button>
                        </div>
                        )}
                        <p className="text-white/35 text-[11px] font-normal leading-snug">
                          {status === 'reserved'
                            ? 'Booked on our end, so cancelling this trip routes to the team instead of paying out 100% automatically.'
                            : 'Not booked on our end, so cancelling this trip pays the customer 100% of what they have paid as Defera Credit.'}
                          {stayData.isRefundable === false ? ' This rate is non-refundable.' : ''}
                        </p>
                      </div>
                    );
                  })()}
                  {/* Hotel Name, Address, Rating, Hotel Class - matches EditCart */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-light text-lg mb-1">{stayData.name}</h4>
                      {stayData.address && (
                        <p className="text-sm text-white/60 font-normal">{stayData.address}</p>
                      )}
                      {stayData.rating && (
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex items-center gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3 h-3 ${
                                  i < Math.floor(stayData.rating.outOfFive)
                                    ? 'fill-brand-400 text-brand-400'
                                    : i < stayData.rating.outOfFive
                                    ? 'fill-brand-400/50 text-brand-400'
                                    : 'fill-none text-white/20'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-white/60">
                            {stayData.rating.label} {stayData.reviews ? `(${stayData.reviews} reviews)` : ''}
                          </span>
                        </div>
                      )}
                    </div>
                    {stayData.hotelClass && (
                      <Badge className="bg-brand-500/20 text-brand-400 border-brand-500/30 flex-shrink-0">
                        {stayData.hotelClass}-Star
                      </Badge>
                    )}
                  </div>

                  {/* Room Type, Bed Type - matches EditCart */}
                  {(stayData.roomType || stayData.bedType) && (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {stayData.roomType && (
                        <div>
                          <p className="text-white/60 font-normal mb-1">Room Type</p>
                          <p className="text-white font-normal capitalize">{stayData.roomType}</p>
                        </div>
                      )}
                      {stayData.bedType && (
                        <div>
                          <p className="text-white/60 font-normal mb-1">Bed Type</p>
                          <p className="text-white font-normal capitalize">{stayData.bedType}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Check-in/Check-out - use parseISO to avoid timezone shift (matches Flight Details) */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {(stayData.check_in || stayData.checkInDate) && (
                      <div>
                        <p className="text-white/60 font-normal mb-1">Check-in</p>
                        <p className="text-white font-normal">{format(parseISO(stayData.check_in || stayData.checkInDate), 'MMM d, yyyy')}</p>
                      </div>
                    )}
                    {(stayData.check_out || stayData.checkOutDate) && (
                      <div>
                        <p className="text-white/60 font-normal mb-1">Check-out</p>
                        <p className="text-white font-normal">{format(parseISO(stayData.check_out || stayData.checkOutDate), 'MMM d, yyyy')}</p>
                      </div>
                    )}
                  </div>

                  {/* Nights and Price */}
                  <div className="flex items-center justify-between pt-3 border-t border-white/10">
                    <div>
                      <p className="text-white/60 font-normal text-sm mb-1">Duration</p>
                      <p className="text-white font-normal">{stayData.nights ?? '—'} {stayData.nights === 1 ? 'night' : 'nights'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white/60 font-normal text-sm mb-1">Hotel Total</p>
                      <p className="text-white font-light text-lg">${parseMonetaryValue(stayData.totalAmount ?? stayData.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      {(stayData.nightly_rate ?? stayData.nightlyRate) && (
                        <p className="text-white/60 font-normal text-xs">${parseMonetaryValue(stayData.nightly_rate ?? stayData.nightlyRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/night</p>
                      )}
                    </div>
                  </div>

                  {/* Amenities */}
                  {stayData.amenities && stayData.amenities.length > 0 && (
                    <div className="pt-3 border-t border-white/10">
                      <p className="text-white/60 font-normal text-sm mb-2">Amenities</p>
                      <div className="flex flex-wrap gap-1.5">
                        {stayData.amenities.slice(0, 6).map((amenity, index) => (
                          <span
                            key={index}
                            className="text-xs px-2 py-1 rounded-full bg-white/[0.03] text-white/60 border border-white/[0.08] font-normal"
                          >
                            {typeof amenity === 'object' ? (amenity?.name || amenity?.label || JSON.stringify(amenity)) : amenity}
                          </span>
                        ))}
                        {stayData.amenities.length > 6 && (
                          <span className="text-xs px-2 py-1 rounded-full bg-white/[0.03] text-white/60 border border-white/[0.08] font-normal">
                            +{stayData.amenities.length - 6} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Travel Insurance for customers: after the hotel, as before. Admins
                get the same card directly under the Flights card (above). */}
            {!viewerIsAdmin && insuranceCard}
            {/* No insurance yet: the trip page can add it while Faye's window
                is open. The server decides whether this trip can (and renders
                nothing when it can't). */}
            {FAYE_INSURANCE_ENABLED && isDeferaCapital && insurancePremiumCents === 0 && !customerInsurance && !insuranceAddedAfterBooking(mainFlight)
              && !['cancelled', 'defaulted', 'delinquent'].includes(String(tripStatus).toLowerCase()) && bookingId && (
              <AddTripInsuranceCard bookingId={String(bookingId)} onAdded={() => onUpdate?.()} />
            )}

            {/* Seat Assignments Editing Section (Admin Only) */}
            {isEditingSeats && currentUser?.role === 'admin' && trip[0]?.flightData?.slices && (
              <motion.div
                className="bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 backdrop-blur-sm border border-brand-800/30 rounded-3xl overflow-hidden"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.15 }}
              >
                <SectionBand
                  icon={Users}
                  iconColor="text-brand-400"
                  accentRgb="167,139,250"
                  title="Edit Seat Assignments"
                />
                <div className="p-6">
                {trip[0].flightData.slices.map((slice, sliceIndex) => (
                  <div key={sliceIndex} className="bg-white/5 rounded-2xl p-4 border border-white/10 space-y-3 mb-4">
                    <p className="text-white/80 font-medium text-sm">
                      {sliceIndex === 0 ? 'Outbound' : 'Return'} Flight
                    </p>
                    {slice.segments?.map((segment, segIndex) => {
                      const segmentId = segment.id || `segment-${sliceIndex}-${segIndex}`;
                      const seatAssignments = trip[0].flightData.seat_assignments || {};
                      
                      return (
                        <div key={segIndex} className="bg-white/5 rounded-xl p-3 space-y-2 border border-white/10">
                          <p className="text-white text-sm font-normal">
                            Segment {segIndex + 1}: {segment.origin?.iata_code || 'N/A'} → {segment.destination?.iata_code || 'N/A'}
                          </p>
                          <p className="text-white/60 text-xs">
                            {segment.marketing_carrier?.name || 'N/A'} {segment.marketing_carrier_flight_number || ''}
                          </p>
                          
                          {/* Passenger Seat Inputs */}
                          <div className="space-y-2 mt-2">
                            {passengers.map((passenger, passengerIndex) => {
                              const passengerId = passenger.id || `passenger-${passengerIndex}`;
                              const currentSeat = seatAssignments[passengerId]?.[segmentId] || '';
                              
                              return (
                                <div key={passengerIndex} className="flex items-center gap-2">
                                  <label className="text-xs text-white/60 font-normal w-32 truncate">
                                    {passenger.given_name || passenger.givenName} {passenger.family_name || passenger.familyName}:
                                  </label>
                                  <Input
                                    value={editedSeatNumbers[passengerId]?.[segmentId] ?? currentSeat}
                                    onChange={(e) => {
                                      setEditedSeatNumbers(prev => ({
                                        ...prev,
                                        [passengerId]: {
                                          ...(prev[passengerId] || {}),
                                          [segmentId]: e.target.value.toUpperCase()
                                        }
                                      }));
                                    }}
                                    placeholder="e.g., 12A"
                                    className="bg-white/5 border-white/10 text-white h-8 text-sm max-w-[100px]"
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleSaveSeatAssignments}
                    disabled={isSavingSeats}
                    className="flex-1 bg-primary hover:bg-primary/90"
                  >
                    {isSavingSeats ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> Save Seat Assignments</>
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      setIsEditingSeats(false);
                      setEditedSeatNumbers({});
                    }}
                    variant="outline"
                    disabled={isSavingSeats}
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20"
                  >
                    Cancel
                  </Button>
                </div>
                </div>
              </motion.div>
            )}

            {/* Passenger Information Section */}
            {/* Ops record — admins and the concierge agent. Six fields, and the
                server names every one of them; everything else about a booking
                stays in the admin edit modal. */}
            {canEditTripOps(currentUser) && (
              <div className="mb-4">
                <TripOpsPanel
                  trip={trip}
                  onSaved={() => onUpdate?.()}
                  canRemoveConfirmation={isAdmin(currentUser)}
                  canSetMoneyStatus={isAdmin(currentUser)}
                />
              </div>
            )}
            {/* What the trip was priced on (migration 201): admins only, read-only,
                closed by default; amber when its travelers don't match the fare. */}
            {isAdmin(currentUser) && (
              <div className="mb-4">
                <PriceReceiptCard trip={trip} />
              </div>
            )}
            <motion.div
              className="bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 backdrop-blur-sm border border-brand-800/30 rounded-3xl overflow-hidden"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <SectionBand
                icon={Users}
                iconColor="text-brand-400"
                accentRgb="34,211,238"
                title="Passengers"
                rightSlot={
                  currentUser?.role === 'admin' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPassengerEdit(true)}
                      className="h-7 px-2.5 text-[11px] font-normal bg-white/5 border-white/10 text-white/80 hover:bg-white/10 hover:border-white/20 rounded-full gap-1.5"
                    >
                      <Edit className="w-3 h-3" />
                      Edit
                    </Button>
                  ) : viewerOwnsTrip && !['cancelled', 'defaulted'].includes(tripStatus) ? (
                    /* Customers can't edit a booked trip's travelers
                       (migration 193), so their Edit only ever failed. The
                       concierge sends the fix to the team instead, with the
                       trip pulled up and the message started. */
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(supportChatPath({ bookingId: mainFlight?.id, topic: 'traveler-correction' }))}
                      className="h-7 px-2.5 text-[11px] font-normal bg-white/5 border-white/10 text-white/80 hover:bg-white/10 hover:border-white/20 rounded-full gap-1.5"
                    >
                      <MessageCircle className="w-3 h-3" />
                      Request a correction
                    </Button>
                  ) : null
                }
              />
              <div className="p-6">
              {isLoading ? (
                <p className="text-sm text-white/60 font-normal">Loading passenger details...</p>
              ) : passengers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {passengers.map((passenger) => {
                    // Handle both camelCase and snake_case field names
                    const phone = passenger.phoneNumber || passenger.phone_number || passenger.phone || '';
                    const email = passenger.email || '';
                    const passportNumber = passenger.passportNumber || passenger.passport_number || '';
                    const passportExpiry = passenger.passportExpiryDate || passenger.passport_expiry_date || passenger.passport_expiry || '';
                    const frequentFlyerProgram = passenger.frequentFlyerProgram || passenger.frequent_flyer_program || '';
                    const frequentFlyerNumber = passenger.frequentFlyerNumber || passenger.frequent_flyer_number || '';
                    const knownTravelerNumber = passenger.knownTravelerNumber || passenger.known_traveler_number || '';
                    const specialRequests = passenger.specialRequests || passenger.special_requests || '';
                    const seatPreferenceLabel = getSeatPreferenceLabel(passenger.seatPreference || passenger.seat_preference);
                    

                    /** Passenger full name without title — used as the copy
                     *  payload (just the actual identity, not the honorific). */
                    const passengerFullName = [
                      passenger.firstName || passenger.first_name || '',
                      passenger.middleName || passenger.middle_name || '',
                      passenger.lastName || passenger.last_name || ''
                    ].map(s => s.trim()).filter(Boolean).join(' ');
                    /** DOB formatted MM/DD/YY for both display and copy. The
                     *  short slash format is what airline check-in / call
                     *  centers ask for verbatim, so making display === copy
                     *  payload avoids a "they look different" UX gotcha. */
                    const dobFormatted = passenger.bornOn
                      ? (() => {
                          try { return format(parseISO(passenger.bornOn), 'MM/dd/yy'); }
                          catch { return null; }
                        })()
                      : null;
                    return (
                      <div key={passenger.id} className="bg-black/20 backdrop-blur-sm border border-white/10 rounded-2xl p-5">
                        <div className="flex items-start gap-3">
                          <UserIcon className="w-4 h-4 mt-1 flex-shrink-0 text-white/40" />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-white mb-2 flex items-center gap-1.5 flex-wrap">
                              <span>
                                {passenger.title ? `${passenger.title} ` : ''}{passengerFullName}
                              </span>
                              {passengerFullName && (
                                <CopyChip
                                  fieldKey={`pax-${passenger.id}-name`}
                                  value={passengerFullName}
                                  copiedField={copiedField}
                                  onCopy={handleCopyField}
                                  ariaLabel="Copy passenger name"
                                />
                              )}
                            </p>
                            <div className="space-y-0.5">
                              {dobFormatted && (
                                <p className="text-xs text-white/60 font-normal flex items-center gap-1.5 flex-wrap">
                                  <span>DOB: {dobFormatted}</span>
                                  <CopyChip
                                    fieldKey={`pax-${passenger.id}-dob`}
                                    value={dobFormatted}
                                    copiedField={copiedField}
                                    onCopy={handleCopyField}
                                    ariaLabel="Copy date of birth"
                                  />
                                </p>
                              )}
                              {passenger.gender && (
                                <p className="text-xs text-white/60 font-normal">
                                  Gender: {passenger.gender.charAt(0).toUpperCase() + passenger.gender.slice(1)}
                                </p>
                              )}
                              {passenger.nationality && (
                                <p className="text-xs text-white/60 font-normal">
                                  Nationality: {passenger.nationality}
                                </p>
                              )}
                              {canViewTripInternals(currentUser) && travelRequirement.international && (() => {
                                const who = displayFirstName(passenger.firstName || passenger.first_name);
                                const { passport, flags } = travelerVisaFlags({
                                  table: travelRequirement.visaTable,
                                  nationality: passenger.nationality,
                                  passportCountry: passenger.passportCountry || passenger.passport_country,
                                  stops: travelRequirement.stops,
                                });
                                const expiryIssue = passportExpiryIssue(passportExpiry, travelRequirement.lastTravelDate);
                                const missing = missingTravelDocument({
                                  nationality: passenger.nationality,
                                  passport_country: passenger.passportCountry || passenger.passport_country,
                                  passport_number: passportNumber,
                                  passport_expiry: passportExpiry,
                                }, travelRequirement).filter((k) => k !== 'passport_expired');
                                const notes = [
                                  ...flags.filter((f) => f.needsAction || f.status === 'visa_on_arrival').map((f) => visaFlagMessage(f, who)),
                                  ...(expiryIssue ? [passportExpiryMessage(expiryIssue, who)] : []),
                                  ...(missing.length ? [`Missing for this international trip: ${missing.map((k) => ({ nationality: 'nationality', passport_country: 'issuing country', passport_number: 'passport number', passport_expiry: 'passport expiration' })[k] || k).join(', ')}.`] : []),
                                ];
                                return (
                                  <div className="mt-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 space-y-1">
                                    <p className="text-[10px] uppercase tracking-[0.18em] text-white/40">
                                      Travel check{passport ? '' : ' (nationality unknown)'}
                                    </p>
                                    {notes.length ? notes.map((note) => (
                                      <p key={note} className="text-xs text-amber-200/90 font-normal leading-relaxed">{note}</p>
                                    )) : (
                                      <p className="text-xs text-emerald-200/80 font-normal">{passport ? 'No visa flags for this trip.' : 'Add a nationality to check visas.'}</p>
                                    )}
                                  </div>
                                );
                              })()}
                              {phone && (
                                <p className="text-xs text-white/60 font-normal flex items-center gap-1.5 flex-wrap">
                                  <span>Phone: {phone}</span>
                                  <CopyChip
                                    fieldKey={`pax-${passenger.id}-phone`}
                                    value={phone}
                                    copiedField={copiedField}
                                    onCopy={handleCopyField}
                                    ariaLabel="Copy phone number"
                                  />
                                </p>
                              )}
                              {email && (
                                <p className="text-xs text-white/60 font-normal flex items-center gap-1.5 flex-wrap break-all">
                                  <span>Email: {email}</span>
                                  <CopyChip
                                    fieldKey={`pax-${passenger.id}-email`}
                                    value={email}
                                    copiedField={copiedField}
                                    onCopy={handleCopyField}
                                    ariaLabel="Copy email"
                                  />
                                </p>
                              )}
                              {passportNumber && (
                                <p className="text-xs text-white/60 font-normal">
                                  Passport: {passportNumber}
                                </p>
                              )}
                              {passportExpiry && (
                                <p className="text-xs text-white/60 font-normal">
                                  Passport Expiry: {(() => {
                                    try {
                                      if (passportExpiry.includes('-')) {
                                        return format(parseISO(passportExpiry), 'MMM d, yyyy');
                                      }
                                      return passportExpiry;
                                    } catch {
                                      return passportExpiry;
                                    }
                                  })()}
                                </p>
                              )}
                              {(frequentFlyerProgram || frequentFlyerNumber) && (
                                <p className="text-xs text-white/60 font-normal">
                                  Frequent Flyer: {frequentFlyerProgram ? `${getProgramLabel(frequentFlyerProgram)}` : ''}{frequentFlyerProgram && frequentFlyerNumber ? ' • ' : ''}{frequentFlyerNumber || ''}
                                </p>
                              )}
                              {knownTravelerNumber && (
                                <p className="text-xs text-white/60 font-normal">
                                  Known Traveler #: {knownTravelerNumber}
                                </p>
                              )}
                              {seatPreferenceLabel && (
                                <p className="text-xs text-white/60 font-normal flex items-center gap-1.5">
                                  <Armchair className="w-3 h-3 text-[hsl(228,35%,58%)]/80 flex-shrink-0" />
                                  Seat Preference: {seatPreferenceLabel}
                                </p>
                              )}
                              {specialRequests && (
                                <p className="text-xs text-white/60 font-normal">
                                  Special Requests: {specialRequests}
                                </p>
                              )}
                            </div>
                            {/* Airline wheelchair questions: the trip's owner
                                answers and changes them, admins see them (and
                                can record them), everyone else reads. */}
                            {needsWheelchairQuestions(passenger) && (
                              <WheelchairAssistanceCard
                                passenger={passenger}
                                canEdit={viewerOwnsTrip || isAdmin(currentUser)}
                                viewerIsOwner={viewerOwnsTrip}
                                closed={['cancelled', 'canceled', 'refunded', 'defaulted'].includes(String(tripStatus).toLowerCase())}
                                onSaved={handleWheelchairSaved}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-white/60 font-normal">No passenger information available.</p>
              )}
              </div>
            </motion.div>

            {/* Defera Miles + Payment Schedule - 2 columns on desktop */}
            {(() => {
              const showDeferaMiles = true;
              const isReservePayment = paymentMethod === 'reserve';
              const isDeferaZero = paymentMethod === 'defera_zero';
              const isStripeInstallmentPlan = isDeferaZero || isDeferaCapital;
              const existingPaymentSchedule = mainFlight.flightData?.payment_schedule || mainFlight.flight_data?.payment_schedule || null;
              const canCompute =
                !!grandTotalWithHold &&
                (mainFlight.departure_date || mainFlight.departureDate);
              const hasPaymentSchedule = !isReservePayment && (existingPaymentSchedule || canCompute);
              // Defera Zero forces 0%; Defera Capital gets the lowest DC tier (5%) — the schedule
              // calculator's `respectStoredDepositPercentage: false` default for this synthesized
              // schedule still snaps up to the band that fits the trip's actual lead time, so this
              // value matters only for far-out DC trips where 5% is genuinely available.
              const fallbackSchedule = {
                deposit_percentage: isDeferaZero ? 0 : (isDeferaCapital ? 5 : 20),
                payment_plan: isStripeInstallmentPlan
                  ? (mainFlight.flightData?._payment_frequency || mainFlight.flight_data?._payment_frequency || 'weekly')
                  : 'weekly',
                booking_date: mainFlight.created_at || mainFlight.createdAt,
                departure_date: mainFlight.departure_date || mainFlight.departureDate
              };

              const showRightColumn = hasPaymentSchedule || showDeferaMiles || staffView;
              if (!hasPaymentSchedule && !showRightColumn) return null;

              /**
               * Defera Miles card is rendered in two different positions depending
               * on whether the trip still has an active payment schedule:
               *   - hasPaymentSchedule=true  → right column, stacked above Booked
               *     By + Cost Breakdown (Payment Schedule fills the left slot)
               *   - hasPaymentSchedule=false → left column on its own, so the 2-col
               *     desktop layout is preserved instead of collapsing to one wide
               *     stack of cards. Defined once here and re-used below to avoid
               *     duplicating ~140 lines of JSX. */
              const deferaMilesCard = showDeferaMiles ? (
                <motion.div
                  className="relative bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 backdrop-blur-sm border border-brand-800/30 rounded-3xl overflow-hidden flex-shrink-0"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.35 }}
                >
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'radial-gradient(ellipse 70% 55% at 88% 5%, rgba(15, 25, 82,0.12), transparent 60%), radial-gradient(ellipse 60% 50% at 10% 100%, rgba(10, 18, 51,0.06), transparent 65%)',
                    }}
                  />
                  <SectionBand
                    icon={Award}
                    iconColor="text-brand-400"
                    accentRgb="10, 18, 51"
                    title="Defera Miles"
                    subtitle="Rewards · redeem for this trip"
                  />
                  <div className="relative p-6">
                    {isMilesLoading ? (
                      <p className="text-sm text-white/60 font-normal">Loading…</p>
                    ) : (
                      <div className="space-y-5">
                        {/* Quiet supporting card: same signature-blue hue family as the
                            plan-action buttons (so it doesn't drift into navy/indigo) but at
                            low opacity — the balance is informational, not a primary action. */}
                        <div className="relative overflow-hidden rounded-2xl border border-brand-800/30 bg-gradient-to-b from-brand-900/45 via-brand-900/25 to-brand-800/20 px-5 py-5">
                          <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                              background:
                                'radial-gradient(ellipse 70% 80% at 90% 10%, rgba(15, 25, 82,0.14), transparent 60%)',
                            }}
                          />
                          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-300/30 to-transparent pointer-events-none" />
                          <div className="relative flex items-end justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <Sparkles className="w-3 h-3 text-brand-300" />
                                <p className="text-[10px] text-brand-200/80 uppercase tracking-widest font-medium">Available balance</p>
                              </div>
                              <p className="flex items-baseline gap-1.5">
                                <span className="text-4xl sm:text-5xl font-light bg-gradient-to-br from-white via-brand-100 to-brand-300 bg-clip-text text-transparent tracking-tight tabular-nums leading-none">
                                  {milesBalance.toLocaleString()}
                                </span>
                                <span className="text-white/50 text-sm font-normal">miles</span>
                              </p>
                            </div>
                            <Link
                              to="/rewards"
                              className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] text-brand-300/80 hover:text-brand-200 transition-colors font-normal uppercase tracking-wider"
                            >
                              View
                              <ArrowRight className="w-3 h-3" />
                            </Link>
                          </div>
                        </div>

                        <div>
                          <p className="text-[11px] text-white/50 uppercase tracking-widest font-normal mb-3">
                            {tripStatus === 'completed' ? 'Save for future trips' : 'Redeem on this trip'}
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                            {(() => {
                              const isCompleted = tripStatus === 'completed';
                              const tiles = [
                                { id: 'seats',   icon: Armchair,     label: 'Buy Seats',     miles: MILES_TILE_MINIMUMS.seats },
                                { id: 'baggage', icon: Luggage,      label: 'Add Baggage',   miles: MILES_TILE_MINIMUMS.baggage },
                                { id: 'cabin',   icon: ArrowUpCircle, label: 'Cabin Upgrade', miles: MILES_TILE_MINIMUMS.cabin },
                              ];
                              return tiles.map(({ id, icon: TileIcon, label, miles }) => {
                                const insufficient = isCompleted || milesBalance < miles;
                                const affordable = !insufficient;
                                return (
                                  <button
                                    key={id}
                                    type="button"
                                    disabled={insufficient}
                                    onClick={() => affordable && openMilesAddOnSmsCompose(id)}
                                    className={cn(
                                      'group relative overflow-hidden flex flex-col items-center justify-center py-5 px-3 rounded-2xl border transition-all duration-200',
                                      affordable
                                        ? 'bg-gradient-to-b from-brand-500/[0.08] to-brand-950/[0.06] border-brand-500/25 hover:border-brand-400/45 hover:from-brand-500/[0.12] hover:to-brand-950/[0.08] hover:-translate-y-px hover:shadow-[0_4px_18px_-6px_rgba(15, 25, 82,0.25)]'
                                        : 'bg-white/[0.02] border-white/[0.06] cursor-not-allowed opacity-50',
                                    )}
                                  >
                                    {affordable && (
                                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-300/30 to-transparent pointer-events-none" />
                                    )}
                                    <TileIcon
                                      className={cn(
                                        'w-6 h-6 mb-2 transition-transform duration-300',
                                        affordable ? 'text-brand-300 group-hover:scale-110' : 'text-white/30',
                                      )}
                                    />
                                    <span
                                      className={cn(
                                        'font-normal text-sm text-center',
                                        affordable ? 'text-white' : 'text-white/40',
                                      )}
                                    >
                                      {label}
                                    </span>
                                    <span
                                      className={cn(
                                        'text-[10px] font-medium mt-1 uppercase tracking-wider tabular-nums',
                                        affordable ? 'text-brand-200/80' : 'text-white/30',
                                      )}
                                    >
                                      from {miles.toLocaleString()}
                                    </span>
                                  </button>
                                );
                              });
                            })()}
                          </div>
                          <Link
                            to="/rewards"
                            className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-brand-200/70 hover:text-brand-200 transition-colors font-medium uppercase tracking-widest"
                          >
                            <Sparkles className="w-3 h-3" />
                            Explore all rewards
                            <ArrowRight className="w-3 h-3" />
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ) : null;

              return (
            <div className={`grid gap-6 md:items-stretch ${showRightColumn ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
              {/* Anything the customer asked Tommy that his manager handed
                  to the team. This is the receipt for the "we'll come back
                  to you within 24 hours" promise Tommy makes in chat.
                  Spans both columns and renders nothing when there's
                  nothing outstanding. */}
              <div className={showRightColumn ? 'md:col-span-2' : ''}>
                <ServiceInquiryCard
                  bookingReference={
                    trip[0]?.booking_reference
                    || trip[0]?.bookingReference
                    || trip[0]?.trip_reference
                    || trip[0]?.tripReference
                    || null
                  }
                />
              </div>
              {/* Payment Schedule (left column on desktop) */}
              {hasPaymentSchedule && (
                  /** The fill-height chain (stretch -> flex-1/min-h-0 down to the lists)
                   *  is DESKTOP-ONLY. On phones the grid row is content-sized, so
                   *  the chain buys nothing - and a shrinkable column with a
                   *  visibly-overflowing history list let the "Finished paying?"
                   *  strip land on top of the last installment row on iOS once
                   *  the schedule data arrived after first layout (13Q84U,
                   *  2026-09-09). No percentage heights either: grid items
                   *  stretch to the row on their own. */
                  <motion.div
                    className="flex flex-col md:min-h-[560px]"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.35 }}
                  >
                    <div className="flex flex-col md:flex-1 md:min-h-0">
                    {isNativeApp && !online ? (
                      <div role="status" className="rounded-3xl border border-white/10 bg-black/20 p-6 flex items-start gap-3">
                        <WifiOff className="w-5 h-5 text-white/60 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <div>
                          <p className="text-base text-white font-medium">Payment schedule</p>
                          <p className="text-sm text-white/70 font-normal mt-1">Your payments will load when you&apos;re back online.</p>
                        </div>
                      </div>
                    ) : (
                    <PaymentScheduleDisplay
                      paymentSchedule={existingPaymentSchedule || fallbackSchedule}
                      tripStatus={tripStatus}
                      totalWithFee={grandTotalWithHold}
                      flightCreditCents={mainFlight?.flightData?._change_flight_credit_cents ?? mainFlight?.flight_data?._change_flight_credit_cents ?? 0}
                      changeFlightHistory={mainFlight?.change_flight_history ?? mainFlight?.changeFlightHistory ?? []}
                      archivedFlightData={mainFlight?.archived_flight_data ?? mainFlight?.archivedFlightData ?? null}
                      currentFlightData={mainFlight?.flightData ?? mainFlight?.flight_data ?? {}}
                      currentGrandTotalCents={Math.round((mainFlight?.grand_total ?? mainFlight?.grandTotal ?? 0) * 100)}
                      insurancePremiumCents={insurancePremiumCents}
                      tripId={mainFlight.id || mainFlight.bookingId || bookingId || trip[0]?.booking_reference || trip[0]?.bookingReference}
                      bookingDate={mainFlight.created_at || mainFlight.createdAt}
                      departureDate={mainFlight.departure_date || mainFlight.departureDate}
                      tripReference={trip[0]?.booking_reference || trip[0]?.bookingReference || trip[0]?.trip_reference || trip[0]?.tripReference || ''}
                      userName={
                        tripUser?.firstName && tripUser?.lastName
                          ? `${tripUser.firstName} ${tripUser.lastName}`
                          : tripUser?.firstName || tripUser?.lastName
                          || (currentUser?.firstName && currentUser?.lastName
                            ? `${currentUser.firstName} ${currentUser.lastName}`
                            : currentUser?.firstName || currentUser?.email || 'Customer')
                      }
                      userEmail={tripUser?.email || null}
                      tripOwnerUserId={tripUser?.id || null}
                      route={(() => {
                        const flightData = mainFlight?.flightData || mainFlight?.flight_data || {};
                        const slices = flightData.slices || [];
                        if (slices.length > 0) {
                          const firstSlice = slices[0];
                          const origin = firstSlice.segments?.[0]?.origin?.iata_code || mainFlight.departure_city;
                          const destination = firstSlice.segments?.[firstSlice.segments.length - 1]?.destination?.iata_code || mainFlight.arrival_city;
                          return `${getCityName(origin)} → ${getCityName(destination)}`;
                        }
                        return '';
                      })()}
                      onExtensionRequested={async () => {
                        if (onUpdate) await onUpdate();
                      }}
                      onScheduleUpdated={async () => {
                        if (onUpdate) await onUpdate();
                      }}
                      onInternalCommentAdded={async () => {
                        if (trip?.[0]?.id) {
                          const commentData = await InternalComment.findByBookingId(trip[0].id);
                          setComments(commentData);
                        }
                      }}
                      cardClassName="flex flex-col md:flex-1 md:min-h-0 md:[&>:last-child]:flex-1 md:[&>:last-child]:min-h-0"
                      fillHeight={true}
                      isDeferaZero={isDeferaZero}
                      isDeferaCapital={isDeferaCapital}
                      /* The concierge desk reads the schedule — whether a trip is
                         paid off is most of what a customer's question turns on —
                         but never moves money: no pay, change, skip or payoff
                         buttons. The endpoints behind those buttons refuse this
                         role anyway; hiding them keeps it from trying. */
                      showActions={!(isConcierge(currentUser) && !isAdmin(currentUser))}
                      deferaZeroPaymentSuccessBookingId={deferaZeroPaymentSuccessBookingId}
                      onDeferaZeroPaymentSuccessHandled={onDeferaZeroPaymentSuccessHandled}
                      onTripPaidClick={
                        // Owner, or an admin acting on the customer's behalf
                        // (endpoint files the inquiry FOR the customer —
                        // same act-on-behalf pattern as seat purchase).
                        tripOwnerId === currentUser?.id || currentUser?.role === 'admin'
                          ? handleFinishedPaying
                          : undefined
                      }
                      tripPaidBusy={isSubmittingTripPaid}
                    />
                    )}
                    </div>
                  </motion.div>
              )}

              {/* Left column fallback when there's no payment schedule (e.g. the trip
                  has been fully paid). Putting Defera Miles here keeps the 2-column
                  desktop layout instead of letting all the right-column cards collapse
                  to a single full-width stack. When a payment schedule exists, this
                  branch is skipped and Defera Miles renders inside the right column
                  instead (below). */}
              {!hasPaymentSchedule && deferaMilesCard}

              {/* Right column: Defera Miles + User Information + Cost Breakdown stacked (desktop) */}
              {showRightColumn && (
                  <div className="flex flex-col gap-6 md:min-h-[560px]">
              {hasPaymentSchedule && deferaMilesCard}
              {/* User Information - below Defera Miles in same column (staff: admin + concierge) */}
              {staffView && (
                <motion.div
                  className="bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 backdrop-blur-sm border border-brand-800/30 rounded-3xl overflow-hidden flex-shrink-0"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.22 }}
                >
                  <SectionBand
                    icon={UserIcon}
                    iconColor="text-brand-400"
                    accentRgb="251,113,133"
                    title="Booked By"
                  />
                  <div className="p-6">
                  {isLoadingUser ? (
                    <p className="text-sm text-white/60 font-normal">Loading user details...</p>
                  ) : tripUser ? (
                    <div className="grid grid-cols-1 gap-4">
                      <div className="bg-black/20 backdrop-blur-sm border border-white/10 rounded-2xl p-5">
                        <div className="flex items-start gap-3">
                          <UserIcon className="w-4 h-4 mt-1 flex-shrink-0 text-white/40" />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-white mb-2 flex items-center gap-1.5 flex-wrap">
                              {currentUser?.role === 'admin' && tripUser.id ? (
                                <button
                                  type="button"
                                  onClick={() => navigate(createPageUrl(`UserDetails?id=${tripUser.id}`))}
                                  className="text-left hover:underline hover:text-[var(--accent-color)] transition-colors cursor-pointer"
                                >
                                  {tripUser.firstName && tripUser.lastName 
                                    ? `${tripUser.firstName} ${tripUser.lastName}`
                                    : tripUser.firstName || tripUser.lastName || 'N/A'}
                                </button>
                              ) : (
                                <span>
                                  {tripUser.firstName && tripUser.lastName 
                                    ? `${tripUser.firstName} ${tripUser.lastName}`
                                    : tripUser.firstName || tripUser.lastName || 'N/A'}
                                </span>
                              )}
                              {(tripUser.firstName || tripUser.lastName) && (
                                <CopyChip
                                  fieldKey="bookedby-name"
                                  value={`${tripUser.firstName || ''} ${tripUser.lastName || ''}`.trim()}
                                  copiedField={copiedField}
                                  onCopy={handleCopyField}
                                  ariaLabel="Copy user name"
                                />
                              )}
                              <VipBadge userId={tripUser.id || null} />
                            </p>
                            <div className="space-y-0.5">
                              {tripUser.email && (
                                <p className="text-xs text-white/60 font-normal flex items-center gap-1.5 flex-wrap break-all">
                                  <span>Email: {tripUser.email}</span>
                                  <CopyChip
                                    fieldKey="bookedby-email"
                                    value={tripUser.email}
                                    copiedField={copiedField}
                                    onCopy={handleCopyField}
                                    ariaLabel="Copy email"
                                  />
                                </p>
                              )}
                              {tripUser.id && (
                                <p className="text-xs text-white/60 font-normal">
                                  User ID: {tripUser.id}
                                </p>
                              )}
                              {(() => {
                                // VIP: suggest a complimentary upgrade on an active trip still ahead.
                                const fd = flightData;
                                const departs = Date.parse(fd?.slices?.[0]?.segments?.[0]?.departing_at || '');
                                const active = ['confirmed', 'completed'].includes(String(tripStatus || '').toLowerCase())
                                  && Number.isFinite(departs) && departs > Date.now();
                                return <VipUpgradeNudge userId={tripUser.id || null} upgraded={Boolean(fd.is_upgraded)} active={active} />;
                              })()}
                              {tripUser.role && (
                                <p className="text-xs text-white/60 font-normal">
                                  Role: {tripUser.role.charAt(0).toUpperCase() + tripUser.role.slice(1)}
                                </p>
                              )}
                              {/** Trip booked date: the day the customer paid the deposit and the
                                *  booking row was materialized. `bookings.created_at` is stamped by
                                *  the webhook that runs after the deposit charge succeeds (Stripe
                                *  checkout.session.completed or NMI deposit-webhook), so it lines
                                *  up with "day they booked the trip" for both processors. Rendered
                                *  above Account Created so ops can eyeball recency vs. account age
                                *  without leaving the trip view. */}
                              {(mainFlight?.created_at || mainFlight?.createdAt) && (
                                <p className="text-xs text-white/60 font-normal">
                                  Trip Booked: {format(parseISO(mainFlight.created_at || mainFlight.createdAt), 'MMM d, yyyy')}
                                </p>
                              )}
                              {/** Admin ops metadata (whole Booked By card is admin-gated
                                *  above — these lines must never render for customers):
                                *  `confirmation_added_at` is stamped once by
                                *  /api/admin/update-booking the first time a confirmation
                                *  number lands on the booking, so it's absent on trips
                                *  confirmed before the stamp existed — omit the line then.
                                *  Booking Supplier reflects the "Agency" checkbox in Edit
                                *  Flight Details (flight_data.is_agency); unchecked means
                                *  Defera booked direct with the airline. */}
                              {flightData?.confirmation_added_at && (
                                <p className="text-xs text-white/60 font-normal">
                                  Confirmation Added: {format(parseISO(flightData.confirmation_added_at), 'MMM d, yyyy')}
                                </p>
                              )}
                              <p className="text-xs text-white/60 font-normal">
                                Booking Supplier: {flightData?.is_agency ? 'Agency' : 'Direct'}
                              </p>
                              {/** Free Flight Upgrade — the customer's standing answer to "may we
                                *  move you to a faster routing at no extra cost?" (required at
                                *  checkout). Ops acts on this weeks after purchase, so it has to be
                                *  readable from the trip rather than inferred.
                                *
                                *  Presence-checked, not truthiness-checked, and the distinction is
                                *  the whole point: bookings made before this shipped carry no field
                                *  at all, and rendering those as "No" would turn a question nobody
                                *  asked into a refusal the customer never gave. Absent = no line.
                                *
                                *  Scope is shown alongside because "Yes" alone isn't actionable —
                                *  same-airline-only vs any-airline is the difference between a move
                                *  the customer agreed to and one they didn't. Mirrors the read in
                                *  server/tommy-capabilities.js so the desk and this card can never
                                *  tell an admin two different things. */}
                              {Object.prototype.hasOwnProperty.call(flightData || {}, 'faster_flight_opt_in') && (
                                <p className="text-xs text-white/60 font-normal">
                                  Upgrade Allowed:{' '}
                                  {flightData.faster_flight_opt_in === true ? (
                                    <span className="text-emerald-300/90">
                                      Yes — {flightData.faster_flight_airline_pref === 'same'
                                        ? 'same airline only'
                                        : 'any airline'}
                                    </span>
                                  ) : (
                                    <span className="text-white/70">No</span>
                                  )}
                                </p>
                              )}
                              {/** Travel-purpose answers (2026-09-30), required at
                                *  checkout. Absent = the booking predates the
                                *  questions, which is why each line is rendered only
                                *  when its own key is present rather than defaulted
                                *  to "No" — a fabricated "No" on an old row is
                                *  indistinguishable from an answer the customer gave. */}
                              {flightData?.travel_purpose && (
                                <p className="text-xs text-white/60 font-normal">
                                  Purpose of Travel:{' '}
                                  <span className="text-white/80 capitalize">{flightData.travel_purpose}</span>
                                </p>
                              )}
                              {Object.prototype.hasOwnProperty.call(flightData || {}, 'traveling_with_others') && (
                                <p className="text-xs text-white/60 font-normal">
                                  Traveling With Others:{' '}
                                  {flightData.traveling_with_others === true ? (
                                    /* Highlighted on purpose: "yes, another Defera
                                       booking" is the one answer that asks someone to
                                       DO something — find the companion booking and
                                       seat the party together. */
                                    <span className="text-emerald-300/90">
                                      Yes — {flightData.companion_booking === 'defera'
                                        ? `another Defera booking${normalizeCompanionReference(flightData.companion_booking_reference) ? ` (${normalizeCompanionReference(flightData.companion_booking_reference)})` : ''}`
                                        : flightData.companion_booking === 'elsewhere'
                                          ? 'booked elsewhere'
                                          : 'booking not specified'}
                                    </span>
                                  ) : (
                                    <span className="text-white/70">No</span>
                                  )}
                                </p>
                              )}
                              {tripUser.createdAt && (
                                <p className="text-xs text-white/60 font-normal">
                                  Account Created: {format(parseISO(tripUser.createdAt), 'MMM d, yyyy')}
                                </p>
                              )}
                              {tripUser.referral_source && (
                                <p className="text-xs text-white/60 font-normal">
                                  Referral Source: {referralSourceLabel(tripUser.referral_source)}
                                </p>
                              )}
                              {/** Payment rail for this booking. `flight_data._payment_processor`
                                *  is the per-booking snapshot ('stripe' | 'nmi', migration 068) —
                                *  absent means the booking predates the NMI track and ran on
                                *  Stripe, same defaulting as the cancel/schedule endpoints.
                                *  STAFF-ONLY: the surrounding Booked By card is already gated,
                                *  but this line carries its own guard so it can never leak to
                                *  customers if the card is ever refactored or reused. */}
                              {staffView && (
                                <p className="text-xs text-white/60 font-normal">
                                  Processor: {flightData?._payment_processor === 'nmi' ? 'NMI' : 'Stripe'}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-white/60 font-normal">No user information available.</p>
                  )}
                  {/*
                    "View activity" deep link — opens /activity prefiltered to this
                    booking. ADMIN-ONLY (see migration 079): the Activity feed is an
                    internal audit tool, not a customer-facing feature. Surrounding
                    Booked By card is already gated to admins; this Link sits inside
                    that gate so it can never leak to customers.

                    We also pass `&user_id=<bookingOwnerId>` so when the admin lands
                    on /activity, the Edge function knows to scope to that customer
                    (instead of dropping into the cross-customer "all_users" view).
                    `tripUser.id` is populated for both the admin-viewing-customer
                    flow and the customer-viewing-own flow; if it's missing we just
                    pass `trip_id` and let the Edge fn resolve the owner via the
                    booking row.
                  */}
                  {(mainFlight?.id || mainFlight?.bookingId || bookingId) && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <Link
                        to={`/activity?trip_id=${encodeURIComponent(mainFlight?.id || mainFlight?.bookingId || bookingId)}${tripUser?.id ? `&user_id=${encodeURIComponent(tripUser.id)}` : ''}`}
                        className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white font-normal w-fit transition"
                      >
                        <ActivityIcon className="w-3.5 h-3.5" />
                        View activity for this trip
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  )}
                  </div>
                </motion.div>
              )}
              {/* Cost Breakdown - below User Information in same column */}
              <motion.div
                className="flex-shrink-0"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.35 }}
              >
                <div className="bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 backdrop-blur-sm border border-brand-800/30 rounded-3xl overflow-hidden">
                  {staffView ? (
                    <>
                      <SectionBand
                        icon={CreditCard}
                        iconColor="text-brand-400"
                        accentRgb="52,211,153"
                        title="Cost Breakdown"
                      />
                      <div className="p-6 space-y-1 text-sm font-normal">
                        {(isMilesOnly || isMilesInstallments || isReserve) && (
                          <div className="flex justify-between items-center py-1">
                            <span className="text-white/60">Payment</span>
                            <span className="text-white font-normal capitalize">
                              {paymentMethod === 'miles' ? 'Miles Only' : isMilesInstallments ? 'Miles + Installments' : isReserve ? 'Reserve' : paymentMethod}
                            </span>
                          </div>
                        )}
                        {isMilesOnly && displayMilesUsed > 0 && (
                          <div className="flex justify-between items-center py-1">
                            <span className="text-white/60">Paid with Defera Miles</span>
                            <span className="text-white font-normal tabular-nums">{displayMilesUsed.toLocaleString()} Miles</span>
                          </div>
                        )}
                        {isReserve && (() => {
                          const reserveCashCents = flightData._reserve_cash_payment_cents ?? flightData.reserve_cash_payment_cents ?? 0;
                          const reserveCashDollars = reserveCashCents / 100;
                          if (reserveCashCents > 0) {
                            const fromReserve = Math.max(0, grandTotalWithHold - reserveCashDollars);
                            return (
                              <>
                                <div className="flex justify-between items-center py-1">
                                  <span className="text-emerald-400/80">From Reserve</span>
                                  <span className="text-emerald-400 font-normal">${fromReserve.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                <div className="flex justify-between items-center py-1">
                                  <span className="text-white/60">Card Payment</span>
                                  <span className="text-white font-normal">${reserveCashDollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                              </>
                            );
                          }
                          return null;
                        })()}
                        {/* Show flight and hotel prices separately for Flights + Stays.
                            `flightPortionForDisplay` is derived from the stored booking
                            columns (see the derivation block above) rather than reading
                            `flightPrice` directly, because on the Slice Pay / Defera
                            Capital / DZ storage convention `mainFlight.total_amount`
                            already includes the hotel + fees + hold — reading it raw
                            here was what caused XFZXIZ to display $5,785.98 as the
                            "Flight" line and $5,785.98 as the "Subtotal" for a
                            $3,823.34 trip. */}
                        {hotelPrice > 0 ? (
                          <>
                            <div className="flex justify-between items-center py-1">
                              <span className="text-white/60">Flight</span>
                              <span className="text-white font-normal">${flightPortionExcludingSeats.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            {showSeatRow && (
                              <div className="flex justify-between items-center py-1">
                                <span className="text-white/60">Seat Selection{checkoutSeatCount > 1 ? ` (${checkoutSeatCount})` : ''}</span>
                                <span className="text-white font-normal">${checkoutSeatTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center py-1">
                              <span className="text-white/60">Hotel ({stayData?.nights || 0} {stayData?.nights === 1 ? 'night' : 'nights'})</span>
                              <span className="text-white font-normal">${hotelPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            {discountTotal > 0 && (
                              <div className="flex justify-between items-center py-1">
                                <span className="text-green-400/80">Discount ({bookingDiscount.code})</span>
                                <span className="text-green-400 font-normal">-${discountTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center border-t border-white/10 pt-3 mt-1">
                              <span className="text-white/70 font-medium">Subtotal</span>
                              <span className="text-white font-medium">${baseFareDisplay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            {!pricingEmbedded && (
                              <div className="flex justify-between items-center py-1">
                                <span className="text-white/60">Base Fare</span>
                                <span className="text-white font-normal">${baseFareExcludingSeats.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </div>
                            )}
                            {/* Financed seat picks. Sits directly under the fare it was
                                added to, above the fees that are charged on top of it. */}
                            {showSeatRow && (
                              <div className="flex justify-between items-center py-1">
                                <span className="text-white/60">Seat Selection{checkoutSeatCount > 1 ? ` (${checkoutSeatCount})` : ''}</span>
                                <span className="text-white font-normal">${checkoutSeatTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </div>
                            )}
                        {actualCostOverride > 0 && (
                          <div className="flex justify-between items-center py-1">
                            <span className="text-brand-400/80">Actual Cost</span>
                            <span className="text-brand-400 font-normal">${actualCostOverride.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {amountRefundedOverride > 0 && (
                          <div className="flex justify-between items-center py-1">
                            <span className="text-red-400/80">Amount Refunded</span>
                            <span className="text-red-400 font-normal">-${amountRefundedOverride.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                            {creditsTotal > 0 && (
                              <div className="flex justify-between items-center py-1">
                                <span className="text-green-400/80">Credits Applied</span>
                                <span className="text-green-400 font-normal">-${creditsTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </div>
                            )}
                            {discountTotal > 0 && (
                              <div className="flex justify-between items-center py-1">
                                <span className="text-green-400/80">Discount ({bookingDiscount.code})</span>
                                <span className="text-green-400 font-normal">-${discountTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </div>
                            )}
                            {(displayMilesUsed ?? 0) > 0 && (
                              <div className="flex justify-between items-center py-1">
                                <span className="text-brand-300/80">Miles Used</span>
                                <span className="text-brand-300 font-normal">{(displayMilesUsed ?? 0).toLocaleString()} miles</span>
                              </div>
                            )}
                            {(displayCashPortion ?? 0) > 0 && (
                              <div className="flex justify-between items-center py-1">
                                <span className="text-white/60">Cash Portion</span>
                                <span className="text-white font-normal">${(displayCashPortion ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </div>
                            )}
                          </>
                        )}
                        {!pricingEmbedded && (
                          <div className="flex justify-between items-center py-1">
                            <span className="text-white/60">Service Fee {hotelPrice > 0 ? '' : `(${actualServiceFeePercent}%)`}</span>
                            <span className={hotelPrice > 0 ? 'text-green-400 font-normal' : 'text-white font-normal'}>
                              {hotelPrice > 0 ? 'Waived' : isMilesOnly && !(serviceFee > 0) ? '—' : `$${serviceFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            </span>
                          </div>
                        )}
                        {/* Hold Fee: symmetric with Service Fee above. When a hotel is
                            present we always render the row (with the stored dollar
                            amount if the plan collected one, otherwise a "Waived" pill
                            matching the Service Fee UX). On flight-only bookings we
                            keep the pre-existing behavior of hiding the row entirely
                            when the fee is zero — that's the shape flight-only cost
                            breakdowns have always had. */}
                        {(holdFee > 0 || hotelPrice > 0) && !pricingEmbedded && (
                          <div className="flex justify-between items-center py-1">
                            <span className="text-white/60">Hold Fee {holdFee > 0 ? `(${actualHoldFeePercent}%)` : ''}</span>
                            <span className={holdFee > 0 ? 'text-white font-normal' : 'text-green-400 font-normal'}>
                              {holdFee > 0
                                ? `$${holdFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : 'Waived'}
                            </span>
                          </div>
                        )}
                        {extensionFeeLine > 0 && !pricingEmbedded && (
                          <div className="flex justify-between items-center py-1">
                            <span className="text-white/60">Extension Fee</span>
                            <span className="text-white font-normal">${extensionFeeLine.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {/* Faye premium: added after the fees and passed through to
                            Faye, so it never feeds the percentages above or Net Margin. */}
                        {insurancePremium > 0 && (
                          <div className="py-1">
                            <div className="flex justify-between items-center">
                              <span className="text-white/60">Travel Insurance ({tripInsurance?.label || 'Faye'})</span>
                              <span className="text-white font-normal">${insurancePremium.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <p className="text-white/40 text-[11px] font-normal">Paid to Faye; no service or hold fee</p>
                          </div>
                        )}
                        {/* Added after booking and charged upfront: its own Stripe or
                            NMI charge, outside the plan, so not in the Grand Total. */}
                        {insuranceUpfrontPremium > 0 && (
                          <div className="py-1">
                            <div className="flex justify-between items-center">
                              <span className="text-white/60">Travel Insurance ({tripInsurance?.label || 'Faye'})</span>
                              <span className="text-white font-normal">${money2(insuranceUpfrontPremium)}</span>
                            </div>
                            <p className="text-white/40 text-[11px] font-normal">Added after booking, paid upfront; not in the Grand Total</p>
                          </div>
                        )}
                        {/* Flight change cost — price difference charged upfront when the
                            customer changed to a costlier flight. Visible to everyone. */}
                        {flightChangeCostCents > 0 && (
                          <div className="flex justify-between items-center py-1">
                            <span className="text-white/60">
                              Flight change{flightChangeUpfrontCharges.length > 1 ? ` (${flightChangeUpfrontCharges.length}×)` : ''}
                            </span>
                            <span className="text-white font-normal">+${(flightChangeCostCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {flightChangeInsuranceCents > 0 && (
                          <div className="flex justify-between items-center py-1">
                            <span className="text-white/60">Travel insurance (flight change)</span>
                            <span className="text-white font-normal">+${(flightChangeInsuranceCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {/* Admin-only: Skip Payment fee revenue. Charged as independent one-off
                            PIs, so it's additive revenue on top of the Grand Total (which it is
                            intentionally NOT part of) and flows into Net Margin below. */}
                        {skipFeeRevenue > 0 && (
                          <div className="flex justify-between items-center py-1">
                            <span className="text-emerald-400/80">
                              Skip Fees{skipFeeEntries.length > 1 ? ` (${skipFeeEntries.length}×)` : ''}
                            </span>
                            <span className="text-emerald-400 font-normal">+${skipFeeRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {/* Post-booking seat purchase. Like Skip Fees above, this is an
                            independent Stripe charge that sits OUTSIDE the Grand Total
                            (the installment plan never bills it) but is real money on
                            both sides, so it flows into Net Margin below.

                            Both halves are shown because only the net is Defera's: the
                            customer's payment is mostly a pass-through to the airline.
                            Showing revenue alone would read like $43 of profit on a $2
                            fee. See src/utils/seatPurchaseEconomics.js. */}
                        {seatPurchaseMoney.hasMoney && (
                          <>
                            <div className="flex justify-between items-center py-1">
                              <span className="text-emerald-400/80">
                                Seat purchase{seatPurchaseMoney.seatCount > 1 ? ` (${seatPurchaseMoney.seatCount}×)` : ''}
                              </span>
                              <span className="text-emerald-400 font-normal">+${seatPurchaseMoney.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between items-center py-0.5 pl-3">
                              <span className="text-white/40 text-[12px]">Airline seat cost</span>
                              <span className="text-white/50 text-[12px] font-normal">−${seatPurchaseMoney.airlineCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between items-center py-0.5 pl-3">
                              <span className="text-white/40 text-[12px]">Defera keeps</span>
                              <span className="text-white/50 text-[12px] font-normal">+${seatPurchaseMoney.netToDefera.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </>
                        )}
                        {milesEarned > 0 && (
                          <div className="flex justify-between items-center py-1">
                            <span className="text-brand-400/60">Miles Liability</span>
                            <span className="text-brand-400 font-normal">${((milesEarned * MILES_LIABILITY_CENTS_PER_MILE) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {milesEarned > 0 && (
                          <div className="flex justify-between items-center py-1">
                            <span className="text-brand-400/60 flex items-center gap-2">
                              <Award className="w-4 h-4" />
                              Miles Earned
                            </span>
                            <span className="text-brand-400 font-normal">{milesEarned.toLocaleString()}</span>
                          </div>
                        )}
                        {netMarginAmount !== null && (
                          <div className="flex justify-between items-center border-t border-white/10 pt-3 mt-2">
                            <span className={netMarginAmount >= 0 ? 'text-emerald-400/80 text-sm' : 'text-red-400/80 text-sm'}>
                              Net Margin
                            </span>
                            <span className={netMarginAmount >= 0 ? 'text-emerald-400 font-normal' : 'text-red-400 font-normal'}>
                              {netMarginAmount >= 0 ? '+' : '−'}${Math.abs(netMarginAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        )}
                        {/* Admin-only: pre-booking fare-change P&L. Surfaces the
                            spread between the customer's locked-in Base Fare and
                            the new Base Fare for trips that have been changed
                            but not yet booked with the airline (no confirmation
                            #). Mirrors the Analytics "Profit / Loss from Fare"
                            metric at the per-trip level so admins can see the
                            realized delta directly on the trip card. Hidden
                            once `_actual_cost` is set — at that point Net
                            Margin above is the authoritative spread. */}
                        {fareChangeNetProfit !== null && (
                          <div
                            className="flex justify-between items-start border-t border-white/10 pt-3 mt-2"
                            title={`Customer Base Fare $${(fareChangeCustomerBase ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} − New Base Fare $${(fareChangeAnticipatedCost ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}. Realized the moment the trip is booked with the airline.`}
                          >
                            <div className="min-w-0">
                              <p className={fareChangeNetProfit >= 0 ? 'text-emerald-400/80 text-sm' : 'text-red-400/80 text-sm'}>
                                Net Profit (Fare Change)
                              </p>
                              <p className="text-white/40 text-[11px] font-normal mt-0.5">
                                Flight changed, not yet booked with airline
                              </p>
                            </div>
                            <span className={fareChangeNetProfit >= 0 ? 'text-emerald-400 font-normal whitespace-nowrap ml-3' : 'text-red-400 font-normal whitespace-nowrap ml-3'}>
                              {fareChangeNetProfit >= 0 ? '+' : '−'}${Math.abs(fareChangeNetProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        )}
                        {renderPurchasedBaggageCostLine()}
                        <div className="flex justify-between items-center border-t border-white/20 pt-4 mt-2">
                          <span className="font-medium text-white text-base">Grand Total</span>
                          <span className="text-xl font-light text-white">${planGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                      {/* Admin-only: Applied Credits — full reference + PIN + claimant
                          surfaced for redemption. Cost Breakdown shows the dollar total,
                          but admins need the actual numbers to redeem the eCredit/gift
                          card with the airline. Inner role check is defense-in-depth so
                          this block stays admin-only even if the surrounding ternary is
                          ever refactored. PY2RE8 was the canary trip where credits were
                          stored in flight_data.credits but never displayed. */}
                      {staffView && Array.isArray(flightData.credits) && flightData.credits.length > 0 && (
                        <div className="mt-5 pt-5 border-t border-white/10">
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <div>
                              <p className="text-white/60 text-xs uppercase tracking-widest font-normal">
                                Applied Credits ({flightData.credits.length})
                              </p>
                              <p className="text-white/40 text-[11px] font-normal mt-0.5">
                                Redemption credentials — admin only
                              </p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {flightData.credits.map((credit, idx) => {
                              const creditKey = credit.id || `credit-${idx}`;
                              const TypeIcon = credit.creditType === 'gift_card'
                                ? Gift
                                : credit.creditType === 'certificate'
                                ? CreditCard
                                : Ticket;
                              const refKey = `${creditKey}:reference`;
                              const pinKey = `${creditKey}:pin`;
                              return (
                                <div key={creditKey} className="bg-green-500/5 border border-green-500/15 rounded-2xl p-3">
                                  <div className="flex items-start justify-between gap-3 mb-2">
                                    <div className="flex items-start gap-2 min-w-0">
                                      <TypeIcon className="w-4 h-4 text-green-400/80 mt-0.5 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-white text-sm font-normal truncate">
                                          {credit.airlineName || credit.airlineCode || 'Airline'}
                                        </p>
                                        <p className="text-[11px] text-white/50 font-normal truncate">
                                          {credit.creditTypeLabel || credit.creditType || 'Credit'}
                                          {credit.status ? ` · ${String(credit.status).replace(/_/g, ' ')}` : ''}
                                        </p>
                                      </div>
                                    </div>
                                    <span className="text-green-400 font-normal text-sm whitespace-nowrap">
                                      −${(Number(credit.claimedAmount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                  <div className="space-y-1.5 text-[12px] font-normal pl-6">
                                    <div className="flex items-center justify-between gap-3">
                                      <span className="text-white/50 flex-shrink-0">Reference</span>
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-white font-mono text-right break-all">
                                          {credit.reference || '—'}
                                        </span>
                                        {credit.reference && (
                                          <button
                                            type="button"
                                            onClick={() => handleCopyField(refKey, credit.reference)}
                                            className="text-white/50 hover:text-white/90 flex-shrink-0"
                                            title="Copy reference"
                                          >
                                            {copiedField === refKey ? (
                                              <Check className="w-3.5 h-3.5 text-green-400" />
                                            ) : (
                                              <Copy className="w-3.5 h-3.5" />
                                            )}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {credit.pin && (
                                      <div className="flex items-center justify-between gap-3">
                                        <span className="text-white/50 flex-shrink-0">PIN</span>
                                        <div className="flex items-center gap-2 min-w-0">
                                          <span className="text-white font-mono text-right break-all">
                                            {credit.pin}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={() => handleCopyField(pinKey, credit.pin)}
                                            className="text-white/50 hover:text-white/90 flex-shrink-0"
                                            title="Copy PIN"
                                          >
                                            {copiedField === pinKey ? (
                                              <Check className="w-3.5 h-3.5 text-green-400" />
                                            ) : (
                                              <Copy className="w-3.5 h-3.5" />
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {credit.claimantLastName && (
                                      <div className="flex items-center justify-between gap-3">
                                        <span className="text-white/50 flex-shrink-0">Claimant Last Name</span>
                                        <span className="text-white text-right">{credit.claimantLastName}</span>
                                      </div>
                                    )}
                                    {credit.createdAt && (() => {
                                      let added = '';
                                      try {
                                        added = format(parseISO(credit.createdAt), 'MMM d, yyyy');
                                      } catch {
                                        added = new Date(credit.createdAt).toLocaleDateString();
                                      }
                                      return (
                                        <div className="flex items-center justify-between gap-3">
                                          <span className="text-white/50 flex-shrink-0">Added</span>
                                          <span className="text-white/70 text-right">{added}</span>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Admin-only: live SerpAPI price check vs booked Base Fare.
                          Outer guard: hotelPrice<=0 hides on Flight+Stay packages.
                          Inner guard: redundant role check is intentional defense-in-depth so
                          this block can never render for non-admins even if the surrounding
                          ternary is ever refactored. */}
                      {staffView && hotelPrice <= 0 && (
                        // Self-contained admin tool pill. Previously a `mt-5 pt-5 border-t` block
                        // that floated awkwardly at the bottom of the Cost Breakdown card; now
                        // wrapped in its own rounded panel with subtle bg/border so it reads as
                        // an intentional "Admin tool" rather than an orphaned section.
                        <div className="mt-5 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="text-white/70 text-xs uppercase tracking-widest font-normal">Live Price Check</p>
                              <p className="text-white/40 text-[11px] font-normal mt-0.5">Compare booked Base Fare with current Google Flights price</p>
                            </div>
                            {isAdmin(currentUser) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleCheckLivePrice}
                              disabled={isCheckingLivePrice}
                              className="self-start sm:self-auto flex-shrink-0 whitespace-nowrap text-white/80 hover:text-white hover:bg-white/10 border border-white/15 rounded-full px-4 h-8 text-xs font-normal"
                            >
                              {isCheckingLivePrice ? (
                                <>
                                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                  Checking…
                                </>
                              ) : livePriceCheck ? 'Refresh' : 'Check Current Price'}
                            </Button>
                            )}
                          </div>

                          {livePriceCheckError && !isCheckingLivePrice && (
                            <p className="mt-3 text-brand-400/90 text-xs font-normal">{livePriceCheckError}</p>
                          )}

                          {livePriceCheck && !isCheckingLivePrice && (
                            <div className="mt-4 space-y-1 text-sm font-normal">
                              {(livePriceCheck.passenger_count || livePriceCheck.cabin_class) && (
                                <p className="text-[11px] text-white/40 font-normal pb-1">
                                  Looked up for{' '}
                                  {livePriceCheck.passenger_count
                                    ? `${livePriceCheck.passenger_count} ${livePriceCheck.passenger_count === 1 ? 'passenger' : 'passengers'}`
                                    : ''}
                                  {livePriceCheck.cabin_class ? ` · ${formatCabinDisplay(livePriceCheck.cabin_class)}` : ''}
                                </p>
                              )}
                              {/* Fare-to-fare comparison: the Google Flights quote is a bare
                                  fare, so the booked side must exclude financed seat picks
                                  or the delta reads as a price move that never happened. */}
                              <div className="flex justify-between items-center py-1">
                                <span className="text-white/60">Base Fare (Booked)</span>
                                <span className="text-white font-normal">
                                  {livePriceCheck.booked_base_display ?? `$${baseFareExcludingSeats.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                </span>
                              </div>
                              <div className="flex justify-between items-center py-1">
                                <span className="text-white/60">Base Fare (Current)</span>
                                <span className="text-white font-normal">
                                  {livePriceCheck.current_price_display ?? '—'}
                                </span>
                              </div>
                              {livePriceCheck.matched && livePriceCheck.delta_cents != null && (
                                <div className="flex justify-between items-center border-t border-white/10 pt-2 mt-1">
                                  <span className="text-white/60 text-xs">Difference</span>
                                  <span
                                    className={
                                      livePriceCheck.price_dropped
                                        ? 'text-emerald-400 font-normal'
                                        : livePriceCheck.price_increased
                                        ? 'text-brand-400 font-normal'
                                        : 'text-white/70 font-normal'
                                    }
                                  >
                                    {livePriceCheck.delta_display}
                                  </span>
                                </div>
                              )}
                              {livePriceCheck.matched && (
                                <p className="text-[11px] text-white/40 font-normal pt-1">
                                  {livePriceCheck.price_dropped && 'Current fare is cheaper than what was booked.'}
                                  {livePriceCheck.price_same && 'Current fare matches the booked Base Fare.'}
                                  {livePriceCheck.price_increased && 'Current fare is higher than what was booked.'}
                                </p>
                              )}
                              {!livePriceCheck.matched && livePriceCheck.in_past && (
                                <p className="text-[11px] text-white/40 font-normal pt-1">Departure has already occurred — no live fare to compare.</p>
                              )}
                              {!livePriceCheck.matched && !livePriceCheck.in_past && (
                                <p className="text-[11px] text-white/40 font-normal pt-1">Exact flight not in current Google Flights results.</p>
                              )}
                              {livePriceCheck.source === 'manual' && (
                                <p className="text-[11px] text-white/50 font-normal pt-1">
                                  From a manual search{livePriceCheck.note ? ` · ${livePriceCheck.note}` : ''}
                                </p>
                              )}
                              {livePriceCheck.checked_at && (
                                <p className="text-[10px] text-white/30 font-normal pt-1">
                                  Last checked {(() => {
                                    try {
                                      return format(parseISO(livePriceCheck.checked_at), 'MMM d, yyyy h:mma');
                                    } catch {
                                      return new Date(livePriceCheck.checked_at).toLocaleString();
                                    }
                                  })()}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Persistent price history (newest first) — only shown when at
                              least one check has been recorded. Hidden during the initial
                              auto-load and on errors so the panel stays clean for fresh bookings. */}
                          {livePriceHistory.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-white/10">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-white/60 text-[11px] uppercase tracking-widest font-normal">
                                  Price History <span className="text-white/40 normal-case tracking-normal">({livePriceHistory.length})</span>
                                </p>
                                {livePriceHistory.length > 5 && (
                                  <button
                                    type="button"
                                    onClick={() => setLivePriceHistoryExpanded((v) => !v)}
                                    className="text-[11px] text-white/50 hover:text-white/80 font-normal underline-offset-2 hover:underline"
                                  >
                                    {livePriceHistoryExpanded ? 'Show less' : `Show all ${livePriceHistory.length}`}
                                  </button>
                                )}
                              </div>
                              <ul className="space-y-1">
                                {(livePriceHistoryExpanded ? livePriceHistory : livePriceHistory.slice(0, 5)).map((row) => {
                                  let dateStr = '';
                                  try {
                                    dateStr = format(parseISO(row.checked_at), 'MMM d, yyyy h:mma');
                                  } catch {
                                    dateStr = new Date(row.checked_at).toLocaleString();
                                  }
                                  const priceStr = row.current_price_display ?? '—';
                                  const deltaClass = row.price_dropped
                                    ? 'text-emerald-400'
                                    : row.price_increased
                                    ? 'text-brand-400'
                                    : 'text-white/60';
                                  const isManual = row.source === 'manual';
                                  return (
                                    <li key={row.id} className="flex items-baseline justify-between gap-3 py-1 text-[12px] font-normal">
                                      <span className="text-white/50 flex-shrink-0">{dateStr}</span>
                                      {isManual && (
                                        <span className="text-white/60 min-w-0 truncate" title={row.note || undefined}>
                                          Manual Search{row.note ? ` · ${row.note}` : ''}
                                        </span>
                                      )}
                                      <span className="flex items-baseline gap-2 ml-auto">
                                        <span className="text-white/80">{priceStr}</span>
                                        {row.matched && row.delta_display && (
                                          <span className={`${deltaClass} text-[11px]`}>{row.delta_display}</span>
                                        )}
                                        {!row.matched && (
                                          <span className="text-white/30 text-[11px]">{isManual ? 'other flights' : 'no match'}</span>
                                        )}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <SectionBand
                        icon={CreditCard}
                        iconColor="text-brand-400"
                        accentRgb="52,211,153"
                        title="Total Cost"
                      />
                      <div className="p-6 space-y-1 text-sm font-normal">
                        {isReserve && (() => {
                          const reserveCashCents = flightData._reserve_cash_payment_cents ?? flightData.reserve_cash_payment_cents ?? 0;
                          const reserveCashDollars = reserveCashCents / 100;
                          if (reserveCashCents > 0) {
                            const fromReserve = Math.max(0, grandTotalWithHold - reserveCashDollars);
                            return (
                              <>
                                <div className="flex justify-between items-center py-1">
                                  <span className="text-emerald-400/80">From Reserve</span>
                                  <span className="text-emerald-400 font-normal">${fromReserve.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                                <div className="flex justify-between items-center py-1">
                                  <span className="text-white/60">Card Payment</span>
                                  <span className="text-white font-normal">${reserveCashDollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                              </>
                            );
                          }
                          return null;
                        })()}
                        {milesEarned > 0 && (
                          <div className="flex justify-between items-center py-1">
                            <span className="text-brand-400/60 flex items-center gap-2">
                              <Award className="w-4 h-4" />
                              Miles Earned
                            </span>
                            <span className="text-brand-400 font-normal">{milesEarned.toLocaleString()}</span>
                          </div>
                        )}
                        {renderCheckoutSeatCostLine()}
                        {renderInsuranceCostLine()}
                        {renderPurchasedBaggageCostLine()}
                        <div className="flex justify-between items-center border-t border-white/20 pt-4 mt-2">
                          <span className="font-medium text-white text-base">Grand Total</span>
                          <span className="text-xl font-light text-white">${planGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
                  </div>
              )}
            </div>
            );
            })()}

            {mainFlight.itinerary_image_url && (
              <motion.div
                className="bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 backdrop-blur-sm border border-brand-800/30 rounded-3xl overflow-hidden"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <SectionBand
                  icon={ImageIcon}
                  iconColor="text-brand-400"
                  accentRgb="148,163,184"
                  title="Original Document"
                />
                <div className="p-6">
                  <Button
                    onClick={() => setShowImageOverlay(true)}
                    variant="outline"
                    className="w-full bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-11 font-normal"
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    View Itinerary
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Internal Comments — staff read them (admin + concierge, served by
                /api/admin/internal-comments); only admins add them (RLS). */}
            {(() => {
              const roles = currentUser?.app_metadata?.roles || [];
              const canComment = roles.includes('admin') || currentUser?.role === 'admin';
              return staffView && (
              <motion.div
                className="bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 backdrop-blur-sm border border-brand-800/30 rounded-3xl overflow-hidden"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.5 }}
              >
                <SectionBand
                  icon={MessageSquare}
                  iconColor="text-brand-400"
                  accentRgb="167,139,250"
                  title="Internal Comments"
                />
                <div className="p-6 space-y-4">
                  {canComment && (
                  <form onSubmit={handleCommentSubmit} className="space-y-3">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Leave an internal note for your team..."
                      className="bg-white/5 text-white border-white/10 focus:border-white/20 rounded-2xl font-normal placeholder:text-white/40"
                      rows={3}
                    />
                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        size="sm"
                        className="bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/30 h-9 font-normal"
                        disabled={isSubmittingComment || !newComment.trim()}
                      >
                        {isSubmittingComment ? 'Submitting...' : 'Add Comment'}
                      </Button>
                    </div>
                  </form>
                  )}

                  <div className="space-y-4 max-h-60 overflow-y-auto pr-2">
                    {comments.length > 0 ? (
                      comments.map(comment => {
                        // Get author from userMap or fallback to current user
                        let authorName = 'Admin';
                        const userFromMap = userMap.get(comment.userId) || userMap.get(comment.user_id);
                        
                        if (userFromMap && userFromMap.name) {
                          authorName = userFromMap.name;
                        } else if (comment.userId === currentUser?.id || comment.user_id === currentUser?.id) {
                          // If comment is from current user, show their name
                          if (currentUser?.firstName && currentUser?.lastName) {
                            authorName = `${currentUser.firstName} ${currentUser.lastName}`;
                          } else if (currentUser?.email) {
                            authorName = currentUser.email.split('@')[0];
                          }
                        }
                        
                        return (
                          <div key={comment.id} className="flex items-start gap-3">
                            <div className="flex-1 bg-white/5 border border-white/10 rounded-2xl p-4">
                              <div className="flex items-center justify-between mb-2">
                                <p className="font-normal text-sm text-white">{authorName}</p>
                                <p className="text-xs text-white/40 font-normal">
                                  {renderTimestamp(comment.createdAt || comment.created_at)}
                                </p>
                              </div>
                              <p className="text-sm text-white/80 whitespace-pre-wrap font-normal">{comment.comment}</p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      !isLoading && <p className="text-sm text-center py-4 text-white/60 font-normal">No internal comments yet.</p>
                    )}
                  </div>
                </div>
              </motion.div>
              );
            })()}
          </div>
            </>
          )}
        </motion.div>
      </motion.div>

      {showDeleteConfirm && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowDeleteConfirm(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 max-w-md w-full"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-red-500/10 rounded-3xl flex items-center justify-center border border-red-500/20">
                <Trash2 className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-light text-white mb-3 tracking-tight">Delete Trip</h3>
              <p className="text-sm text-white/85 font-normal mb-4 leading-relaxed">
                Are you sure you want to delete this trip? This action cannot be undone and will remove all associated passenger data.
              </p>
              <p className="text-xs text-white/60 font-normal mb-6 leading-relaxed">
                To confirm deletion, please type <span className="text-white/70 font-medium">"Yes please delete"</span> below:
              </p>
              <Input
                type="text"
                value={deleteConfirmationText}
                onChange={(e) => setDeleteConfirmationText(e.target.value)}
                placeholder="Yes please delete"
                className="mb-6 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-white/20"
                disabled={isDeleting}
                autoFocus
              />
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmationText('');
                  }}
                  className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 h-11 font-normal"
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white h-11 font-normal disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isDeleting || deleteConfirmationText.trim() !== 'Yes please delete'}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Trip'}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* "Finished paying" acknowledgement — a single-button overlay, not a
          flow. Whether we just filed the Confirmation Number Release
          inquiry or it was already moving, the customer reads one message
          and taps Okay. */}
      {tripPaidNotice && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setTripPaidNotice(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 max-w-md w-full"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              {/* Success states get the emerald check; failures must not — a
                  checkmark over "we couldn't submit" reads as success. */}
              <div className={`w-16 h-16 mx-auto mb-6 rounded-3xl flex items-center justify-center border ${
                tripPaidNotice.ok
                  ? 'bg-emerald-500/10 border-emerald-500/20'
                  : 'bg-amber-500/10 border-amber-500/20'
              }`}
              >
                {tripPaidNotice.ok ? (
                  <CheckCircle className="w-8 h-8 text-emerald-400" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-amber-400" />
                )}
              </div>
              <h3 className="text-xl font-light text-white mb-3 tracking-tight">{tripPaidNotice.title}</h3>
              <p className="text-sm text-white/85 font-normal mb-8 leading-relaxed">
                {tripPaidNotice.message}
              </p>
              <Button
                onClick={() => setTripPaidNotice(null)}
                className="w-full bg-white text-black hover:bg-white/90 h-11 font-normal"
              >
                Okay
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {showImageOverlay && mainFlight.itinerary_image_url && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowImageOverlay(false)}
          style={{ paddingTop: '80px', paddingBottom: '80px' }}
        >
          <motion.div
            className="absolute inset-0 bg-black/90 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            className="relative max-w-5xl w-full max-h-full flex flex-col"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-light text-lg tracking-tight">Original Itinerary</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowImageOverlay(false)}
                className="text-white/60 hover:text-white hover:bg-white/10 h-10 w-10"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl overflow-hidden flex-1 flex items-center justify-center p-4">
              <img
                src={mainFlight.itinerary_image_url}
                alt="Flight Itinerary"
                className="max-w-full max-h-full object-contain"
              />
            </div>
          </motion.div>
        </motion.div>
      )}

      <AdminEditModal
        isOpen={showAdminEdit}
        onClose={() => setShowAdminEdit(false)}
        trip={trip}
        onSave={handleAdminSave}
      />

      <PassengerEditModal
        isOpen={showPassengerEdit}
        onClose={() => setShowPassengerEdit(false)}
        passengers={passengers}
        trip={trip}
        onSave={handlePassengerSave}
      />

      {/* Cancellation Policy Modal */}
      <ActionModalShell
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        icon={XCircle}
        title={cancelMode === 'unbooked_credit'
          ? 'Cancel Flight'
          : (cancelMode === 'completed'
            ? 'Cancellation Policy'
            : (canInstantCancelDeferaZero ? 'Cancel Trip' : 'Cancellation Policy'))}
        description={cancelMode === 'unbooked_credit'
          ? 'This trip is eligible for Defera Credit.'
          : (cancelMode === 'completed'
            ? 'Your trip is paid in full. Cancellations follow airline policy.'
            : (canInstantCancelDeferaZero
              ? 'Stop your plan before any payment is collected.'
              : 'Review airline policy and how you would like to proceed.'))}
        maxWidthClass="sm:max-w-md"
        mobilePlacement="center"
        bodyClassName="space-y-4"
        footer={(
          <button
            type="button"
            onClick={() => setShowCancelModal(false)}
            className={cn('w-full', MODAL_GHOST_BTN_CLASS)}
          >
            Never mind
          </button>
        )}
      >
        {/*
          Confirmation for the 75% cash refund. Deliberately REPLACES the
          credit body rather than stacking a second dialog: the customer is
          agreeing to one specific set of figures, and those figures came from
          the server, not from arithmetic done here.
        */}
        {zeroRefundPreview?.needs_team_review && (
          <div className="space-y-4">
            <p className="text-white/85 text-sm font-normal leading-relaxed">
              {zeroRefundPreview.message}
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={executeZeroCashRefund}
                disabled={isExecutingZeroRefund}
                className="w-full flex items-center justify-center gap-2 px-4 h-12 rounded-full bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.12] text-white/90 font-normal text-sm transition-colors disabled:opacity-50 touch-manipulation"
              >
                {isExecutingZeroRefund ? (<><Loader2 className="w-4 h-4 animate-spin" />Sending…</>) : 'Send to our team'}
              </button>
              <button
                type="button"
                onClick={() => setZeroRefundPreview(null)}
                disabled={isExecutingZeroRefund}
                className="w-full px-4 h-11 rounded-full bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] text-white/70 font-normal text-sm transition-colors disabled:opacity-50"
              >
                Go back
              </button>
            </div>
          </div>
        )}
        {zeroRefundPreview && !zeroRefundPreview.needs_team_review && (
          <div className="space-y-4">
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-2">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-white/60 text-sm font-normal">Back to your card</span>
                <span className="text-white text-lg font-medium tabular-nums">
                  ${((zeroRefundPreview.cash_refund_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              {Number(zeroRefundPreview.credit_restore_cents) > 0 && (
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-white/60 text-sm font-normal">Back as Defera Credit</span>
                  <span className="text-emerald-300 text-lg font-medium tabular-nums">
                    ${((zeroRefundPreview.credit_restore_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              {Number(zeroRefundPreview.deposit_returned_cents) > 0 && (
                <p className="text-white/50 text-xs font-normal leading-snug">
                  Includes your ${((zeroRefundPreview.deposit_returned_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} deposit in full, since you booked less than 24 hours ago.
                </p>
              )}
              <div className="flex items-baseline justify-between gap-3 pt-2 border-t border-white/[0.06]">
                <span className="text-white/40 text-xs font-normal">
                  {Number(zeroRefundPreview.extension_fee_kept_cents) > 0
                    ? `Defera keeps (the $${fmtCurrency(zeroRefundPreview.extension_fee_kept_cents)} Extension Fee, and 25% of the rest of your card payments${Number(zeroRefundPreview.deposit_returned_cents) > 0 ? ' after the deposit' : ''})`
                    : Number(zeroRefundPreview.deposit_returned_cents) > 0 ? 'Defera keeps (25% of card payments after the deposit)' : 'Defera keeps (25% of card payments)'}
                </span>
                <span className="text-white/40 text-xs tabular-nums">
                  ${((zeroRefundPreview.cash_retained_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {Number(zeroRefundPreview.credit_restore_cents) > 0 && (
              <p className="text-white/50 text-xs font-normal leading-relaxed">
                Part of this trip was paid with Defera Credit. That money never came off a card, so it
                can&apos;t be refunded as cash — it goes back to your Defera Credit balance in full.
              </p>
            )}

            <p className="text-white/85 text-sm font-normal leading-relaxed">
              This cancels your trip and refunds you now. It can&apos;t be undone.
            </p>

            <div className="space-y-2">
              <button
                type="button"
                onClick={executeZeroCashRefund}
                disabled={isExecutingZeroRefund}
                className="w-full flex items-center justify-center gap-2 px-4 h-12 rounded-full bg-red-950/50 hover:bg-red-950/70 border border-red-900/50 text-red-300 font-normal text-sm transition-colors disabled:opacity-50 touch-manipulation"
              >
                {isExecutingZeroRefund ? (<><Loader2 className="w-4 h-4 animate-spin" />Refunding…</>) : 'Confirm — cancel & refund me'}
              </button>
              <button
                type="button"
                onClick={() => setZeroRefundPreview(null)}
                disabled={isExecutingZeroRefund}
                className="w-full px-4 h-11 rounded-full bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] text-white/70 font-normal text-sm transition-colors disabled:opacity-50"
              >
                Go back
              </button>
            </div>
          </div>
        )}

        {!zeroRefundPreview && cancelMode === 'unbooked_credit' && (
          <div className="space-y-4">
            {/* Credit card */}
            <div className="rounded-xl bg-emerald-950/20 border border-emerald-900/30 px-4 py-4">
              <p className="text-emerald-300/60 text-[11px] uppercase tracking-wider font-normal mb-1.5">{bookedWithin24h ? 'Back to you' : 'Defera Credit'}</p>
              {isLoadingCancelCredit ? (
                <div className="flex items-center gap-2 text-emerald-300/80 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Calculating…
                </div>
              ) : (
                <p className="text-emerald-300 text-2xl font-semibold tabular-nums">
                  ${(Math.max(0, (Number(cancelCreditPaidCents) || 0) - cancelPremiumInPaidCents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              )}
              {/* Packages only: show the hotel inside the figure, so nobody has
                  to wonder whether their stay is covered by it. */}
              {!isLoadingCancelCredit && cancelPackageSplit && (
                <div className="mt-3 pt-3 border-t border-emerald-900/30 space-y-1.5">
                  <p className="text-emerald-200/50 text-[11px] font-normal">
                    What you&rsquo;ve paid so far, across this trip
                  </p>
                  {[
                    { label: 'Flights', cents: cancelPackageSplit.flightCents },
                    { label: cancelStayName, cents: cancelPackageSplit.stayCents },
                    { label: 'Fees', cents: cancelPackageSplit.feeCents },
                  ].filter((row) => row.cents > 0).map((row) => (
                    <div key={row.label} className="flex items-baseline justify-between gap-3">
                      <span className="text-white/60 text-[12.5px] font-normal min-w-0 truncate">{row.label}</span>
                      <span className="text-white/80 text-[12.5px] font-normal tabular-nums shrink-0">
                        ${(row.cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  ))}
                  <p className="text-emerald-200/40 text-[11px] font-normal leading-snug pt-0.5">
                    Your payments cover the whole trip, so this is each part&rsquo;s share of them. All of it comes back as credit.
                  </p>
                </div>
              )}
            </div>

            {bookedWithin24h && !isLoadingCancelCredit && Number(cancelCreditPaidCents) > 0 && (
              <p className="text-white/70 text-[13px] font-normal leading-relaxed">
                You booked less than 24 hours ago, so your deposit goes back to your card. Anything else you&rsquo;ve paid comes back as Defera Credit.
              </p>
            )}

            {/* The Faye premium paid so far never becomes credit: it goes back the
                way PREMIUM_REFUND_METHOD says. No mention of why the policy isn't
                issued — that would describe ticketing. */}
            {!isLoadingCancelCredit && cancelPremiumRefundCents > 0 && (
              <div className="rounded-xl bg-white/[0.03] border border-white/[0.08] px-4 py-3 flex items-baseline justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-white/80 text-sm font-normal">Travel insurance</p>
                  <p className="text-white/50 text-xs font-normal mt-0.5">
                    {PREMIUM_REFUND_METHOD === 'card' ? 'Goes back to your card' : 'Added to your Defera Credit'}
                  </p>
                </div>
                <span className="text-white/85 text-base font-normal tabular-nums shrink-0">
                  ${(cancelPremiumRefundCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* Body copy — no reference to "ticketed" or "hasn't been ticketed" */}
            <p className="text-white/85 text-sm font-normal leading-relaxed">
              {Number(cancelCreditPaidCents) > 0
                ? (cancelPremiumRefundCents > 0 && PREMIUM_REFUND_METHOD === 'card'
                  ? `Cancel now to get back 100% of everything you\u2019ve paid${cancelExtensionFeeNote}: the travel insurance part to your card, the rest as Defera Credit. Your plan stops immediately and no further payments will be taken.`
                  : `Cancel now to receive 100% of everything you\u2019ve paid${cancelExtensionFeeNote} back as Defera Credit. Your plan stops immediately and no further payments will be taken.`)
                : 'Cancel now to stop your payment plan. No payments have been collected, so you won\u2019t be charged anything.'}
            </p>
            <p className="text-white/60 text-xs font-normal leading-relaxed">
              Defera Credit is applied to your account instantly, never expires, and can be used to book any future trip — including trips for others.
            </p>

            {/* CTA */}
            <button
              type="button"
              onClick={handleCancelForCredit}
              disabled={isCancellingDeferaZero || isLoadingCancelCredit}
              className="w-full flex items-center justify-center gap-2 px-4 h-12 rounded-full bg-red-950/50 hover:bg-red-950/70 active:bg-red-950/80 border border-red-900/50 text-red-300 font-normal text-sm transition-colors disabled:opacity-50 touch-manipulation"
            >
              {isCancellingDeferaZero ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Cancelling…
                </>
              ) : (
                Number(cancelCreditPaidCents) > 0
                  ? (bookedWithin24h ? 'Cancel & get my deposit back' : 'Cancel & receive Defera Credit')
                  : 'Cancel Flight'
              )}
            </button>

            {/*
              Defera Zero's published term is 75% of payments back AS CASH, up
              to 24 hours before departure. That option lives in the policy
              modal — which this credit overlay REPLACES on any unticketed
              trip, i.e. the normal state for a Zero booking. So the customer
              was only ever shown credit.

              100% credit is more money than 75% cash, and usually the better
              deal, which is why it stays the headline. But it is not the same
              thing to someone who needs actual money, and we publish the cash
              term on the trip's own cancel screen. Both now appear, with the
              trade-off stated, and the customer chooses.

              Only when they've actually paid something — 75% of nothing is
              nothing, and offering it would be noise.
            */}
            {isDeferaZero && Number(cancelCreditPaidCents) > 0 && (
              <div className="pt-1">
                <button
                  type="button"
                  onClick={openZeroCashRefund}
                  disabled={isCancellingDeferaZero || isLoadingCancelCredit || isLoadingZeroRefund}
                  className="w-full text-left px-4 py-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.08] transition-colors disabled:opacity-50 touch-manipulation"
                >
                  <div className="text-white/85 text-sm font-normal">
                    {isLoadingZeroRefund ? 'Working out your refund…' : 'Prefer cash? Take 75% back instead'}
                  </div>
                  <div className="text-white/50 text-xs font-normal mt-0.5 leading-relaxed">
                    ${(((Number(cancelCreditPaidCents) || 0) * 0.75) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} refunded to your card, up to 24 hours before departure — less than the credit above, but it's money back.
                  </div>
                </button>
              </div>
            )}
          </div>
        )}

        {!zeroRefundPreview && cancelMode === 'instant_zero' && (
          <div className="space-y-4">
            <p className="text-white/85 text-sm font-normal leading-relaxed">
              No payment has been made yet. Cancel this trip to stop your plan—you won&apos;t be charged.
            </p>
            <button
              type="button"
              onClick={handleInstantCancelDeferaZero}
              disabled={isCancellingDeferaZero}
              className="w-full flex items-center justify-center gap-2 px-4 h-11 rounded-full bg-red-950/50 hover:bg-red-950/70 border border-red-900/50 text-red-300 font-normal text-sm transition-colors disabled:opacity-50"
            >
              {isCancellingDeferaZero ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Cancel Trip'
              )}
            </button>
          </div>
        )}

        {!zeroRefundPreview && cancelMode === 'completed' && (
          <div className="space-y-4">
            <div className="rounded-xl bg-white/[0.02] border border-white/[0.08] px-4 py-3">
              <h3 className="text-white/90 text-xs uppercase tracking-wider font-medium mb-2">Airline Policy</h3>
              <p className="text-white/65 text-sm font-normal leading-relaxed">
                All cancellations are governed by the airline&apos;s terms. Bookings are generally non-refundable, though certain fares may allow a flight credit. Because your Defera plan is paid in full, any eligible credit would be issued according to the airline&apos;s rules.
              </p>
            </div>
            <p className="text-white/60 text-xs font-normal leading-relaxed">
              Our team can help you understand what the airline allows for this fare and walk you through next steps.
            </p>

            {/*
              Defera Zero's 75% cash refund applies to EVERY Zero trip — ticketed
              or not. Zero is sold only on fully refundable fares, so Defera gets
              the airfare back from the airline either way; a confirmation number
              was never a condition. Only the 24-hour cutoff closes it, and the
              server enforces that (this button opens a preview, not a payout).
            */}
            {isDeferaZero && (
              <button
                type="button"
                onClick={openZeroCashRefund}
                disabled={isLoadingZeroRefund}
                className="w-full text-left px-4 py-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.08] transition-colors disabled:opacity-50 touch-manipulation"
              >
                <div className="text-white/90 text-sm font-medium">
                  {isLoadingZeroRefund ? 'Working out your refund…' : 'Cancel & receive 75% refund'}
                </div>
                <div className="text-white/60 text-xs font-normal mt-0.5 leading-relaxed">
                  Up to 24 hours before departure. 75% of what you paid by card comes back.
                </div>
              </button>
            )}

            <button
              type="button"
              onClick={() => handleCancelScenario('general')}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.08] transition-colors group text-left"
            >
              <div className="flex-1 min-w-0">
                <div className="text-white/90 text-sm font-medium">Talk to support</div>
                <div className="text-white/60 text-xs font-normal mt-0.5">For cancellation help or questions about airline credit</div>
              </div>
              <ArrowRight className="w-4 h-4 text-white/25 group-hover:text-white/55 transition-colors flex-shrink-0 ml-2" />
            </button>
          </div>
        )}

        {!zeroRefundPreview && cancelMode === 'policy' && (
          <>
            <div className="space-y-4">
              <div className="rounded-xl bg-white/[0.02] border border-white/[0.08] px-4 py-3">
                <h3 className="text-white/90 text-xs uppercase tracking-wider font-medium mb-2">Airline Policy</h3>
                <p className="text-white/65 text-sm font-normal leading-relaxed">
                  All cancellations are governed by the airline&apos;s terms. Bookings are generally non-refundable, though certain fares may allow a flight credit. Any applicable credit will be issued only after the installment plan has been paid in full.
                </p>
              </div>

              <div className="rounded-xl bg-white/[0.02] border border-white/[0.08] px-4 py-3">
                <h3 className="text-white/90 text-xs uppercase tracking-wider font-medium mb-2">How Defera Works</h3>
                <p className="text-white/65 text-sm font-normal leading-relaxed">
                  Defera pays for the entire trip in advance. If a booking is canceled, the airline does not refund the fare, and Defera does not recoup those costs. Instead, when permitted by the airline, the booking is converted into a flight credit issued to the passenger.
                </p>
              </div>
            </div>

            <div className="bg-red-950/35 border border-red-900/40 rounded-xl px-4 py-3">
              <h3 className="text-red-300 text-xs uppercase tracking-wider font-normal mb-2">Important Warning</h3>
              <p className="text-red-300/75 text-sm font-normal leading-relaxed">{ic.installmentDefaultCancelWarningBody}</p>
            </div>

            <div className="pt-1">
              <p className="text-white/40 text-[11px] uppercase tracking-wider mb-3">How would you like to proceed?</p>
              <div className="space-y-2">
                {isDeferaZero && (
                  <button
                    type="button"
                    onClick={openZeroCashRefund}
                    disabled={isLoadingZeroRefund}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-emerald-950/25 hover:bg-emerald-950/40 border border-emerald-900/35 transition-colors group text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-emerald-300 text-sm font-normal">Cancel & receive 75% refund</div>
                      <div className="text-emerald-400/55 text-xs font-normal mt-0.5">Cancel up to 24 hours before departure. 75% of payments refunded.</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-emerald-400/30 group-hover:text-emerald-400/60 transition-colors flex-shrink-0 ml-2" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleCancelScenario('payoff')}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.08] transition-colors group text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-white/90 text-sm font-medium">Cancel & continue paying off balance</div>
                    <div className="text-white/60 text-xs font-normal mt-0.5">Then receive flight credit upon completion</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/25 group-hover:text-white/55 transition-colors flex-shrink-0 ml-2" />
                </button>

                <button
                  type="button"
                  onClick={() => handleCancelScenario('default')}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-red-950/25 hover:bg-red-950/40 border border-red-900/35 transition-colors group text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-red-300 text-sm font-normal">Cancel & stop payments</div>
                    <div className="text-red-400/55 text-xs font-normal mt-0.5">Forfeit all payments and send balance to collections</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-red-400/30 group-hover:text-red-400/60 transition-colors flex-shrink-0 ml-2" />
                </button>

                <button
                  type="button"
                  onClick={() => handleCancelScenario('general')}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.08] transition-colors group text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-white/90 text-sm font-medium">Talk to support</div>
                    <div className="text-white/60 text-xs font-normal mt-0.5">For general inquiries or further questions</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/25 group-hover:text-white/55 transition-colors flex-shrink-0 ml-2" />
                </button>
              </div>
            </div>
          </>
        )}
      </ActionModalShell>

      {/* Request Flight Change (ineligible fares / airlines) */}
      <ActionModalShell
        isOpen={showRequestChangeModal}
        onClose={() => setShowRequestChangeModal(false)}
        icon={MessageCircle}
        title="Request Flight Change"
        description={`${buildTripRouteLabel()}${trip[0]?.booking_reference || trip[0]?.trip_reference ? ` · ${trip[0]?.booking_reference || trip[0]?.trip_reference}` : ''}`}
        maxWidthClass="sm:max-w-md"
        bodyClassName="space-y-4"
        footer={(
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={openChangeFlightSms}
              className={cn('w-full inline-flex items-center justify-center gap-2', MODAL_PRIMARY_BTN_CLASS)}
            >
              <MessageCircle className="w-4 h-4" />
              Text Support
            </button>
            <button
              type="button"
              onClick={() => setShowRequestChangeModal(false)}
              className={cn('w-full', MODAL_GHOST_BTN_CLASS)}
            >
              Never mind
            </button>
          </div>
        )}
      >
        <div className="rounded-xl bg-white/[0.02] border border-white/[0.08] px-4 py-3">
          <p className="text-white/85 text-sm font-normal leading-relaxed">
            {changeFlightRouting === 'inquiry'
              ? 'Flight changes on this trip are handled by our team rather than in the app. Message us with the dates you have in mind and we’ll take it from there.'
              : (changeFlightEligibility.reason || 'Self-service Change Flight is not available for this booking.')}
          </p>
        </div>
        <p className="text-white/60 text-xs font-normal leading-relaxed">
          Our team can review change options with the airline on your behalf. We&apos;ll open a pre-filled text message—add any details about the dates or routes you need.
        </p>
      </ActionModalShell>

      {/* Change Flight on a completed (ticketed) trip — the in-app change
          search can't modify an issued ticket, so changes happen directly
          with the airline. */}
      <ActionModalShell
        isOpen={showAirlineChangeModal}
        onClose={() => setShowAirlineChangeModal(false)}
        icon={Plane}
        title="Change Flight"
        description={`${buildTripRouteLabel()}${trip[0]?.booking_reference || trip[0]?.trip_reference ? ` · ${trip[0]?.booking_reference || trip[0]?.trip_reference}` : ''}`}
        maxWidthClass="sm:max-w-md"
        bodyClassName="space-y-4"
        footer={(
          <button
            type="button"
            onClick={() => setShowAirlineChangeModal(false)}
            className={cn('w-full', MODAL_GHOST_BTN_CLASS)}
          >
            Done
          </button>
        )}
      >
        <div className="rounded-xl bg-white/[0.02] border border-white/[0.08] px-4 py-3">
          <p className="text-white/85 text-sm font-normal leading-relaxed">
            This trip is paid in full and ticketed with {getTripAirline(trip).name || 'the airline'}. Date and time changes are made directly with the airline using your confirmation number.
          </p>
        </div>
        {fareChangeConfirmation && (
          <div className="rounded-xl bg-white/[0.02] border border-white/[0.08] px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-white/45 text-xs font-normal">Airline confirmation</div>
              <div className="text-white text-sm font-medium tracking-wide">{fareChangeConfirmation}</div>
            </div>
            <CopyChip
              fieldKey="airline-change-confirmation"
              value={fareChangeConfirmation}
              copiedField={copiedField}
              onCopy={handleCopyField}
              ariaLabel="Copy airline confirmation number"
            />
          </div>
        )}
        <p className="text-white/60 text-xs font-normal leading-relaxed">
          If the airline directs you back to your booking agency, message support and we&apos;ll handle the change for you.
        </p>
      </ActionModalShell>

      {/* Live seat-map purchase flow (one-time, booking owner only) */}
      {showSeatPurchaseFlow && (
        <React.Suspense fallback={null}>
          <PurchaseSeatsFlow
            isOpen={showSeatPurchaseFlow}
            onClose={() => setShowSeatPurchaseFlow(false)}
            booking={trip?.[0]}
            passengers={passengers}
            actAsUserId={isSeatPurchaseActAs ? tripOwnerId : null}
            onPurchased={handleSeatPurchaseComplete}
            onConciergeFallback={() => {
              setShowSeatPurchaseFlow(false);
              setShowPurchaseSeatsModal(true);
            }}
          />
        </React.Suspense>
      )}

      {/* Purchase Seats — concierge fallback when live seat-map flow unavailable */}
      <ActionModalShell
        isOpen={showPurchaseSeatsModal}
        onClose={() => setShowPurchaseSeatsModal(false)}
        icon={Armchair}
        title="Purchase Seats"
        description="Paid in full today — separate from your installment plan"
        maxWidthClass="sm:max-w-md"
        bodyClassName="space-y-4"
        footer={(
          <button
            type="button"
            onClick={() => setShowPurchaseSeatsModal(false)}
            className={cn('w-full', MODAL_GHOST_BTN_CLASS)}
          >
            Never mind
          </button>
        )}
      >
        <p className="text-white/65 text-sm font-normal leading-relaxed break-words">
          If your cabin class doesn&apos;t include complimentary seat selection, you can purchase seats in advance at any time. Alternatively, you can purchase seats directly with the airline after completing your installment plan, or you&apos;ll be automatically assigned a seat by the airline at check-in.
        </p>

        <div className="rounded-xl bg-white/[0.02] border border-white/[0.08] px-4 py-3">
          <p className="text-white/60 text-xs font-normal leading-relaxed break-words">
            <span className="text-white/80 font-medium">Important:</span>
            {' '}
            Add-on purchases such as seat selections must be paid for upfront and cannot be rolled into your installment plan. A small processing fee will be added to these transactions.
          </p>
        </div>

        <button
          type="button"
          onClick={handlePurchaseSeats}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.08] transition-colors group text-left"
        >
          <div className="flex-1 min-w-0">
            <div className="text-white/90 text-sm font-medium">Select seats now</div>
            <div className="text-white/60 text-xs font-normal mt-0.5">Contact us to purchase seats</div>
          </div>
          <ArrowRight className="w-4 h-4 text-white/25 group-hover:text-white/55 transition-colors flex-shrink-0 ml-2" />
        </button>
      </ActionModalShell>

      {showBaggagePurchaseFlow && (
        <React.Suspense fallback={null}>
          <PurchaseBaggageFlow
            isOpen={showBaggagePurchaseFlow}
            onClose={() => setShowBaggagePurchaseFlow(false)}
            booking={trip?.[0]}
            passengers={passengers}
            actAsUserId={isSeatPurchaseActAs ? tripOwnerId : null}
            onPurchased={handleBaggagePurchaseComplete}
            onConciergeFallback={() => {
              setShowBaggagePurchaseFlow(false);
              handlePurchaseBaggage();
            }}
          />
        </React.Suspense>
      )}

      {/* Cabin Upgrade Modal */}
      {showCabinUpgradeModal && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-start justify-center pt-20 sm:pt-24 pb-4 px-4 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="fixed inset-0 bg-black/90"
            onClick={() => setShowCabinUpgradeModal(false)}
          />
          <motion.div
            className="relative bg-[#111] border border-white/10 rounded-xl w-full max-w-[calc(100vw-1rem)] sm:max-w-md mx-2 sm:mx-0 overflow-x-hidden shadow-2xl"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowCabinUpgradeModal(false)}
              className="absolute top-3 right-3 p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="px-5 pt-5 pb-4">
              <h2 className="text-white text-base font-semibold flex items-center gap-2">
                <ArrowUpCircle className="w-5 h-5 text-[var(--accent-color)]" />
                Cabin Upgrade
              </h2>
            </div>
            <div className="px-5 pb-4 space-y-4">
              <p className="text-white/85 text-sm leading-relaxed">
                Upgrade to Premium Economy, Business, or First Class. Pricing starts at $80+ (8,000+ miles) and varies by airline and availability.
              </p>
              <div className="border-t border-white/10 pt-4">
                <button
                  onClick={handleCabinUpgrade}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors group text-left"
                >
                  <div className="flex-1">
                    <div className="text-white text-sm font-medium">Request cabin upgrade</div>
                    <div className="text-white/60 text-xs mt-0.5">Contact us for pricing and availability</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors flex-shrink-0 ml-2" />
                </button>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-white/10">
              <button
                onClick={() => setShowCabinUpgradeModal(false)}
                className="w-full py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors"
              >
                Never mind
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Change Seat Selection Modal */}
      {showChangeSeatModal && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-start justify-center pt-20 sm:pt-24 pb-4 px-4 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/90"
            onClick={() => setShowChangeSeatModal(false)}
          />
          
          {/* Modal */}
          <motion.div
            className="relative bg-[#111] border border-white/10 rounded-xl w-full max-w-[calc(100vw-1rem)] sm:max-w-md mx-2 sm:mx-0 overflow-x-hidden shadow-2xl"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowChangeSeatModal(false)}
              className="absolute top-3 right-3 p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Header */}
            <div className="px-5 pt-5 pb-4">
              <h2 className="text-white text-base font-semibold">Change Seat Selection</h2>
            </div>
            
            {/* Content */}
            <div className="px-5 pb-4 space-y-4">
              <div>
                <p className="text-white/85 text-sm leading-relaxed break-words">
                  Your trip already has seat assignments. We've selected window seats and seats together with other passengers in your reservation, positioned as close as possible to the front of the plane. However, you can change your seat selection at any time.
                </p>
              </div>
              
              {/* Divider */}
              <div className="border-t border-white/10 pt-4">
                <p className="text-white/60 text-xs mb-3">Would you like to change your seat selection?</p>
                
                <button
                  onClick={handleChangeSeatSelection}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors group text-left"
                >
                  <div className="flex-1">
                    <div className="text-white text-sm font-medium">Yes, change my seats</div>
                    <div className="text-white/60 text-xs mt-0.5">Contact us to update your seat selection</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors flex-shrink-0 ml-2" />
                </button>
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-5 py-4 border-t border-white/10">
              <button
                onClick={() => setShowChangeSeatModal(false)}
                className="w-full py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors"
              >
                Keep current seats
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>,
    document.body,
  );
}

/**
 * Public component. The admin Trips list arrives paged and slim (each row's
 * raw search-offer blob stripped, marked `_slim: true`; see
 * server/admin-bookings-paging.js). This modal and the editors it launches
 * read that blob and AdminEditModal round-trips the whole flight_data, so a
 * slim row is hydrated through POST /api/admin/booking before the dialog
 * opens. Full rows (customer dashboard, UserDetails) pass straight through.
 */
export default function FlightDetailsModal(props) {
  const { trip, isOpen } = props;
  const { trip: fullTrip, ready } = useHydratedTrip(trip, {
    enabled: Boolean(isOpen),
    fetchFull: (ids) => FlightBooking.fetchFull(ids),
  });
  return <FlightDetailsModalInner {...props} trip={ready ? fullTrip : null} isOpen={Boolean(isOpen) && ready} />;
}

FlightDetailsModalInner.propTypes = {
  /** Named "back" control — see the wrapper's propTypes below. */
  backLabel: PropTypes.string,
};

FlightDetailsModal.propTypes = {
  trip: PropTypes.array,
  isOpen: PropTypes.bool,
  /** Renders a named "back" control — for callers arriving from another page. */
  backLabel: PropTypes.string,
};
